// In-memory fake of the CompanyOS API, typed by the SDK, used until auth (S1-04) and the real
// routes are wired at integration. It plugs into the real SDK client as its `fetch`, so the
// client, its URL building and its RFC 9457 error handling are exercised unchanged.
//
// S1-16 adds the routes the operator console, the company view and the DE profile read, over
// the deterministic data in fake-data.ts, with the API's access rules (13 §Principals): agency
// cost/time rows and founder hours for agency operators only, keys for managers only, client
// viewers see sent reports only, and every `kpi` route answers `403 feature_disabled` when the
// add-on is off (ADR-0016). Every request is logged in `calls` (the per-screen call budget).
import { deDay, evaluateGate, type GateDef } from "@companyos/domain-kpi";
import {
  completenessRulesOf,
  completenessSummary,
  cutCompleteness,
  executionScore,
  financeScore,
  KPI_GROUP_OF,
  keystonesScore,
  type LensScore,
  lensThresholdsOf,
  lensWeightsOf,
  overallScore,
  performanceScore,
  responsibilitiesScore,
  type ScoredLens,
  type SeatFacts,
  seatCompleteness,
  tasksScore,
} from "@companyos/domain-work/org";
import { defaultProfile } from "@companyos/schema/contracts";
import { type OperationOutput, operations, type Problem, type RouteId } from "@companyos/sdk";
import { ALL_EVENT_TYPES } from "../features/webhooks/event-types";
import { isoWeekKey } from "../focus/focus";
import { weekStart, weeksBetween } from "../focus/weeks";
import { createAccessFake, type FakeMail } from "./fake-access";
import { type AgencyDeps, createAgency, createAgencyState } from "./fake-agency";
import { createAgentHealthFake, type FakeSkillsmithReport } from "./fake-agent-health";
import { createAgentPolicyHandlers } from "./fake-agent-policy";
import { createAgentRecordHandlers, type RecordRun } from "./fake-agent-record";
import { createAgentRequestHandlers, MANAGED_ADD_ROUTES, managedGate } from "./fake-agent-requests";
import { createAgentsFake } from "./fake-agents";
import { createAssessFake } from "./fake-assess";
import { createBillingFake } from "./fake-billing";
import { createBoardNumberHandlers } from "./fake-board-numbers";
import { createBriefsFake, FAKE_BRIEF_RULES_ROUTES, FAKE_BRIEFS_ROUTES } from "./fake-briefs";
import { createBusinessFake } from "./fake-business";
import { type CadencePortfolioBridge, createCadenceFake } from "./fake-cadence";
import { createCalendarFake } from "./fake-calendar";
import { createChatFake, FAKE_AGENTS_ROUTES, FAKE_CHAT_ROUTES } from "./fake-chat";
import { createCommitmentsFake, type FakeCommitmentSeed } from "./fake-commitments";
import { createCompanyExportHandlers } from "./fake-company-export";
import { createContactsFake, type FakeContact } from "./fake-contacts";
import { createControlFake } from "./fake-control";
import {
  createCosFake,
  FAKE_COS_AGENTS_ROUTES,
  FAKE_COS_CHAT_ROUTES,
  type FakeSuggestionSeed,
} from "./fake-cos";
import { createCosModesFake, FAKE_COS_MODES_ROUTES } from "./fake-cos-modes";
import { createCrewFake, type InboxItem } from "./fake-crews";
import {
  COMPANY_IDS,
  companyScorecard,
  costLedger,
  DE_IDS,
  deProfile,
  deStartedOn,
  deSummary,
  type FakeCompany,
  type FakeDe,
  reports,
  seatScorecard,
  seedCompanies,
  seedDes,
  sharedDesName,
  timeEntries,
  timeline,
  uid,
  type Viewer,
  weekOf,
} from "./fake-data";
import { createDecideHandlers, FAKE_DECIDE_ROUTES } from "./fake-decide";
import { createDeployHandlers, FAKE_SELF_SERVE_ROUTES } from "./fake-deploys";
import { createDriftAuditHandlers } from "./fake-drift-audits";
import {
  metricDefs,
  observationRow,
  people,
  periodTypeOf,
  reportHtml,
  seats,
  seedGateObservations,
  subscriptions,
  targetRules,
  validateImport,
} from "./fake-entry";
import { createFilesFake } from "./fake-files";
import { createFinanceHandlers } from "./fake-finance";
import { createFlowsFake } from "./fake-flows";
import { FAKE_OPERATOR_COMPANY_ID } from "./fake-gate-data";
import { applyBatchReview, createGateHandlers, seedBatchReview } from "./fake-gates";
import { createImportHandlers } from "./fake-imports";
import { createInboundWebhookHandlers } from "./fake-inbound-webhooks";
import { createInsightsFake } from "./fake-insights";
import { createIntakeFake, type FakeInboundEvent, type FakeIntegrationJob } from "./fake-intake";
import { createItemTemplatesFake } from "./fake-item-templates";
import { createKnowledgeFake } from "./fake-knowledge";
import { createKpiVisibility } from "./fake-kpi-visibility";
import { createLoopsFake, FAKE_LOOP_ROUTES, type FakeLoopIncident } from "./fake-loops";
import { createMemoryFake, FAKE_MEMORY_ROUTES, type FakeRecallState } from "./fake-memory";
import { createMemoryPeopleFake, FAKE_MEMORY_PEOPLE_ROUTES } from "./fake-memory-people";
import { type ComposedHandler, mergedPages, summedCounts } from "./fake-merge";
import { type AuthFailure, createMfaFake, DEMO_RECOVERY_CODES, DEMO_TOTP_SECRET } from "./fake-mfa";
import { createModelAccessHandlers } from "./fake-model-access";
import { createModelPolicyHandlers } from "./fake-model-policy";
import { createMonitoringFake, type MonitoredRun } from "./fake-monitoring";
import { JORDAN, myNumbers, type NumbersStores, personOf, selfScope } from "./fake-numbers";
import { createOooFake } from "./fake-ooo";
import { createOperatorConsoleFake, refuseNonPlatformAdmin } from "./fake-operator-console";
import {
  createOperatorSkillProposalsFake,
  type FakeSkillProposal,
} from "./fake-operator-skill-proposals";
import { createOpportunitiesFake, type FakeSkillsmithOpportunity } from "./fake-opportunities";
import { createOpsHandlers, createOpsState, recordSetupSwitch } from "./fake-ops";
import { orgHandlers, quarterOf } from "./fake-org";
import { createOutboxFake, type FakeOutboxItem } from "./fake-outbox";
import { createP1Fake } from "./fake-p1";
import { createPagesFake } from "./fake-pages";
import { createPaletteFake, type InternalFetch } from "./fake-palette";
import { createPortfolioFake, PORTFOLIO, portfolioCompany } from "./fake-portfolio";
import { createPostResultsHandlers } from "./fake-post-results";
import { createProcessHandlers } from "./fake-process";
import { createProvisioningHandlers } from "./fake-provisioning";
import { fakePurgePlan, fakePurgeResult, sameName } from "./fake-purge";
import { createPushFake } from "./fake-push";
import { createReachFake, FAKE_REACH_ROUTES } from "./fake-reach";
import {
  createReportingFake,
  seedReportingObservations,
  seedReportingRuns,
} from "./fake-reporting";
import { createRoleTemplatesFake } from "./fake-role-templates";
import { createRulesFake, FAKE_RULES_MEMORY_ROUTES, type FakeProcedureRun } from "./fake-rules";
import { createRunsTwin } from "./fake-runs";
import { createSatellitesFake } from "./fake-satellites";
import { cardForPeriod, periodParam } from "./fake-scorecard-grain";
import { createScreensFake, FAKE_SCREENS_ROUTES } from "./fake-screens";
import { createSeatBoardsFake } from "./fake-seat-boards";
import { createServiceEndpointsFake } from "./fake-service-endpoints";
import { createSharedDesFake } from "./fake-shared-des";
import { createSkillPackHandlers } from "./fake-skill-packs";
import {
  liveOf as liveObservationOf,
  STRUCTURE_KPI_ROUTES,
  type StructureDeps,
  structureHandlers,
} from "./fake-structure";
import { createSubscriptionHandlers } from "./fake-subscriptions";
import { teamEntryAllowed } from "./fake-team-kpis";
import { createTemplateHandlers, type FakeCatalog } from "./fake-templates";
import { createWalletFake } from "./fake-wallet";
import { createWebhookHandlers } from "./fake-webhooks";
import {
  createWorkFake,
  type FakeWorkTemplateHandlers,
  type LiveRunLike,
  type WorkBridge,
} from "./fake-work";
import { createWorkBundleFake } from "./fake-work-bundle";
import { createWorkBundle2Fake } from "./fake-work-bundle-2";
import {
  createWorkforceHandlers,
  deProfileText,
  EMPTY_DE_PROFILE,
  type Leave,
} from "./fake-workforce";
import type {
  AgentKey,
  AgentKeyScope,
  BudgetRow,
  CompanyRole,
  DeProfile,
  Deploy,
  DeSubscription,
  DeTemplateOverride,
  FridayReportRecord,
  GatePerDeRow,
  Goal,
  ImportBatch,
  MeResponse,
  Observation,
  OrgChartDimensions,
  OrgChartKpiVisibility,
  Responsibility,
  Scorecard,
  TimeEntry,
} from "./types";

export { COMPANY_IDS, DE_IDS };

/** WF-FIX: `selfHost(…, n)` queues its n wake-ups from here, a minute apart (fixed, not now). */
export const SELF_HOSTED_INBOX_BASE = "2026-06-01T09:00:00.000Z";

const USER_IDS = {
  operator: "0193a0a0-0000-7000-8000-0000000000a1",
  owner: "0193a0a0-0000-7000-8000-0000000000b2",
  viewer: "0193a0a0-0000-7000-8000-0000000000c3",
  member: "0193a0a0-0000-7000-8000-0000000000d4",
  /** CHART-DIMENSIONS: Jordan Lee, a member who holds Acme's Recruiting lead seat. */
  holder: "0193a0a0-0000-7000-8000-0000000000e5",
  /** HARDEN-mfa: Gwen Gamma, an admin of Gamma (which requires two-step sign-in). */
  gammaAdmin: "0193a0a0-0000-7000-8000-0000000000f6",
} as const;

/**
 * HARDEN-mfa: the seeded accounts enrolled in two-step sign-in, on a trusted device: the operator
 * and the founder (FOUNDER-OS; an agency operator too, so required to).
 */
const DEMO_ENROLLED: readonly string[] = ["operator@htva.example", "founder@htva.example"];

const membership = (n: number) => `0193a0a0-0000-7000-8000-0000000001${String(n).padStart(2, "0")}`;

/** `/v1/me`'s company facts, from the seeded companies (their calendars included). */
const companies = Object.fromEntries(
  seedCompanies().map((c) => [
    c.slug,
    {
      company_id: c.id,
      company_slug: c.slug,
      company_name: c.name,
      company_calendar: c.calendar_config,
    },
  ]),
) as Record<
  "acme" | "beta" | "gamma" | "htva",
  {
    company_id: string;
    company_slug: string;
    company_name: string;
    company_calendar: FakeCompany["calendar_config"];
  }
>;

/**
 * acme: kpi on; beta: kpi off (add-on disabled); gamma: kpi in grace (read-only); htva (S1-17,
 * HTVA's own company with the gate numbers): kpi on. Chat (M2-06) follows the same pattern:
 * on for acme (the demo channels) and htva, off for beta, in grace for gamma.
 */
const entitlements: MeResponse["entitlements"] = {
  // Chat on for Acme: task threads (Work mode, M2-07) are `chat` routes, switched on with M2.
  // Agents on for Acme (M3-08): runs, approvals and the wake-up queue (agents bundles memory).
  [COMPANY_IDS.acme]: {
    core: "active",
    kpi: "active",
    chat: "active",
    agents: "active",
    memory: "active",
  },
  [COMPANY_IDS.beta]: { core: "active", kpi: "disabled", chat: "disabled" },
  [COMPANY_IDS.gamma]: { core: "active", kpi: "grace", chat: "grace" },
  [COMPANY_IDS.htva]: { core: "active", kpi: "active", chat: "active" },
};

export type FakeAccount = { email: string; password: string; label: string; me: MeResponse };
type SeedAccount = Omit<FakeAccount, "me"> & { me: Omit<MeResponse, "mfa"> };

const SEEDED_ACCOUNTS: SeedAccount[] = [
  {
    email: "operator@htva.example",
    password: "demo",
    label: "HTVA operator (all companies)",
    me: {
      user: { id: USER_IDS.operator, email: "operator@htva.example", name: "Olivia Operator" },
      memberships: [],
      agency: {
        agency_id: "0193a0a0-0000-7000-8000-00000000a9e1",
        role: "operator",
        // DC-03: HTVA's operator authors the catalog (tests flip it with `setMe`).
        template_author: true,
        // DC-05: and may override verification for a pilot (05 `verification_override`).
        verification_override: true,
        grants: [
          { ...companies.acme, level: "operator" },
          { ...companies.beta, level: "operator" },
          { ...companies.gamma, level: "operator" },
          { ...companies.htva, level: "operator" },
        ],
      },
      entitlements,
      dock: {
        [COMPANY_IDS.acme]: {
          items: [
            {
              id: "link:drive",
              kind: "link_out",
              label: "Drive",
              icon: "folder",
              href: "https://drive.google.com",
              pinned_by: "company",
            },
          ],
        },
      },
    },
  },
  {
    email: "owner@acme.example",
    password: "demo",
    label: "Acme owner (also Beta admin)",
    me: {
      user: { id: USER_IDS.owner, email: "owner@acme.example", name: "Maya Owner" },
      memberships: [
        {
          ...companies.acme,
          membership_id: membership(1),
          kind: "person",
          roles: ["owner"],
        },
        {
          ...companies.beta,
          membership_id: membership(2),
          kind: "person",
          roles: ["admin"],
        },
      ],
      agency: null,
      entitlements: {
        [COMPANY_IDS.acme]: entitlements[COMPANY_IDS.acme] ?? {},
        [COMPANY_IDS.beta]: entitlements[COMPANY_IDS.beta] ?? {},
      },
    },
  },
  {
    email: "viewer@acme.example",
    password: "demo",
    label: "Acme client viewer",
    me: {
      user: { id: USER_IDS.viewer, email: "viewer@acme.example", name: "Victor Viewer" },
      memberships: [
        {
          ...companies.acme,
          membership_id: membership(3),
          kind: "person",
          roles: ["client_viewer"],
        },
      ],
      agency: null,
      entitlements: { [COMPANY_IDS.acme]: entitlements[COMPANY_IDS.acme] ?? {} },
    },
  },
  {
    // MEMBER-ROLE: a plain team member — Chat, Work, Chart and Agents; no Setup, no KPI reads.
    email: "member@acme.example",
    password: "demo",
    label: "Acme member",
    me: {
      user: { id: USER_IDS.member, email: "member@acme.example", name: "Mia Member" },
      memberships: [
        {
          ...companies.acme,
          membership_id: membership(4),
          kind: "person",
          roles: ["member"],
        },
      ],
      agency: null,
      entitlements: { [COMPANY_IDS.acme]: entitlements[COMPANY_IDS.acme] ?? {} },
    },
  },
  {
    // CHART-DIMENSIONS: a member who holds a seat — the chart's seat-holder view (the floor
    // everywhere; KPI and load on their own line; cost never).
    email: "jordan@acme.example",
    password: "demo",
    label: "Acme seat holder (Jordan Lee, Recruiting lead)",
    me: {
      user: { id: USER_IDS.holder, email: "jordan@acme.example", name: "Jordan Lee" },
      memberships: [
        {
          ...companies.acme,
          membership_id: uid(0x1000, 0xa11),
          kind: "person",
          roles: ["member"],
        },
      ],
      agency: null,
      entitlements: { [COMPANY_IDS.acme]: entitlements[COMPANY_IDS.acme] ?? {} },
    },
  },
  {
    // HARDEN-mfa: Gamma requires two-step sign-in of its owners and admins (`settings.security`,
    // seeded on in createFakeApi) and Gwen has not set it up: the forced-enrolment screen.
    email: "admin@gamma.example",
    password: "demo",
    label: "Gamma admin (must set up two-step sign-in)",
    me: {
      user: { id: USER_IDS.gammaAdmin, email: "admin@gamma.example", name: "Gwen Gamma" },
      memberships: [
        {
          ...companies.gamma,
          membership_id: membership(6),
          kind: "person",
          roles: ["admin"],
        },
      ],
      agency: null,
      entitlements: { [COMPANY_IDS.gamma]: entitlements[COMPANY_IDS.gamma] ?? {} },
    },
  },
  {
    // FOUNDER-OS (12): HTVA's founder — owner of Founder OS (the portfolio company) and of HTVA,
    // an agency operator on every client. The account holds HTVA (owned), Acme and Beta
    // (clients); Gamma stays outside it (fake-portfolio.ts).
    email: "founder@htva.example",
    password: "demo",
    label: "HTVA founder (Founder OS)",
    me: {
      user: {
        id: PORTFOLIO.founderUserId,
        email: "founder@htva.example",
        name: PORTFOLIO.founderName,
      },
      memberships: [
        {
          company_id: PORTFOLIO.companyId,
          company_slug: PORTFOLIO.slug,
          company_name: "Founder OS",
          company_calendar: portfolioCompany().calendar_config,
          membership_id: PORTFOLIO.founderMembership,
          kind: "person",
          roles: ["owner"],
        },
        {
          ...companies.htva,
          membership_id: PORTFOLIO.htvaMembership,
          kind: "person",
          roles: ["owner"],
        },
      ],
      agency: {
        agency_id: "0193a0a0-0000-7000-8000-00000000a9e1",
        role: "admin",
        template_author: false,
        verification_override: false,
        grants: [
          { ...companies.acme, level: "operator" },
          { ...companies.beta, level: "operator" },
          { ...companies.gamma, level: "operator" },
          { ...companies.htva, level: "operator" },
        ],
      },
      entitlements: {
        ...entitlements,
        [PORTFOLIO.companyId]: { core: "active", kpi: "active", chat: "active" },
      },
      portfolio: {
        account_id: PORTFOLIO.accountId,
        company_id: PORTFOLIO.companyId,
        level: "owner",
      },
    },
  },
];

/**
 * HARDEN-mfa: the seeded accounts' `/v1/me` `mfa` as first served (the twin recomputes it on
 * every `/v1/me`, from the account's enrolment and the companies' settings). The operator is
 * enrolled (fake-mfa.ts `DEMO_TOTP_SECRET`); Gwen is required by Gamma and not enrolled.
 */
export const DEMO_ACCOUNTS: FakeAccount[] = SEEDED_ACCOUNTS.map((a) => ({
  ...a,
  me: {
    ...a.me,
    operator_name: "HTVA",
    mfa: {
      enrolled: DEMO_ENROLLED.includes(a.email),
      operator_required: a.me.agency !== null,
      required_company_ids: a.email === "admin@gamma.example" ? [COMPANY_IDS.gamma] : [],
    },
  },
}));

const PROBLEM_TYPE = "https://companyos.app/errors/";

/** M8-ASSESS: the `me` a token-authed respondent route sees (no account). */
const ANONYMOUS_ME = {
  user: { id: "00000000-0000-7000-8000-000000000000", email: "", name: "" },
  memberships: [],
  agency: null,
  entitlements: {},
} as unknown as MeResponse;

export function problem(
  status: number,
  code: Problem["code"],
  title: string,
  detail?: string,
  /** Extension members (RFC 9457 §3.2), e.g. `invitation_invalid`'s `reason`. */
  extras?: Record<string, unknown>,
): Response {
  const body = {
    type: `${PROBLEM_TYPE}${code}`,
    title,
    status,
    code,
    request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
    ...(detail ? { detail } : {}),
    ...extras,
  };
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/problem+json" },
  });
}

export const notFound = () => problem(404, "not_found", "Not found");

/** The value each `json()` response was made from (a Response's own body is read async). */
const jsonBodies = new WeakMap<Response, unknown>();

export function json(body: unknown, status = 200): Response {
  const res = new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json" },
  });
  jsonBodies.set(res, body);
  return res;
}

/**
 * The body of a `json()` response, synchronously: a fake that composes another twin's handler
 * (M4-03's crews read the inbox and post into chat) reads its answer without awaiting it.
 */
export function peekJson<T>(res: Response): T | undefined {
  return res.ok ? (jsonBodies.get(res) as T | undefined) : undefined;
}

export function featureDisabled(feature: string): Response {
  return new Response(
    JSON.stringify({
      type: `${PROBLEM_TYPE}feature_disabled`,
      title: "Feature not enabled",
      status: 403,
      code: "feature_disabled",
      feature,
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      detail: `The ${feature} add-on is not enabled for this company.`,
    }),
    { status: 403, headers: { "content-type": "application/problem+json" } },
  );
}

/** One request as the fake saw it (`id` is null for a route the fake does not serve). */
export type FakeCall = { id: RouteId | null; method: string; path: string };

export type FakeApi = {
  fetch: typeof fetch;
  /** STEP1-OUTBOX test hook: put an item in a company's outbox (empty otherwise). */
  addOutboxItem(item: FakeOutboxItem): void;
  /** LOOP-BREAKER test hook: a loop the breaker caught in a company (none otherwise). */
  addLoopIncident(companyId: string, incident: FakeLoopIncident): void;
  /**
   * MONITORING-P1 test hook: the DE's bridge answers a run's live stop request now (stopped, or
   * could not stop); false when the run has none. The demo plays it by time (fake-monitoring.ts).
   */
  answerStop(runId: string, ok: boolean): boolean;
  /**
   * SCREENS-S1 test hook: set a company's stack maxima (0 = none of that kind); SCREENS-VIEWER:
   * and how its browsers are watched (`browserLive`).
   */
  setScreensCapacity(
    companyId: string,
    max: { browsers?: number; desktops?: number; browserLive?: "vnc" | "screenshot" },
  ): void;
  /** INC2-INTAKE: something a company received, and an integration job (a dead letter, say). */
  addInboundEvent(e: FakeInboundEvent): void;
  addIntegrationJob(j: FakeIntegrationJob): void;
  /** INC2-CONTACT test hook: put a contact in a company (none otherwise). */
  addContact(c: FakeContact): void;
  /** RECALL-PROCEDURES test hook: a DE's recorded run of a playbook (none otherwise). */
  addProcedureRun(companyId: string, run: FakeProcedureRun): void;
  /** Signs in; false for a wrong password or a pending two-step challenge (`signInStep`). */
  signIn(email: string, password: string): boolean;
  /**
   * HARDEN-mfa: Better Auth's `sign-in/email`: signed in, or (enrolled, untrusted device) a
   * pending challenge and no session yet, or refused.
   */
  signInStep(email: string, password: string): "signed_in" | "two_factor" | "invalid";
  /** Answers the pending sign-in challenge; null when signed in, else the failure. */
  verifyTwoFactor(
    kind: "totp" | "backup",
    code: string,
    trustDevice: boolean,
  ): Promise<AuthFailure | null>;
  enableTwoFactor(
    email: string,
    password: string,
  ): { totpURI: string; backupCodes: string[] } | AuthFailure;
  /** Finishes enrolment (verify-totp with the session). */
  confirmTwoFactor(email: string, code: string): Promise<AuthFailure | null>;
  disableTwoFactor(email: string, password: string): AuthFailure | null;
  regenerateRecoveryCodes(email: string, password: string): string[] | AuthFailure;
  /** Test hook: the account's raw authenticator secret (`createOTP(secret).totp()`). */
  twoFactorSecret(email: string): string | null;
  /** Test hook: the recovery codes not spent yet. */
  recoveryCodes(email: string): string[];
  /** Test hook: whether this browser is a trusted device of the account (no challenge). */
  setTrustedDevice(email: string, trusted: boolean): void;
  signOut(): void;
  readonly signedInAs: string | null;
  /** Test hook: replace an account's `/v1/me` payload. */
  setMe(email: string, me: MeResponse): void;
  /** Mail the API would have sent (invitations, sign-in links), oldest first. */
  readonly mailbox: FakeMail[];
  sendMagicLink(email: string, callbackPath: string, name?: string): string;
  lastMagicLink(email: string): string | null;
  /** Opens a mailed sign-in link: signs in (creating the account) and returns its landing path. */
  openMagicLink(token: string): string | null;
  sessions(): {
    id: string;
    createdAt: string;
    expiresAt: string;
    userAgent: string | null;
    current: boolean;
  }[];
  revokeSession(id: string): void;
  hasPassword(email: string): boolean;
  /** Null when changed, else Better Auth's error code. */
  changePassword(email: string, current: string, next: string): string | null;
  updateName(email: string, name: string): void;
  requestPasswordReset(email: string): void;
  lastResetToken(email: string): string | null;
  /** Null when reset, else Better Auth's error code. */
  resetPassword(token: string, next: string): string | null;
  /** Null when set (link-only accounts), else Better Auth's error code. */
  setPassword(email: string, next: string): string | null;
  /**
   * WF-FIX test hook: marks a DE self-hosted (production: its lead profile's
   * `overrides.self_hosted` and `overrides.runtime`), with queued wake-ups waiting for it: the
   * `requested_at` of each (ISO), or a count of them requested a minute apart from a fixed
   * instant (`SELF_HOSTED_INBOX_BASE`), so the oldest never moves with the clock.
   */
  selfHost(
    membershipId: string,
    runtime: NonNullable<DeProfile["self_hosted"]>["runtime"],
    inbox?: number | string[],
  ): void;
  /**
   * Test hook: set an add-on's status for a company on every account that sees it (the next
   * `/v1/me` reads it). Add-ons not sold yet (`agents`, `memory`) have no operator switch.
   */
  entitle(
    companyId: string,
    feature: string,
    status: "active" | "trial" | "grace" | "disabled",
  ): void;
  /**
   * Test hook (M4-03): the signed-in person's crew run proposes (`crewRun.propose`'s rules; the
   * twin has no run tokens), e.g. a drafted reply to one of their inbox items.
   */
  crewPropose(companyId: string, body: unknown): Promise<{ status: number; body: unknown }>;
  /** Test hook (HARDEN-pwa): the server's Web Push on (a test VAPID key) or off (the default). */
  setPushEnabled(on: boolean): void;
  /** Test hook (SKILLSMITH-HUB): a company's Skillsmith files a report (the agent made on first use). */
  fileSkillsmithReport(
    companyId: string,
    report: FakeSkillsmithReport,
  ): OperationOutput<"operator.agentHealth.review">;
  /** Test hook (SKILL-PROPOSALS-OPERATOR): a company's DE proposes a skill (maybe reviewed). */
  fileSkillProposal(
    companyId: string,
    proposal: FakeSkillProposal,
  ): OperationOutput<"operator.skillProposals.get">;
  /** Test hook (MEMORY-QUALITY-WATCH): the hourly watch raises a finding for a company. */
  fileMemoryFinding(
    companyId: string,
    finding: {
      kind: OperationOutput<"operator.memoryQuality.get">["findings"][number]["kind"];
      severity: OperationOutput<"operator.memoryQuality.get">["findings"][number]["severity"];
      title: string;
      suggestion?: string;
    },
  ): OperationOutput<"operator.memoryQuality.get">["findings"][number];
  /** Test hook (SKILLSMITH-OPPORTUNITIES): a company's Skillsmith files a helper opportunity. */
  fileSkillsmithOpportunity(
    companyId: string,
    opportunity?: FakeSkillsmithOpportunity,
  ): OperationOutput<"operator.opportunities.send">;
  /**
   * Local seed (INBOX-READABLE): Recall proposal notices in the owner's inbox, several per scope
   * (seats, a team, a space), for screenshots and tests of the inbox's Memory rows. Never on a
   * real server: the in-memory API only.
   */
  seedMemoryInbox(companyId: string): number;
  /**
   * Local seed (INBOX-PAGE-MAIN): an approval inbox item in the owner's inbox for each of the
   * company's pending approvals (Waiting on me), for screenshots and tests of the bell's peek.
   * Never on a real server: the in-memory API only.
   */
  seedWaitingInbox(companyId: string): number;
  /**
   * Test hook (CLOSED-LOOPS): a commitment between two members (membership ids); `inboxFor`
   * also puts its inbox item (an escalation) in that member's inbox.
   */
  seedCommitment(
    companyId: string,
    seed: FakeCommitmentSeed,
    opts?: { inboxFor?: string },
  ): OperationOutput<"commitments.get">;
  /** Test hook (COS-SLICE1): a pending follow-up card under a message. */
  suggest(companyId: string, seed: FakeSuggestionSeed): OperationOutput<"suggestions.accept">;
  /** FOUNDER-OS: limit a grant holder (user id) to some companies of the demo account. */
  setPortfolioSubset(userId: string, companyIds: string[] | null): void;
  /** Every request since the last `resetCalls()`, oldest first. */
  readonly calls: FakeCall[];
  resetCalls(): void;
};

/**
 * MEMBER-ROLE: `company#reader` reads (manager | client_viewer) — every contract route with
 * access `reader` (fake-contract.test.ts checks it), plus the Friday report reads, which the API
 * authorizes as `reader` too. A plain member (no manager or client-viewer role) gets 403.
 */
export const FAKE_READER_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "costs.list",
  "timeEntries.list",
  "metrics.list",
  "seats.metrics",
  "metrics.get",
  "targets.list",
  "observations.list",
  "scorecards.company",
  "reports.list",
  "reports.get",
  "reports.html",
  "reports.pdf",
  // REPORTS-UI: the caller's own report email.
  "reports.subscription",
  "reports.setSubscription",
]);

/** Routes behind the `kpi` add-on (the contract's `feature`; fake-contract.test.ts checks it). */
export const FAKE_KPI_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "seats.scorecard",
  "digitalEmployees.scorecard",
  "scorecards.company",
  "reports.list",
  // S1-17
  "metrics.list",
  "observations.list",
  "observations.create",
  "me.numbers",
  "targets.list",
  "reports.get",
  "reports.html",
  "reports.pdf",
  "reports.generate",
  "reports.update",
  "reports.send",
  "reports.reconcile",
  "reports.subscription",
  "reports.setSubscription",
  // Structure: metrics, targets, observation writes, publications, seat metric sets.
  ...STRUCTURE_KPI_ROUTES,
  // AGENT-REPORTING: reporting duties and the seat's KPI scopes.
  "reporting.seat",
  "reporting.putDuty",
  "reporting.deleteDuty",
  "seats.putKpiScopes",
]);

export type Principal = {
  me: MeResponse;
  company: FakeCompany;
  roles: CompanyRole[];
  operator: boolean;
  manager: boolean;
};

export type Ctx = {
  url: URL;
  params: Record<string, string>;
  body: unknown;
  me: MeResponse;
  /** Resolves `{co}` with the caller's roles; null = not visible (404). */
  principal: Principal | null;
  /** Request headers (`Idempotency-Key`). */
  headers: Headers;
};

export type Handler = (ctx: Ctx) => Response;

const ALL_SCOPES: AgentKeyScope[] = ["ingest:activity", "ingest:outcome", "ingest:cost"];

function compile(path: string): RegExp {
  const pattern = path.replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/\{(\w+)\}/g, "(?<$1>[^/]+)");
  return new RegExp(`^${pattern}$`);
}

export function page<T>(
  items: T[],
  url: URL,
): { data: T[]; next_cursor: string | null; prev_cursor: null } {
  const limit = Math.min(200, Number(url.searchParams.get("limit") ?? 50) || 50);
  const offset = Number(url.searchParams.get("cursor") ?? 0) || 0;
  const data = items.slice(offset, offset + limit);
  return {
    data,
    next_cursor: offset + limit < items.length ? String(offset + limit) : null,
    prev_cursor: null,
  };
}

const WEEK = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

export function validation(detail: string): Response {
  return problem(422, "validation_failed", "Validation failed", detail);
}

export function createFakeApi(
  options: {
    signedInAs?: string | null;
    today?: () => Date;
    /**
     * Test-only: add-on states merged into the demo accounts' entitlements per company id (e.g.
     * `agents`, which is not sold yet, so no demo company has it and no operator can enable it).
     */
    entitlements?: Record<string, MeResponse["entitlements"][string]>;
    /** M7-04: how the twin's server reaches Recall (default `connected`). */
    recall?: FakeRecallState;
    /**
     * D12, test-only: crews are bound to their person in Recall, so the consent step works. The
     * default (unbound) is the server today: binding crews is still an open decision.
     */
    personalConsentBound?: boolean;
    /**
     * DE-PROFILE-LIVE, test-only: a DE's profile and its shipped default, by membership id (no
     * seeded DE has either).
     */
    deProfiles?: Record<string, Pick<FakeDe, "profile" | "profile_default">>;
    /**
     * SKILL-OVERRIDES: company additions already written to some of our skills (by key), in
     * every company; `stale_default` makes the skill show "Default updated".
     */
    skillAdditions?: Record<
      string,
      { text: string; stale_default?: { version: string; text: string } }
    >;
  } = {},
): FakeApi {
  const accounts = new Map(DEMO_ACCOUNTS.map((a) => [a.email, structuredClone(a)]));
  for (const [companyId, extra] of Object.entries(options.entitlements ?? {}))
    for (const a of accounts.values())
      if (a.me.entitlements[companyId])
        a.me.entitlements[companyId] = { ...a.me.entitlements[companyId], ...extra };
  let current = options.signedInAs ?? null;
  const now = options.today ?? (() => new Date());
  // FOUNDER-OS: the demo founder's Founder OS company (`kind = portfolio`) sits beside them.
  const companies = [...seedCompanies(), portfolioCompany()];
  // HARDEN-mfa: Gamma requires two-step sign-in of its owners and admins (Gwen, not enrolled,
  // meets the forced-enrolment screen; the operator and the founder are enrolled).
  for (const c of companies)
    if (c.slug === "gamma")
      c.settings = { ...c.settings, security: { require_two_factor_for_admins: true } };
  const des = seedDes();
  for (const de of des) {
    const p = options.deProfiles?.[de.membership_id];
    if (p) Object.assign(de, structuredClone(p));
  }
  // DE-WIZARD: each DE's newest deploy (the deploys twin's array, shared by reference), for its
  // readiness. A DE the create route adds gets the same lookup.
  let deDeploys: readonly Deploy[] = [];
  let deployTick: () => void = () => {};
  const withLatestDeploy = (de: FakeDe) => {
    de.latestDeploy = () => {
      deployTick();
      const d = deDeploys
        .filter(
          (x) =>
            x.de_membership_id === de.membership_id &&
            x.target_company_id === COMPANY_IDS[de.company],
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      if (!d) return null;
      return {
        state: d.state,
        needs_approval: d.state === "staged" && d.path === "done_for_you" && d.kind === "first",
        reason:
          d.state === "rejected" ? d.rejected_reason : d.error?.message || d.error?.code || null,
      };
    };
    return de;
  };
  for (const de of des) withLatestDeploy(de);
  const calls: FakeCall[] = [];
  let keySeq = 0x100;
  // S1-17 state: rows written through the forms and imports, on top of the generated data.
  const createdTime: (TimeEntry & { batch_id?: string })[] = [];
  const observations: Observation[] = [
    ...seedGateObservations(),
    // AGENT-REPORTING: Riley's reported numbers (fake-reporting.ts).
    ...seedReportingObservations(companies, now()),
  ];
  const reportStore = new Map<string, FridayReportRecord[]>();
  const staleReports = new Set<string>();
  const batches: (ImportBatch & { company_id: string })[] = [];
  let seq = 0x1000;
  // UI des state: time-entry edits (null = voided) over generated rows, every listed row by id,
  // DE subscriptions per company, and LiteLLM issuances per DE.
  const timeEdits = new Map<string, TimeEntry | null>();
  const timeIndex = new Map<string, TimeEntry>();
  const subscriptionStore = new Map<string, DeSubscription[]>();
  /** CHART-HOWIWORK: each DE's "How I work" versions, oldest first (none seeded: no sample text). */
  const howIWorkStore = new Map<
    string,
    OperationOutput<"digitalEmployees.howIWorkVersions">["data"]
  >();
  /** TASK-BUDGETS: each DE's per-task limit override (none seeded: the defaults apply). */
  const taskLimitStore = new Map<
    string,
    OperationOutput<"digitalEmployees.taskLimits">["override"]
  >();
  const taskLimitsOf = (id: string): OperationOutput<"digitalEmployees.taskLimits"> => {
    const defaults = { steps: 60, cost_micros: 1_000_000, time_ms: 30 * 60_000 };
    const override = taskLimitStore.get(id) ?? null;
    return {
      membership_id: id,
      enabled: false,
      defaults,
      override,
      effective: {
        steps: override?.steps ?? defaults.steps,
        cost_micros: override?.cost_micros ?? defaults.cost_micros,
        time_ms: override?.time_ms ?? defaults.time_ms,
      },
    };
  };
  const issuances = new Map<
    string,
    {
      attempt: number;
      alias: string;
      key: string;
      expires_at: string;
      state: "issued" | "delivered";
    }
  >();
  // Operations state (add-ons, failed events, costs, report deliveries): see opsHandlers().
  const ops = createOpsState(now);
  const consumerRecords = ops.consumers;
  // M5-05: the agency console (approvals SLA, supervisor load, cost-to-serve, break-glass).
  const agencyState = createAgencyState(now);
  let approvalsOf: AgencyDeps["approvalsOf"] = () => [];
  // WF-FIX: every run the twins know, for a DE profile's `activity.last_run_at`.
  let runsOf: (companyId: string) => readonly LiveRunLike[] = () => [];
  /** WF-FIX: the newest run the twins know of this DE; the twin makes no MCP calls. */
  const activityOf = (companyId: string, de: FakeDe): NonNullable<DeProfile["activity"]> => ({
    last_run_at:
      runsOf(companyId)
        .filter((r) => r.membership_id === de.membership_id)
        .map((r) => r.created_at)
        .sort()
        .at(-1) ?? null,
    last_mcp_at: null,
  });

  const todayWeek = () => isoWeekKey(now());

  /** The company's reports (generated data, then mutated by generate / note / send). */
  function reportsOf(company: FakeCompany): FridayReportRecord[] {
    let list = reportStore.get(company.id);
    if (!list) {
      list = reports(company, todayWeek());
      reportStore.set(company.id, list);
    }
    return list;
  }

  /**
   * The server's `report_stale` (review #3, `sendStale`): a draft is stale, a frozen version is
   * not, an unfrozen one is when the data changed or a newer version of its week exists.
   */
  function staleOf(company: FakeCompany, r: FridayReportRecord): boolean {
    if (r.state === "draft" || !r.html_object_key) return true;
    if (r.frozen_at) return false;
    if (staleReports.has(r.id)) return true;
    return reportsOf(company).some((x) => x.week_key === r.week_key && x.version > r.version);
  }

  /** A ledger or observation change in `week`: generated, unsent versions go stale. */
  function touch(companyId: string, week: string) {
    const list = reportStore.get(companyId) ?? [];
    for (const r of list)
      if (r.week_key === week && r.state === "generated") staleReports.add(r.id);
  }

  /**
   * Every time entry of a DE's seat in a week: generated rows (none for a DE created here) plus
   * the ones written here, with the edits made through `PATCH`/`DELETE /time-entries/{id}`.
   */
  function entriesOf(de: FakeDe, week: string): TimeEntry[] {
    const rows = [
      ...(de.fresh ? [] : timeEntries(de, week, todayWeek())),
      ...createdTime.filter((t) => t.seat_id === de.seat_id && t.period_key === week),
    ].flatMap((t) => {
      const edit = timeEdits.get(t.id);
      return edit === undefined ? [t] : edit === null ? [] : [edit];
    });
    for (const t of rows) timeIndex.set(t.id, t);
    return rows;
  }

  function rolesIn(me: MeResponse, companyId: string): CompanyRole[] {
    const roles = new Set<CompanyRole>();
    for (const m of me.memberships)
      if (m.company_id === companyId) for (const r of m.roles) roles.add(r);
    for (const g of me.agency?.grants ?? [])
      if (g.company_id === companyId)
        roles.add(g.level === "operator" ? "agency_operator" : "client_viewer");
    return [...roles];
  }

  function principalFor(me: MeResponse, companyId: string | undefined): Principal | null {
    if (!companyId) return null;
    const company = companies.find((c) => c.id === companyId);
    if (!company) return null;
    const roles = rolesIn(me, companyId);
    if (!roles.length) return null;
    return {
      me,
      company,
      roles,
      operator: roles.includes("agency_operator"),
      manager: roles.some((r) => r === "owner" || r === "admin" || r === "agency_operator"),
    };
  }

  /** REPORTS-UI: the caller's own person membership here (none for an operator's grant). */
  const reportSelf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;
  const selfOnly = () =>
    problem(
      403,
      "forbidden",
      "Forbidden",
      "only a signed-in member changes their own report email",
    );
  const subscriptionOf = (
    p: Principal,
    self: NonNullable<ReturnType<typeof reportSelf>>,
  ): OperationOutput<"reports.subscription"> => {
    const fr = p.company.settings.friday_report;
    const email = p.me.user.email.normalize("NFKC").toLowerCase();
    return {
      eligible: self.roles.some((r) => r === "owner" || r === "admin" || r === "client_viewer"),
      subscribed: !(fr?.opted_out_membership_ids ?? []).includes(self.membership_id),
      extra: (fr?.recipients_extra ?? []).some(
        (e) => e.normalize("NFKC").trim().toLowerCase() === email,
      ),
    };
  };

  const viewerOf = (p: Principal): Viewer => ({
    operator: p.operator,
    // HELPERS-2: a DE's stored person name shows only while the company names DEs by person.
    personNames: p.company.settings.workforce?.de_names === "person",
  });
  const kpiOn = (me: MeResponse, companyId: string) => {
    const s = me.entitlements[companyId]?.kpi ?? "disabled";
    return s === "active" || s === "trial" || s === "grace";
  };
  const desOf = (companyId: string) => des.filter((d) => COMPANY_IDS[d.company] === companyId);
  /** An add-on's state for a company, its dependencies included (`agents` needs `memory`). */
  const addonState = (me: MeResponse, companyId: string, key: "chat" | "agents" | "memory") => {
    const map = me.entitlements[companyId] ?? {};
    const all =
      key === "agents" ? [map.agents, map.memory] : key === "memory" ? [map.memory] : [map.chat];
    if (all.some((s) => s !== "active" && s !== "trial" && s !== "grace")) return "off";
    return all.includes("grace") ? "grace" : "on";
  };
  const addonOf = (id: RouteId): "chat" | "agents" | "memory" | null =>
    FAKE_CHAT_ROUTES.has(id) || FAKE_COS_CHAT_ROUTES.has(id)
      ? "chat"
      : FAKE_AGENTS_ROUTES.has(id) ||
          FAKE_SELF_SERVE_ROUTES.has(id) ||
          FAKE_COS_AGENTS_ROUTES.has(id) ||
          FAKE_COS_MODES_ROUTES.has(id) ||
          FAKE_BRIEFS_ROUTES.has(id) ||
          FAKE_BRIEF_RULES_ROUTES.has(id) ||
          // SCREENS-S1: behind the `agents` add-on, as the contract (fake-screens.ts).
          (FAKE_SCREENS_ROUTES as readonly RouteId[]).includes(id) ||
          // B5-DECIDE: behind the `agents` add-on, as the contract (fake-decide.ts).
          (FAKE_DECIDE_ROUTES as readonly RouteId[]).includes(id) ||
          // LOOP-BREAKER: behind the `agents` add-on, as the contract (fake-loops.ts).
          (FAKE_LOOP_ROUTES as readonly RouteId[]).includes(id)
        ? "agents"
        : FAKE_MEMORY_ROUTES.has(id) ||
            FAKE_REACH_ROUTES.has(id) ||
            FAKE_RULES_MEMORY_ROUTES.has(id) ||
            FAKE_MEMORY_PEOPLE_ROUTES.has(id)
          ? "memory"
          : null;
  // Work mode (M2-07): spaces, boards, views, fields, tasks, follows, inbox, task threads. Its
  // spaces are the company's one list: the chat twin reads and writes the same array.
  let workBridge: WorkBridge | null = null;
  // Each person's starred boards (the WOS-BUNDLE twin's favorites; the Calendar's tasks overlay).
  let starredBoardsOf: ((p: Principal, membershipId: string) => string[]) | null = null;
  let workTemplateHandlers: FakeWorkTemplateHandlers | null = null;
  const workHandlers = createWorkFake({
    now,
    companies,
    desOf,
    // SHARED-DES: assigning a shared DE needs its `task` grant (fake-shared-des.ts).
    taskRefusal: (p, seatIds) => sharedDes.taskRefusal(p, seatIds),
    // TASK-LOCK: the runs the agents twin holds (a running run on a task locks it).
    runsOf: (companyId) => runsOf(companyId),
    ownerMembership: (companyId) => {
      for (const a of accounts.values())
        for (const m of a.me.memberships)
          if (m.company_id === companyId && m.roles.includes("owner")) return m.membership_id;
      return null;
    },
    featureOn: (p, feature) => {
      const s = p.me.entitlements[p.company.id]?.[feature] ?? "disabled";
      return s === "active" || s === "trial" || s === "grace";
    },
    registerBridge: (bridge) => {
      workBridge = bridge;
    },
    // `templates.apply` (fake-org.ts) creates spaces, channels, boards and tasks through these.
    registerTemplateHandlers: (handlers) => {
      workTemplateHandlers = handlers;
    },
  });
  // M7-02: pages, on the Work twin's spaces, tasks and threads.
  const pagesFake = createPagesFake({ now, companies, work: () => workBridge });
  // BIZ-PLAYBOOKS / BIZ-CLIENT-HEALTH (fake-business.ts): over the other twins, as the caller.
  const businessFake = createBusinessFake({
    now,
    companies,
    work: () => workBridge,
    call: <T>(id: RouteId, ctx: Ctx): T | undefined => {
      const res = handlers[id]?.(ctx);
      return res instanceof Response ? peekJson<T>(res) : undefined;
    },
    approvalsOf: (companyId) => approvalsOf(companyId),
  });
  // RECALL-RULES / RECALL-PROCEDURES / RECALL-UPKEEP (fake-rules.ts): starts empty.
  const rulesFake = createRulesFake({
    now,
    desOf: (companyId) => desOf(companyId),
    recallLive: (options.recall ?? "connected") === "connected" || options.recall === "not_set_up",
    approvalsOf: (companyId) => approvalsOf(companyId),
    call: <T>(id: RouteId, ctx: Ctx): T | undefined => {
      const res = handlers[id]?.(ctx);
      return res instanceof Response ? peekJson<T>(res) : undefined;
    },
    nameOf: (companyId, membershipId) =>
      [...accounts.values()].find((a) =>
        a.me.memberships.some(
          (x) => x.company_id === companyId && x.membership_id === membershipId,
        ),
      )?.me.user.name ?? null,
  });
  // TASK-FILES: task files (attachments, folders, trash, the agent tools) on the Work twin's tasks.
  const filesFake = createFilesFake({
    now,
    work: () => workBridge,
    nameOf: (companyId, membershipId) => {
      const de = desOf(companyId).find((d) => d.membership_id === membershipId);
      if (de) return { name: de.display_name, kind: "digital_employee" };
      for (const a of accounts.values())
        for (const m of a.me.memberships)
          if (m.company_id === companyId && m.membership_id === membershipId)
            return { name: a.me.user.name, kind: "person" };
      return null;
    },
    seedMembers: (companyId) => {
      let owner: string | null = null;
      for (const a of accounts.values())
        for (const m of a.me.memberships)
          if (!owner && m.company_id === companyId && m.roles.includes("owner"))
            owner = m.membership_id;
      const agent = desOf(companyId).find((d) => d.display_name === "Riley")?.membership_id ?? null;
      return { owner, agent };
    },
  });
  // SEARCH-NOTIF: ⌘K over the twin's own list routes, and notification preferences. The palette
  // asks those routes as the caller through `internal` (set once `fakeFetch` exists).
  let internal: InternalFetch = async () => notFound();
  const paletteFake = createPaletteFake({ internal: (path, init) => internal(path, init), now });
  // GUIDED-FLOW: the guided-flow runs over the seats, responsibilities, KPIs, budgets and DEs.
  const flowsFake = createFlowsFake({
    json,
    problem,
    notFound,
    now,
    nextSeq: () => ++seq,
    seatsOf: (p) => structureStores?.seatsOf(p.company) ?? [],
    internal: (path, init) => internal(path, init),
  });
  // HARDEN-pwa: Web Push subscriptions (off, as without VAPID keys, until `setPushEnabled`).
  const pushFake = createPushFake({ now });
  // SKILLSMITH-HUB: Agent health (empty until a test files a report through `fileSkillsmithReport`).
  const agentHealthFake = createAgentHealthFake({ now, companies: () => companies });
  // SKILL-PROPOSALS-OPERATOR: the operator-only queue (empty until `fileSkillProposal`).
  const skillProposalsFake = createOperatorSkillProposalsFake({ now, companies: () => companies });
  // ADMIN-INSIGHTS: Ask and pinned answers, over the twin's own tasks, spend and approvals.
  const insightsFake = createInsightsFake({
    now,
    internal: (path, init) => internal(path, init),
    tasksOf: (companyId) => workBridge?.tasksOf(companyId) ?? [],
  });
  // SKILLSMITH-OPPORTUNITIES: the helper sales pipeline and helper quotes (empty until a test
  // files an opportunity through `fileSkillsmithOpportunity`).
  const opportunitiesFake = createOpportunitiesFake({
    now,
    companies: () => companies,
    people: (companyId) => {
      type Person = OperationOutput<"operator.opportunities.get">["people"][number];
      const out = new Map<string, Person>();
      const roleOf = (roles: readonly string[]): Person["role"] | null =>
        roles.includes("owner")
          ? "owner"
          : roles.includes("admin")
            ? "admin"
            : roles.includes("member")
              ? "member"
              : null;
      for (const a of accounts.values())
        for (const m of a.me.memberships) {
          const role = roleOf(m.roles);
          if (m.company_id === companyId && m.kind === "person" && role)
            out.set(m.membership_id, {
              membership_id: m.membership_id,
              name: a.me.user.name,
              email: a.me.user.email,
              role,
            });
        }
      const company = companies.find((c) => c.id === companyId);
      if (company)
        for (const p of people(company, desOf(companyId), false)) {
          const role = roleOf(p.roles);
          if (role && !out.has(p.id))
            out.set(p.id, { membership_id: p.id, name: p.display_name, email: p.email, role });
        }
      return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    des: (companyId) =>
      desOf(companyId)
        .filter((d) => !d.removed_at)
        .map((d) => ({ membership_id: d.membership_id, name: d.display_name })),
    notify: (companyId, offer, to) => {
      const state = workBridge?.stateOf(companyId);
      if (!state) return;
      const at = now().toISOString();
      const price = [
        offer.one_off_minor > 0 ? `$${(offer.one_off_minor / 100).toFixed(2)} now` : null,
        offer.monthly_minor > 0 ? `$${(offer.monthly_minor / 100).toFixed(2)}/month` : null,
      ]
        .filter(Boolean)
        .join(" + ");
      for (const membership_id of to)
        state.inbox.push({
          company_id: companyId,
          id: crypto.randomUUID(),
          membership_id,
          kind: "system",
          subject_kind: "helper_offer",
          subject_id: offer.id,
          message_id: null,
          source_event_id: crypto.randomUUID(),
          coalesce_key: null,
          count: 1,
          summary: `Helper quote: ${offer.helper_name} (${price}). Approve or decline on Billing`,
          read_at: null,
          done_at: null,
          snoozed_until: null,
          desktop: false,
          category: null,
          from_membership_id: null,
          nudged_at: null,
          escalated_at: null,
          created_at: at,
          updated_at: at,
        });
    },
  });
  // SCREENS-S1: the company's computer, its live screens, the Waiting list and browser logins.
  const screensFake = createScreensFake({
    now,
    desOf,
    peopleOf: (companyId) =>
      [...accounts.values()].flatMap((a) =>
        a.me.memberships
          .filter((m) => m.company_id === companyId && m.kind === "person")
          .map((m) => ({ membership_id: m.membership_id, name: a.me.user.name, roles: m.roles })),
      ),
    seatTitle: (p, seatId) =>
      (structureStores?.seatsOf(p.company) ?? seats(p.company, desOf(p.company.id))).find(
        (s) => s.id === seatId,
      )?.title ?? null,
  });
  // WF-CONTROL: Claude Code connections, the Workforce Control add-on, profile revisions.
  const controlFake = createControlFake({
    now,
    accounts: () => [...accounts.values()].map((a) => a.me),
    recordSwitch: (companyId, status, userId, note) =>
      recordSetupSwitch(ops, companyId, "workforce_plugin", status, userId, note, now()),
    deReadable: (p, id) => Boolean(deIn(p, id)),
    agentExists: (p, id) => Boolean(workforceGuards?.agentOf(p, id)),
    // `mfa` is set below (fake-mfa.ts); only read when a request runs.
    twoFactor: {
      enrolled: (email) => mfa.enrolled(email),
      verify: (email, code) => mfa.stepUp(email, code),
    },
  });
  // CONNECTIONS: Operator console › Connections (the platform's service endpoints).
  const serviceEndpointsFake = createServiceEndpointsFake({ now });
  // SATELLITE-REGISTRY: Operator › Platform › Satellites.
  const satellitesFake = createSatellitesFake({ now });
  // OPERATOR-CONSOLE: Operator › Settings (versioned settings, baseline, roster, operator agents).
  const operatorConsoleFake = createOperatorConsoleFake({
    now,
    placement: satellitesFake.placement,
  });
  // WALLET-P1: Setup › Billing and Operator › Billing (fake-wallet.ts).
  const walletFake = createWalletFake({ now, companies });
  // STEP1-OUTBOX: Workforce › Outbox (fake-outbox.ts; empty: no sample data).
  const outboxFake = createOutboxFake({ now });
  // LOOP-BREAKER: Workforce › Loops (fake-loops.ts; empty: no sample data).
  const loopsFake = createLoopsFake({ now });
  // OOO-DELEGATE: Account › Out of office (fake-ooo.ts; empty).
  const oooFake = createOooFake({ now });
  // INC2-INTAKE: Workforce › Intake (fake-intake.ts; empty: no sample data).
  const intakeFake = createIntakeFake({ now });
  // INC2-CONTACT: the contact list and connection grants (fake-contacts.ts; empty: no samples).
  const contactsFake = createContactsFake({
    now,
    newId: () => crypto.randomUUID(),
    // INC2-ACCESS: the matrix reads the twin's own connections and DEs (handlers below)
    call: (route, ctx) => handlers[route]?.(ctx),
  });
  // DOCS-KB: the knowledge sources read every page of the company as a manager would.
  const knowledgeFake = createKnowledgeFake({
    now,
    desOf,
    peopleOf: (companyId) =>
      [...accounts.values()].flatMap((a) =>
        a.me.memberships
          .filter((m) => m.company_id === companyId && m.kind === "person")
          .map((m) => ({ membership_id: m.membership_id, name: a.me.user.name })),
      ),
    pagesOf: (p) => {
      const url = new URL(
        `http://fake.local/v1/companies/${p.company.id}/pages?limit=200&include_archived=true`,
      );
      const res = pagesFake.handlers["pages.list"]?.({
        url,
        params: { co: p.company.id },
        body: undefined,
        me: p.me,
        principal: { ...p, roles: ["owner"], manager: true },
        headers: new Headers(),
      });
      return (res && peekJson<{ data: never[] }>(res)?.data) ?? [];
    },
    spacesOf: (companyId) =>
      (workBridge?.spacesOf(companyId) ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        kind: s.kind,
      })),
    agentsState: (p) => addonState(p.me, p.company.id, "agents"),
    readsPages: (p) => p.roles.some((r) => r !== "client_viewer"),
  });
  // Chat (M2-06): channels, topics, messages and what the chat screens read, in Work's spaces.
  const chatFake = createChatFake({
    now,
    json,
    problem: problem as never,
    notFound,
    validation,
    page,
    desOf,
    // AGENT-MENTION: on (or trial) admits a run; grace is read-only, as the API's consumers.
    // The signed-in account's view first (setMe in tests and the demo switcher change it).
    agentsOn: (companyId) => {
      const mine = current ? accounts.get(current)?.me.entitlements[companyId]?.agents : undefined;
      if (mine) return mine === "active" || mine === "trial";
      for (const a of accounts.values()) {
        const s = a.me.entitlements[companyId]?.agents;
        if (s) return s === "active" || s === "trial";
      }
      return false;
    },
    work: () => workBridge,
    pagesOf: (companyId, spaceId) => pagesFake.sidebarPages(companyId, spaceId),
    // CHAT-FILES "Save to task": the files twin stores the independent copy.
    saveChatCopy: (p, taskId, input, provenance) =>
      filesFake.saveChatCopy(p, taskId, input, provenance),
    // CHAT-PRESENCE: a DE waiting on a login needs its owner; a live screen shows on its card.
    screens: (companyId) => screensFake.presenceOf(companyId),
  });
  // COS-SLICE1: the Chief of Staff settings and the follow-up cards (fake-cos.ts).
  const cosFake = createCosFake({
    now,
    desOf,
    channelsOf: (companyId) => {
      const team = chatFake.meetings.teamChannel(companyId);
      return team ? [team] : [];
    },
  });
  const peopleOf = (companyId: string) =>
    [...accounts.values()].flatMap((a) =>
      a.me.memberships
        .filter((m) => m.company_id === companyId && m.kind === "person")
        .map((m) => ({ membership_id: m.membership_id, name: a.me.user.name })),
    );
  // COS-MODES: Solo / Team, the default level and each person's "Who may direct my agents".
  const cosModesFake = createCosModesFake({
    desOf,
    ceoOf: (companyId) => ownerOf(companyId),
    peopleOf,
  });
  // DE-BRIEFS: the brief rhythm at three levels and the CoS report's sender (fake-briefs.ts).
  const briefsFake = createBriefsFake({
    now,
    desOf,
    peopleOf,
    ceoOf: (companyId) => ownerOf(companyId),
    timezoneOf: (co) => companies.find((x) => x.id === co)?.calendar_config.timezone ?? "UTC",
  });
  // DC-11: the self-serve deploy refuses a seat without a budget (the agents twin holds them).
  let seatHasBudget: (companyId: string, seatId: string) => boolean = () => false;
  // M5-04: the finance twin's budget vs actual reads the same budgets.
  let budgetsOf: (companyId: string) => BudgetRow[] = () => [];
  const agentsOn = (p: Principal) => {
    const s = p.me.entitlements[p.company.id]?.agents ?? "disabled";
    return s === "active" || s === "trial" || s === "grace";
  };
  const runsTwin = createRunsTwin({ now, desOf, agentsOn });
  // MONITORING-P1: a run's telemetry and Stop on host (the run as the twins' `runs.get` answers
  // the caller), the alerts and Operator › Guards (fake-monitoring.ts).
  const monitoringFake = createMonitoringFake({
    now,
    agentsOn,
    runFor: (ctx, id) => {
      const res = handlers["runs.get"]?.({ ...ctx, params: { ...ctx.params, id } });
      if (!(res instanceof Response)) return notFound();
      return res.ok ? (peekJson<MonitoredRun>(res) ?? notFound()) : res;
    },
    runsOf: (companyId) =>
      runsOf(companyId).flatMap((r) =>
        r.id
          ? [
              {
                id: r.id,
                membership_id: r.membership_id,
                state: r.state ?? "queued",
                started_at: r.started_at ?? null,
                created_at: r.created_at,
              },
            ]
          : [],
      ),
    refuseOperator: refuseNonPlatformAdmin,
    desOf: (companyId) =>
      desOf(companyId).map((d) => ({
        membership_id: d.membership_id,
        display_name: d.display_name,
        seat_id: d.seat_id ?? null,
        seat_title: d.seat_title ?? null,
      })),
  });
  const agentsHandlers = createAgentsFake({
    now,
    companies,
    desOf,
    ownerMembership: (companyId) => ownerOf(companyId),
    featureOn: (p, feature) => {
      const s = p.me.entitlements[p.company.id]?.[feature] ?? "disabled";
      return s === "active" || s === "trial" || s === "grace";
    },
    extraRuns: (companyId) => [...chatFake.runsOf(companyId), ...runsTwin.runsOf(companyId)],
    shareSeatBudget: (has) => {
      seatHasBudget = has;
    },
    shareBudgets: (of) => {
      budgetsOf = of;
    },
    shareApprovals: (read) => {
      approvalsOf = read;
    },
    shareRuns: (read) => {
      runsOf = read;
    },
    // AGENT-REPORTING: the reporting runs and the value they hold for review; a decided
    // `kpi_value` records the value in the same request.
    seedMore: (company, s) => {
      const more = seedReportingRuns(company, desOf(company.id), now());
      s.runs.push(...more.runs);
      for (const [id, steps] of more.steps) s.steps.set(id, steps);
      s.approvals.push(...more.approvals);
      // B2-GATES: a DE's batch review over real tasks of the work twin (fake-gates.ts).
      const w = workBridge?.stateOf(company.id);
      const batch = w
        ? seedBatchReview(
            company,
            desOf(company.id),
            w.boards.filter((b) => !b.archived_at),
            w.tasks.filter((t) => !t.deleted),
            now(),
          )
        : null;
      if (batch) s.approvals.push(batch);
      // BIZ-PLAYBOOKS: a DE's proposed new version of the company's first SOP.
      const docUpdate = businessFake.seedDocUpdate(
        company,
        desOf(company.id),
        pagesFake.pagesOf(company),
      );
      if (docUpdate) s.approvals.push(docUpdate);
    },
    onDecided: (company, a) => {
      reporting.recordReview(company, a);
      // B2-GATES: a batch review applies row by row, as the server's executor does.
      if (a.kind === "task_batch")
        applyBatchReview(a.approved_payload?.change, (id) => workBridge?.taskOf(company.id, id));
    },
    // ACTIVITY: the Work twin's tasks; Who = an AG (its DE) or a team (its seats).
    tasksOf: (companyId) =>
      (workBridge?.tasksOf(companyId) ?? []).map((t) => ({
        id: t.id,
        title: t.title,
        board_id: t.board_id,
      })),
    whoOf: (p, kind, id) =>
      kind === "ag"
        ? { memberships: [workforceGuards?.reportsToOf(p, id) ?? null].filter((x) => x !== null) }
        : {
            seats: (structureStores?.teamMembersOf(p.company) ?? [])
              .filter((m) => m.team_id === id)
              .map((m) => m.seat_id),
          },
  });
  const agency = createAgency(agencyState, {
    now,
    todayWeek,
    companies: () => companies,
    desOf,
    approvalsOf: (companyId) => approvalsOf(companyId),
    postedCosts: (companyId) => ops.costs.filter((c) => c.company_id === companyId),
  });
  const deIn = (p: Principal, id: string | undefined) =>
    desOf(p.company.id).find((d) => d.membership_id === id) ?? null;
  /** PURGE-RETIRED-AGENTS: a DE's plan (its tasks would go to its supervisor's seat). */
  const purgePlanOfDe = (de: FakeDe) =>
    fakePurgePlan({
      kind: "digital_employee",
      id: de.membership_id,
      name: de.display_name,
      status: de.status,
      keys: de.keys.length,
      seat: {
        seat_id: de.supervisor.seat_id,
        seat_title: de.supervisor.title,
        holder_name: de.supervisor.name,
      },
    });

  function weekParam(url: URL, name: string): string | Response {
    const type = url.searchParams.get(`${name}_type`) ?? "week";
    const value = url.searchParams.get(name) ?? (name === "week" ? todayWeek() : null);
    if (type !== "week") return validation(`${name}_type=${type} is not served by the fake`);
    if (!value || !WEEK.test(value)) return validation(`${name} must be a week key`);
    return value;
  }

  function cardFor(p: Principal, de: FakeDe, week: string): Scorecard {
    return seatScorecard({
      de,
      week,
      today: todayWeek(),
      viewer: viewerOf(p),
      currency: p.company.reporting_currency,
    });
  }

  function companyCard(p: Principal, week: string): Scorecard {
    const list = desOf(p.company.id);
    return companyScorecard(
      p.company,
      list.map((de) => ({ de, card: cardFor(p, de, week) })),
      week,
    );
  }

  // Invitations, people edits, roles, magic links and sessions (fake-access.ts).
  const access = createAccessFake({
    accounts,
    companies,
    now,
    current: () => current,
    setCurrent: (email) => {
      current = email;
    },
    // The handlers receive this file's principals, so `company` is a full FakeCompany.
    generatedPeople: (p) => people(p.company as FakeCompany, desOf(p.company.id), p.operator),
    entitlementsOf: (companyId) => {
      for (const a of accounts.values()) {
        const e = a.me.entitlements[companyId];
        if (e) return e;
      }
      return { core: "active" };
    },
    metricKeys: (companyId) => {
      const company = companies.find((c) => c.id === companyId);
      return company ? metricDefs(company).map((m) => m.key) : [];
    },
    problem,
    json,
    page,
    // HARDEN-mfa: an enrolled account gets no sign-in link (fake-mfa.ts).
    twoFactorEnabled: (email) => mfa.enrolled(email),
    // ACTIONS: `roles.delete` refuses a role a seat names (Setup › Seats' or the generated ones).
    roleSeats: (companyId, roleId) => {
      const company = companies.find((c) => c.id === companyId);
      if (!company) return 0;
      const all = structureStores?.seatsOf(company) ?? seats(company, desOf(companyId));
      return all.filter((s) => s.role_id === roleId).length;
    },
  });

  // HARDEN-mfa: two-step sign-in (fake-mfa.ts). The seeded operator is enrolled and this browser
  // is its trusted device (Better Auth's 30-day trust-device cookie), so a "demo" password signs
  // it in without a code, as every existing test and spec expects.
  const mfa = createMfaFake(
    {
      now,
      password: (email, password) =>
        !access.hasPassword(email)
          ? "none"
          : accounts.get(email)?.password === password
            ? "ok"
            : "wrong",
      signedIn: (email) => {
        current = email;
        access.startSession(email);
      },
    },
    DEMO_ENROLLED.map((email) => ({
      email,
      secret: DEMO_TOTP_SECRET,
      backupCodes: DEMO_RECOVERY_CODES,
      trusted: true,
    })),
  );

  /**
   * `/v1/me`'s `mfa`, from the live enrolment and settings: operators always need two-step
   * sign-in; an owner or admin where the company's `settings.security` requires it.
   */
  function mfaOf(me: MeResponse): MeResponse["mfa"] {
    return {
      enrolled: mfa.enrolled(me.user.email),
      operator_required: me.agency !== null,
      required_company_ids: me.memberships
        .filter(
          (m) =>
            m.roles.some((r) => r === "owner" || r === "admin") &&
            companies.find((c) => c.id === m.company_id)?.settings.security
              ?.require_two_factor_for_admins === true,
        )
        .map((m) => m.company_id),
    };
  }

  /** The membership of the company's owner account (Work's inbox reader, Agents' decider). */
  function ownerOf(companyId: string): string | null {
    for (const a of accounts.values())
      for (const m of a.me.memberships)
        if (m.company_id === companyId && m.roles.includes("owner")) return m.membership_id;
    return null;
  }

  let structureStores: Parameters<NonNullable<StructureDeps["share"]>>[0] | null = null;
  // WORKFORCE: the live agents reporting to a DE (fake-workforce.ts), for the retire/remove guard.
  let workforceGuards: {
    liveAgentRowsOf: (p: Principal, membershipId: string) => { id: string; name: string }[];
    reportsToOf: (p: Principal, agentId: string) => string | null;
    strandedAfter: (p: Principal, leave: Leave) => string[];
    clearTeam: (p: Principal, teamId: string) => void;
    chartAgentsOf: (p: Principal) => OperationOutput<"orgChart.get">["agents"];
    agentOf: (
      p: Principal,
      id: string,
    ) => { name: string; reports_to_membership_id: string } | null;
  } | null = null;
  /**
   * Production's `assertNoneStranded` for a write by which a DE leaves (retire, remove, unassign,
   * the close of a seat it held): 422 `supervisor_required` naming the DEs and agents whose
   * chain would no longer end at a person. A person leaving is not checked (as production).
   */
  const strandedAfter = (p: Principal, leave: Leave) => {
    const names = workforceGuards?.strandedAfter(p, leave) ?? [];
    return names.length
      ? problem(
          422,
          "supervisor_required",
          "Supervisor required: the reporting chain must end at a person",
          `this would leave ${names.join(", ")} without a person at the top of the reporting chain; reassign them first`,
        )
      : null;
  };
  // KPI-VISIBILITY: the grants and the caller's standing every twin KPI read shares
  // (fake-kpi-visibility.ts) over the structure stores.
  const kpiVisibility = createKpiVisibility({
    json,
    problem,
    notFound,
    validation,
    now,
    newId: (n) => uid(0x5800, n),
    seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
    assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
    teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
    teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
    rolesOf: (companyId) => access.rolesOf(companyId),
    peopleOf: (p) => access.peopleOf(p),
    accountsOf: (p) =>
      [...accounts.values()].flatMap((a) =>
        a.me.memberships
          .filter((m) => m.company_id === p.company.id)
          .map((m) => ({ id: m.membership_id, display_name: a.me.user.name, roles: [...m.roles] })),
      ),
  });
  // AGENT-REPORTING: duties, KPI scopes and the review of agent values (fake-reporting.ts).
  const reporting = createReportingFake({
    json,
    problem,
    notFound,
    validation,
    now,
    nextSeq: () => ++seq,
    desOf,
    stores: () => structureStores,
    observations,
    approvalsOf: (companyId) => approvalsOf(companyId),
  });
  /** CHART-DIMENSIONS: the org twin's responsibility store (the chart's floor). */
  let responsibilitiesOf: ((c: FakeCompany) => Responsibility[]) | null = null;
  /** CHART-KB: the org twin's goals (the quarter's priorities are the Keystones lens). */
  let goalsOf: ((c: FakeCompany) => Goal[]) | null = null;
  /** Parity P5: the org twin's recurring work rows (the routine controls act on them). */
  let recurringOf: ((c: FakeCompany) => OperationOutput<"recurringWork.create">[]) | null = null;
  let catalog: FakeCatalog | null = null;
  // MODEL-POLICY: the deploys twin's template overrides (its own array, shared by reference).
  let deOverrides: readonly DeTemplateOverride[] = [];

  // MEMBER-ENTRY (fake-numbers.ts): the holder's scope and `GET me/numbers` over the structure
  // block's stores and the shared observation rows.
  const numberStores = (p: Principal): NumbersStores | null => {
    // The Acme seat holder's numbers are seeded on its first read (fake-numbers.ts).
    if (personOf(p) === JORDAN) structureStores?.seedMemberNumbers(p.company);
    return structureStores
      ? {
          seatsOf: structureStores.seatsOf,
          metricsOf: structureStores.metricsOf,
          assignmentsOf: structureStores.assignmentsOf,
          seatMetricsOf: structureStores.seatMetricsOf,
          observations,
          liveOf: liveObservationOf,
          now,
        }
      : null;
  };
  /** The holder's seats and metrics; null when it holds no seat (nothing to write). */
  const selfScopeOf = (p: Principal) => {
    const s = numberStores(p);
    const scope = s ? selfScope(s, p) : null;
    return scope && scope.size > 0 ? scope : null;
  };
  const numbersOf = (p: Principal) => {
    const s = numberStores(p);
    return s ? myNumbers(s, p) : { currency: "USD", seats: [] };
  };

  // M4-01/M4-03: personal crews. The features compose the other twins synchronously: each call
  // is another twin's own handler, read through `peekJson`, as the caller (the crew's owner).
  const heldSeats = (p: Principal, membershipId: string) => {
    const seatList = structureStores?.seatsOf(p.company) ?? [];
    return (structureStores?.assignmentsOf(p.company) ?? []).flatMap((a) => {
      if (a.membership_id !== membershipId || a.active_to !== null) return [];
      const seat = seatList.find((x) => x.id === a.seat_id && x.status !== "closed");
      return seat ? [seat] : [];
    });
  };
  /**
   * SHELL-DECISIONS (as `domain/org/de-visibility.ts`): the DEs a member works with — on their
   * seat or team, below their seat, serving them, or in a chat they are in. Null: every DE
   * (a manager or the agency operator).
   */
  const desShownTo = (p: Principal): Set<string> | null => {
    if (p.manager || p.operator) return null;
    const me = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
    if (!me) return new Set();
    const seats = structureStores?.seatsOf(p.company) ?? [];
    const live = (structureStores?.assignmentsOf(p.company) ?? []).filter(
      (a) => a.active_to === null,
    );
    const held = new Set(live.filter((a) => a.membership_id === me).map((a) => a.seat_id));
    const teams = structureStores?.teamsOf(p.company) ?? [];
    const members = structureStores?.teamMembersOf(p.company) ?? [];
    const myTeams = new Set(
      teams
        .filter(
          (t) =>
            (t.lead_seat_id && held.has(t.lead_seat_id)) ||
            (t.human_owner_seat_id && held.has(t.human_owner_seat_id)) ||
            members.some((m) => m.team_id === t.id && held.has(m.seat_id)),
        )
        .map((t) => t.id),
    );
    const near = new Set<string>(held);
    for (const m of members) if (myTeams.has(m.team_id)) near.add(m.seat_id);
    for (const t of teams)
      if (myTeams.has(t.id))
        for (const s of [t.lead_seat_id, t.human_owner_seat_id]) if (s) near.add(s);
    // Below their seats, at any depth (its own visited set: a team seat is walked too).
    const seen = new Set<string>(held);
    let frontier = [...held];
    while (frontier.length) {
      const next = seats
        .filter((s) => s.supervisor_seat_id && frontier.includes(s.supervisor_seat_id))
        .map((s) => s.id)
        .filter((id) => !seen.has(id));
      for (const id of next) {
        seen.add(id);
        near.add(id);
      }
      frontier = next;
    }
    for (const l of sharedDes.serviceLinks(p.company))
      if (
        l.target_kind === "company" ||
        (l.target_kind === "seat" && l.target_id && held.has(l.target_id)) ||
        (l.target_kind === "team" && l.target_id && myTeams.has(l.target_id))
      )
        near.add(l.seat_id);
    const out = new Set(live.filter((a) => near.has(a.seat_id)).map((a) => a.membership_id));
    for (const id of chatFake.matesOf(p.company.id, me)) out.add(id);
    return out;
  };
  /** `ctx` (the caller) addressed to another route of the company. */
  const twinCtx = (
    ctx: Ctx,
    path: string,
    params: Record<string, string>,
    body?: unknown,
  ): Ctx => ({
    ...ctx,
    url: new URL(`http://fake.local/v1/companies/${ctx.principal?.company.id}${path}`),
    params: { co: ctx.principal?.company.id ?? "", ...params },
    body,
  });
  /** A route both Work and Chat serve by id: Work's answer, else Chat's (`twinRoutes`). */
  const eitherTwin = (id: RouteId, ctx: Ctx): Response => {
    const w = workHandlers[id]?.(ctx);
    if (w && w.status !== 404) return w;
    return chatFake.handlers[id]?.(ctx) ?? notFound();
  };
  const crewFake = createCrewFake({
    now,
    personalConsent: options.personalConsentBound
      ? "bound"
      : options.recall === undefined || options.recall === "connected"
        ? "unbound"
        : "no_recall",
    seatsHeld: (p, membershipId) =>
      heldSeats(p, membershipId).map((s) => ({ title: s.title, role_id: s.role_id })),
    roleExists: (companyId, roleId) => access.rolesOf(companyId).some((r) => r.id === roleId),
    ownerSeats: (p, membershipId) => {
      const all = structureStores?.seatsOf(p.company) ?? [];
      return heldSeats(p, membershipId).map((s) => ({
        id: s.id,
        title: s.title,
        hasReports: all.some((x) => x.supervisor_seat_id === s.id && x.status !== "closed"),
      }));
    },
    teamsOfSeats: (p, seatIds) => {
      if (!seatIds.length) return [];
      const ctx: Ctx = {
        url: new URL(`http://fake.local/v1/companies/${p.company.id}/teams?limit=200`),
        params: { co: p.company.id },
        body: undefined,
        me: p.me,
        principal: p,
        headers: new Headers(),
      };
      const list = handlers["teams.list"]?.(ctx);
      const teams =
        list instanceof Response
          ? peekJson<{ data: { id: string; name: string }[] }>(list)
          : undefined;
      return (teams?.data ?? [])
        .filter((t) => {
          const one = handlers["teams.get"]?.({ ...ctx, params: { co: p.company.id, id: t.id } });
          const members =
            one instanceof Response ? peekJson<{ members: { seat_id: string }[] }>(one) : undefined;
          return (members?.members ?? []).some((m) => seatIds.includes(m.seat_id));
        })
        .map((t) => ({ id: t.id, name: t.name }));
    },
    chat: chatFake.crew,
    inbox: {
      list: (ctx) => {
        chatFake.prime(ctx.principal?.company.id ?? "");
        const out: InboxItem[] = [];
        for (const filter of ["", "snoozed", "done"])
          for (const h of [workHandlers["inbox.list"], chatFake.handlers["inbox.list"]]) {
            const res = h?.(
              twinCtx(ctx, `/inbox?limit=200${filter ? `&filter=${filter}` : ""}`, {}),
            );
            for (const i of (res && peekJson<{ data: InboxItem[] }>(res)?.data) ?? [])
              if (!out.some((x) => x.id === i.id)) out.push(i);
          }
        return out.sort((a, b) => b.created_at.localeCompare(a.created_at));
      },
      done: (ctx, id) => eitherTwin("inbox.done", twinCtx(ctx, `/inbox/${id}/done`, { id }, {})).ok,
      snooze: (ctx, id, until) =>
        eitherTwin("inbox.snooze", twinCtx(ctx, `/inbox/${id}/snooze`, { id }, { until })).ok,
    },
    tasks: {
      visible: (ctx, id) => eitherTwin("tasks.get", twinCtx(ctx, `/tasks/${id}`, { id })).ok,
      update: (ctx, id, change) => {
        if (change.status !== undefined) {
          const res = eitherTwin(
            "tasks.setStatus",
            twinCtx(ctx, `/tasks/${id}/status`, { id }, { status: change.status }),
          );
          if (!res.ok) return false;
        }
        if (change.comment !== undefined) {
          // A comment in the task's thread (the twin posts it in the owner's name).
          const task = peekJson<{ thread_id: string | null }>(
            eitherTwin("tasks.get", twinCtx(ctx, `/tasks/${id}`, { id })),
          );
          if (!task?.thread_id) return false;
          const res = eitherTwin(
            "messages.create",
            twinCtx(
              ctx,
              `/threads/${task.thread_id}/messages`,
              { id: task.thread_id },
              { client_id: crypto.randomUUID(), body_markdown: change.comment },
            ),
          );
          if (!res.ok) return false;
        }
        return true;
      },
    },
    // M7-05: hire the crew as a DE: Setup › Seats' `seats.create`, then the DE on the new seat
    // (as `digitalEmployees.create`, its supervisor the person holding the supervisor seat).
    hire: (p, h) => {
      if (!access.rolesOf(p.company.id).some((r) => r.id === h.seat.role_id))
        return validation("seat.role_id: no such role in this company");
      const list = desOf(p.company.id);
      const supSeat =
        structureStores?.seatsOf(p.company).find((x) => x.id === h.seat.supervisor_seat_id) ??
        seats(p.company, list).find((x) => x.id === h.seat.supervisor_seat_id);
      if (!supSeat || supSeat.status === "closed")
        return validation("seat.supervisor_seat_id: no such open seat in this company");
      const holder = (structureStores?.assignmentsOf(p.company) ?? []).find(
        (a) => a.seat_id === supSeat.id && a.active_to === null && a.membership_kind === "person",
      )?.membership_id;
      const sup =
        list.find((d) => d.supervisor.seat_id === supSeat.id)?.supervisor ??
        (holder
          ? {
              seat_id: supSeat.id,
              title: supSeat.title,
              membership_id: holder,
              name: p.me.memberships.some((m) => m.membership_id === holder)
                ? p.me.user.name || p.me.user.email
                : (people(p.company, list, true).find((x) => x.id === holder)?.display_name ??
                  "Supervisor"),
            }
          : null);
      if (!sup)
        return problem(
          422,
          "supervisor_required",
          "Supervisor required",
          "the seat's supervisor seat has no active person",
        );
      const ctx: Ctx = {
        url: new URL(`http://fake.local/v1/companies/${p.company.id}/seats`),
        params: { co: p.company.id },
        body: { ...h.seat },
        me: p.me,
        principal: p,
        headers: new Headers(),
      };
      const res = handlers["seats.create"]?.(ctx);
      if (!(res instanceof Response)) return notFound();
      if (!res.ok) return res;
      const seat = peekJson<{ id: string; title: string }>(res);
      const key = (Object.keys(COMPANY_IDS) as (keyof typeof COMPANY_IDS)[]).find(
        (k) => COMPANY_IDS[k] === p.company.id,
      );
      const first = list[0];
      if (!seat || !key || !first) return notFound();
      const de: FakeDe = {
        company: key,
        membership_id: uid(0x1000, 0xe00 + ++seq),
        display_name: h.display_name,
        seat_id: seat.id,
        seat_title: seat.title,
        supervisor: sup,
        founder_membership_id: first.founder_membership_id,
        template_key: h.template_key,
        instance: "",
        started_weeks_ago: 0,
        status: "onboarding",
        skill: 1,
        keys: [],
        fresh: true,
        runtime: null,
        started_on: null,
        litellm_key_alias: null,
        provisioning_error: null,
      };
      des.push(de);
      return { seat_id: seat.id, de_membership_id: de.membership_id };
    },
  });

  /** MEETINGS: a Work task as an agenda card shows it (done: its board's done status). */
  function cadenceTask(companyId: string, id: string) {
    const t = workBridge?.taskOf(companyId, id);
    if (!t) return null;
    const board = workBridge?.boardOf(companyId, t.board_id);
    return {
      id: t.id,
      number: t.number,
      title: t.title,
      status: t.status,
      done: !!board?.status_set.find((s) => s.key === t.status)?.done,
      due_at: t.due_at,
      board_id: t.board_id,
      assignee_seat_ids: [...t.assignee_seat_ids],
    };
  }

  /** M8-ASSESS: the people a round may ask (active person members; the owner flagged). */
  const assessPeople = (c: FakeCompany) => {
    const out = new Map<string, { id: string; display_name: string; owner: boolean }>();
    for (const a of accounts.values())
      for (const m of a.me.memberships)
        if (
          m.company_id === c.id &&
          m.kind === "person" &&
          m.roles.some((r) => r === "owner" || r === "admin" || r === "member")
        )
          out.set(m.membership_id, {
            id: m.membership_id,
            display_name: a.me.user.name,
            owner: m.roles.includes("owner"),
          });
    const ownerAccount = [...accounts.values()].find((a) =>
      a.me.memberships.some((m) => m.company_id === c.id && m.roles.includes("owner")),
    );
    const p = ownerAccount ? principalFor(ownerAccount.me, c.id) : null;
    if (p)
      for (const x of access.peopleOf(p))
        if (x.kind === "person" && x.status === "active" && !out.has(x.id))
          out.set(x.id, { id: x.id, display_name: x.display_name, owner: false });
    return [...out.values()];
  };

  // FOUNDER-OS: the switcher's list, the account and the portfolio views (fake-portfolio.ts) over
  // the structure, org and cadence twins' stores.
  let cadenceBridge: CadencePortfolioBridge | null = null;
  const nameOf = (c: FakeCompany, id: string) =>
    desOf(c.id).find((x) => x.membership_id === id)?.display_name ??
    people(c, desOf(c.id), true).find((x) => x.id === id)?.display_name ??
    [...accounts.values()].find((a) =>
      a.me.memberships.some((m) => m.company_id === c.id && m.membership_id === id),
    )?.me.user.name ??
    sharedDesName(id) ??
    "Member";
  // SHARED-DES: serves links, the org chart's `service_links`, the `task` grant on assign.
  const sharedDes = createSharedDesFake({
    json,
    problem,
    notFound,
    validation,
    now,
    seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
    assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
    teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
    teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
    nameOf,
  });
  // AWARENESS-MVP: the boards a DE seat uses (fake-seat-boards.ts).
  const seatBoards = createSeatBoardsFake({
    json,
    notFound,
    validation,
    forbidden: () => problem(403, "forbidden", "Forbidden"),
    seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
    boardOf: (companyId, boardId) => workBridge?.boardOf(companyId, boardId) ?? null,
  });
  const portfolioFake = createPortfolioFake({
    json,
    problem,
    notFound,
    validation,
    now,
    companies,
    seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
    assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
    goalsOf: (c) => goalsOf?.(c) ?? [],
    desOf,
    nameOf,
    cadence: () => cadenceBridge,
  });

  // BILLING-PLANS: the Plan side of Billing and Operator › Billing (fake-billing.ts), counting the
  // company's people and its DEs' agents as the caller may read them.
  const billingFake = createBillingFake({
    now,
    companies,
    desOf,
    peopleCount: (me, companyId) => {
      const p = principalFor(me, companyId);
      if (!p) return 0;
      return access
        .peopleOf(p)
        .filter((x) => x.status === "active" && !x.roles.every((r) => r === "client_viewer"))
        .length;
    },
    agentsOf: (me, companyId, membershipId) => {
      const p = principalFor(me, companyId);
      if (!p || !workforceGuards) return [];
      return workforceGuards.liveAgentRowsOf(p, membershipId);
    },
    templates: () => catalog?.all() ?? [],
  });
  // CLOSED-LOOPS: commitments between the company's people and DEs (starts empty).
  const commitmentsFake = createCommitmentsFake({
    now,
    partyOf: (companyId, membershipId) => {
      const person = peopleOf(companyId).find((x) => x.membership_id === membershipId);
      if (person) return { kind: "person", ref: null, name: person.name };
      const de = desOf(companyId).find((d) => d.membership_id === membershipId);
      return de ? { kind: "digital_employee", ref: null, name: de.display_name } : null;
    },
  });
  // B4-ITEM-TEMPLATES: a board's item templates over the Work twin's boards and tasks.
  const itemTemplatesFake = createItemTemplatesFake({
    now,
    work: () => workBridge,
    workHandlers: () => workHandlers,
  });
  // Parity P1: prefs, task layouts, watchers, field history, observations (fake-p1.ts); spread
  // last in the map below, since it wraps Work's `tasks.get` and `tasks.setStatus`.
  const p1Fake = createP1Fake({
    now,
    desOf,
    work: () => workBridge,
    previous: (id) => (ctx) => eitherTwin(id, ctx),
  });
  // B4-ITEM-TEMPLATES: a `template_id` is laid into the body before P1's required-field check and
  // Work's create (fake-item-templates.ts), and the made task marked as from the template.
  const templatedCreate: Partial<Record<RouteId, ComposedHandler>> = {
    "tasks.create": async (given) => {
      const filled = itemTemplatesFake.fill(given);
      if (filled instanceof Response) return filled;
      const res = await p1Fake["tasks.create"]?.(filled.ctx);
      return res ? filled.after(res) : notFound();
    },
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    ...billingFake.handlers,
    ...portfolioFake.handlers,
    // M8-ASSESS: assessment rounds, respondents (magic links), reports and chart proposals; an
    // application goes through the org handlers below (roles, seats, responsibilities, metrics).
    ...createAssessFake({
      json,
      problem,
      notFound,
      validation,
      now,
      companyOf: (id) => companies.find((c) => c.id === id) ?? null,
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      personSeatsOf: (c) =>
        (structureStores?.assignmentsOf(c) ?? [])
          .filter((a) => a.membership_kind === "person" && a.active_to === null)
          .map((a) => ({ membership_id: a.membership_id, seat_id: a.seat_id })),
      responsibilitiesOf: (c) => responsibilitiesOf?.(c) ?? [],
      peopleOf: assessPeople,
      rolesOf: (companyId) => access.rolesOf(companyId),
      metricsOf: (c) => structureStores?.metricsOf(c) ?? [],
      call: (id, ctx) => handlers[id]?.(ctx) ?? notFound(),
      peek: peekJson,
    }),
    // CHART-DIMENSIONS (`GET org-chart/dimensions`): the API's rule (domain/org/
    // org-chart-dimensions.ts) over the twins: the chart as the caller reads it, then the floor
    // (responsibilities on every seat) and the gated dimensions per seat where the caller's
    // relation (manager, holder, a holder above) reaches the company's audience. KPI is the
    // structure twin's seat scorecard (a refused one reads as "no data"), load the work twin's
    // open tasks, cost the finance twin's budget vs actual for the week's month.
    "orgChart.dimensions": (ctx) => {
      const { principal, url } = ctx;
      if (!principal) return notFound();
      const sync = (r: Response | Promise<Response> | undefined) =>
        r instanceof Response ? r : undefined;
      const chartRes = sync(handlers["orgChart.get"]?.(ctx)) ?? notFound();
      const chart = peekJson<{
        seats: {
          id: string;
          title: string;
          supervisor_seat_id: string | null;
          holders: { membership_id: string }[];
        }[];
      }>(chartRes);
      if (!chart) return chartRes;
      const week = url.searchParams.get("period") ?? todayWeek();
      if (!WEEK.test(week)) return validation("period must be a week key");
      const month = new Date(weekStart(week).getTime() + 3 * 86_400_000).toISOString().slice(0, 7);
      const mine = new Set(
        principal.me.memberships
          .filter((m) => m.company_id === principal.company.id)
          .map((m) => m.membership_id),
      );
      const held = new Set(
        chart.seats
          .filter((s) => s.holders.some((h) => mine.has(h.membership_id)))
          .map((s) => s.id),
      );
      const sup = new Map(chart.seats.map((s) => [s.id, s.supervisor_seat_id]));
      const above = (id: string) => {
        let cur = sup.get(id) ?? null;
        const seen = new Set<string>();
        while (cur && !seen.has(cur)) {
          if (held.has(cur)) return true;
          seen.add(cur);
          cur = sup.get(cur) ?? null;
        }
        return false;
      };
      const set = principal.company.settings.chart?.visibility ?? {};
      const visibility: OrgChartDimensions["visibility"] = {
        kpi: set.kpi ?? "reporting_line",
        load: set.load ?? "reporting_line",
        cost: set.cost ?? "managers",
        overall: set.overall ?? "reporting_line",
        keystones: set.keystones ?? "everyone",
      };
      // CHART-KB: the quarter of the focused week (the Keystones lens and the keystone item).
      const quarter = quarterOf(new Date(weekStart(week).getTime() + 3 * 86_400_000));
      const rules = completenessRulesOf(principal.company.settings.chart?.completeness);
      // CHART-LENSES: the effective steps and weights, and Overall per seat from every part
      // the seat has (the API's rule: each part to its own audience, Overall to its own).
      const lenses: OrgChartDimensions["lenses"] = {
        thresholds: lensThresholdsOf(principal.company.settings.chart?.lenses?.thresholds),
        weights: lensWeightsOf(principal.company.settings.chart?.lenses?.weights),
        default_lens: principal.company.settings.chart?.lens ?? "structure",
      };
      const reaches = (level: string, id: string) =>
        principal.manager ||
        level === "everyone" ||
        (level === "reporting_line" && (held.has(id) || above(id))) ||
        (level === "holder" && held.has(id));
      // KPI-VISIBILITY: a grant naming the caller adds its seats to the level of the dimension's
      // group (the API's `kpiVisibilityOf(...).grants_reach`).
      const standing = kpiVisibility.standingOf(principal);
      const opens = (dim: keyof typeof KPI_GROUP_OF, id: string) =>
        reaches(visibility[dim], id) || standing.grants_reach(KPI_GROUP_OF[dim], id);
      const kpi = kpiOn(principal.me, principal.company.id);
      const dimensions: OrgChartDimensions["dimensions"] = kpi
        ? ["team", "responsibilities", "kpi", "load", "cost"]
        : ["team", "responsibilities", "load", "cost"];
      const tasks = workBridge?.tasksOf(principal.company.id) ?? [];
      const nowMs = now().getTime();
      type CostDim = NonNullable<OrgChartDimensions["seats"][number]["cost"]>;
      let costNodes: Map<string, CostDim> | null = null;
      const costOf = () => {
        if (costNodes) return costNodes;
        costNodes = new Map();
        const res = sync(
          handlers["finance.budgetVsActual"]?.(
            twinCtx(ctx, `/finance/budget-vs-actual?period_type=month&period_key=${month}`, {}),
          ),
        );
        const view = res
          ? peekJson<{
              currency: string;
              nodes: {
                kind: string;
                id: string;
                actual_minor: number;
                budget_minor: number | null;
                utilization: string | null;
                restricted_hidden: boolean;
              }[];
            }>(res)
          : undefined;
        if (view)
          for (const n of view.nodes)
            if (n.kind === "seat")
              costNodes.set(n.id, {
                actual_minor: n.actual_minor,
                budget_minor: n.budget_minor,
                utilization: n.utilization,
                currency: view.currency as CostDim["currency"],
                restricted_hidden: n.restricted_hidden,
              });
        return costNodes;
      };
      const teamMembers = structureStores?.teamMembersOf(principal.company) ?? [];
      const hasReports = new Set(
        chart.seats.map((s) => s.supervisor_seat_id).filter((v): v is string => v !== null),
      );
      const assignments = structureStores?.assignmentsOf(principal.company) ?? [];
      const shown: number[] = [];
      const seats: OrgChartDimensions["seats"] = chart.seats.map((s) => {
        const out: OrgChartDimensions["seats"][number] = {
          seat_id: s.id,
          can_view_kpis: opens("kpi", s.id),
        };
        const seatRow = structureStores?.seatsOf(principal.company).find((x) => x.id === s.id);
        const rows = (responsibilitiesOf?.(principal.company) ?? [])
          .filter((r) => r.seat_id === s.id && r.status === "active")
          .sort((a, b) => Number(b.weight) - Number(a.weight) || a.name.localeCompare(b.name));
        out.responsibilities = {
          count: rows.length,
          measurable: rows.filter((r) => r.kpi_metric_ids.length > 0).length,
          top: rows.slice(0, 5).map((r) => ({
            id: r.id,
            name: r.name,
            weight: r.weight,
            measured: r.kpi_metric_ids.length > 0,
          })),
        };
        const overall = opens("overall", s.id);
        const read: { kpi?: typeof out.kpi; load?: typeof out.load; cost?: typeof out.cost } = {};
        if (kpi && (overall || opens("kpi", s.id))) {
          const res = sync(
            // The API reads the band straight from the scorecard (`seatScorecardFor`), not through
            // the seat scorecard route's viewer rule: the twin reads it as a manager would.
            handlers["seats.scorecard"]?.({
              ...twinCtx(ctx, `/seats/${s.id}/scorecard?period=${week}`, { id: s.id }),
              principal: { ...principal, manager: true },
            }),
          );
          const card = res ? peekJson<Scorecard>(res) : undefined;
          read.kpi =
            card && card.metrics.length > 0
              ? {
                  band: card.band,
                  kpi_score: card.kpi_score,
                  goal_score: card.goal_score,
                  completeness: card.completeness,
                  metrics: card.metrics.length,
                  top: [...card.metrics]
                    .sort((a, b) => b.kpi_weight - a.kpi_weight || a.name.localeCompare(b.name))
                    .slice(0, 4)
                    .map((m) => ({
                      metric_def_id: m.metric_def_id,
                      name: m.name,
                      unit: m.unit,
                      actual: m.actual,
                      target: m.target,
                      attainment: m.attainment,
                      state: m.state,
                      bands: m.bands ?? null,
                      health: m.health ?? null,
                    })),
                }
              : null;
          if (opens("kpi", s.id)) out.kpi = read.kpi;
        }
        // CHART-KB keystones: the quarter's priorities (open or achieved) with their milestones.
        const priorities = (goalsOf?.(principal.company) ?? []).filter(
          (g) =>
            g.seat_id === s.id &&
            g.kind === "priority" &&
            g.period_key === quarter &&
            (g.state === "open" || g.state === "achieved"),
        );
        const keystones: NonNullable<OrgChartDimensions["seats"][number]["keystones"]> = {
          quarter,
          count: priorities.length,
          on: 0,
          at_risk: 0,
          off: 0,
          top: [],
        };
        for (const g of priorities) {
          const track = g.state === "achieved" ? "on" : g.track;
          if (track === "on") keystones.on += 1;
          else if (track === "at_risk") keystones.at_risk += 1;
          else keystones.off += 1;
          if (keystones.top.length < 3)
            keystones.top.push({
              id: g.id,
              title: g.title,
              track,
              milestones_done: g.milestones.filter((m) => m.done_at !== null).length,
              milestones_total: g.milestones.length,
            });
        }
        if (opens("keystones", s.id)) out.keystones = keystones;
        // CHART-KB completeness: the whole seat's facts, shown to the holder, the line above
        // and managers; a missing item in a gated dimension is named to that audience only.
        const facts: SeatFacts = {
          held: s.holders.length > 0,
          status: seatRow?.status ?? "filled",
          digital: assignments.some(
            (a) =>
              a.seat_id === s.id &&
              a.active_to === null &&
              a.membership_kind === "digital_employee",
          ),
          supervisor: s.supervisor_seat_id !== null,
          reports: hasReports.has(s.id),
          teams: teamMembers.filter((m) => m.seat_id === s.id).length,
          responsibilities: out.responsibilities.count,
          measurables: kpi ? (structureStores?.measurablesOf(principal.company, s.id) ?? 0) : null,
          finance: (() => {
            const c = costOf().get(s.id);
            return Boolean(c && (c.budget_minor !== null || c.actual_minor > 0));
          })(),
          keystones: keystones.count,
        };
        const completeness = seatCompleteness(facts, rules);
        if (reaches("reporting_line", s.id)) {
          out.completeness = cutCompleteness(completeness, (gate) => opens(gate, s.id));
          // W9-FIXES: the summary counts the seats the chart draws, never a closed one.
          if ((seatRow?.status ?? "filled") !== "closed") shown.push(completeness.score);
        }
        if (overall || opens("load", s.id)) {
          const open = tasks.filter(
            (t) => t.completed_at === null && t.assignee_seat_ids.includes(s.id),
          );
          read.load = {
            open: open.length,
            overdue: open.filter((t) => t.due_at !== null && Date.parse(t.due_at) < nowMs).length,
            estimate_minutes: open.reduce((a, t) => a + (t.estimate_minutes ?? 0), 0),
          };
          if (opens("load", s.id)) out.load = read.load;
        }
        if (overall || opens("cost", s.id)) {
          read.cost = costOf().get(s.id) ?? null;
          // DE-BUDGET-PACE: the seat's DE against its own limit (the DE budget route's pace).
          const deOn = assignments.find(
            (a) =>
              a.seat_id === s.id &&
              a.active_to === null &&
              a.membership_kind === "digital_employee",
          );
          if (read.cost && deOn) {
            const res = sync(
              handlers["digitalEmployees.budget"]?.(
                twinCtx(ctx, `/digital-employees/${deOn.membership_id}/budget`, {
                  id: deOn.membership_id,
                }),
              ),
            );
            const pace =
              res?.ok === true
                ? peekJson<{
                    pace: {
                      status: "on_track" | "ahead_of_pace" | "reserve_reached" | "no_budget";
                      spent_minor: number;
                      limit_minor: number | null;
                      projected_minor: number | null;
                      runout_at: string | null;
                    };
                  }>(res)?.pace
                : undefined;
            if (pace)
              read.cost = {
                ...read.cost,
                de_budget: {
                  membership_id: deOn.membership_id,
                  status: pace.status,
                  spent_minor: pace.spent_minor,
                  limit_minor: pace.limit_minor,
                  projected_minor: pace.projected_minor,
                  runout_at: pace.runout_at,
                },
              };
          }
          if (opens("cost", s.id)) out.cost = read.cost;
        }
        if (overall) {
          const parts: Partial<Record<ScoredLens, LensScore>> = {
            responsibilities: responsibilitiesScore(
              out.responsibilities,
              s.holders.length > 0,
              lenses.thresholds.responsibilities,
            ),
            tasks: tasksScore(read.load, lenses.thresholds.tasks),
            finance: financeScore(read.cost, lenses.thresholds.finance),
            keystones: keystonesScore(keystones, lenses.thresholds.keystones),
          };
          if (kpi) {
            parts.performance = performanceScore(read.kpi);
            parts.execution = executionScore(read.kpi, lenses.thresholds.execution);
          }
          out.overall = overallScore(parts, lenses.weights, lenses.thresholds);
        }
        return out;
      });
      const default_dimensions = (
        principal.company.settings.chart?.dimensions ?? ["team", "responsibilities"]
      ).filter((d) => dimensions.includes(d));
      return json({
        week,
        month,
        dimensions,
        default_dimensions,
        visibility,
        lenses,
        kpi_visible_seat_ids: chart.seats.filter((s) => opens("kpi", s.id)).map((s) => s.id),
        seats,
        completeness: {
          quarter,
          required: rules.required,
          de_exempt_finance: rules.de_exempt_finance,
          summary: completenessSummary(shown),
        },
      } satisfies OrgChartDimensions);
    },
    // KPIS-MODE: the seats whose KPI data the caller may see — the API's `kpiVisibleSeatIds`
    // (managers every seat; else `settings.chart.visibility.kpi`, default the reporting line).
    "orgChart.kpiVisibility": (ctx) => {
      const { principal } = ctx;
      if (!principal) return notFound();
      const sync = (r: Response | Promise<Response> | undefined) =>
        r instanceof Response ? r : undefined;
      const chartRes = sync(handlers["orgChart.get"]?.(ctx)) ?? notFound();
      const chart = peekJson<{
        seats: {
          id: string;
          supervisor_seat_id: string | null;
          holders: { membership_id: string }[];
        }[];
      }>(chartRes);
      if (!chart) return chartRes;
      const mine = new Set(
        principal.me.memberships
          .filter((m) => m.company_id === principal.company.id)
          .map((m) => m.membership_id),
      );
      const held = new Set(
        chart.seats
          .filter((s) => s.holders.some((h) => mine.has(h.membership_id)))
          .map((s) => s.id),
      );
      const sup = new Map(chart.seats.map((s) => [s.id, s.supervisor_seat_id]));
      const above = (id: string) => {
        let cur = sup.get(id) ?? null;
        const seen = new Set<string>();
        while (cur && !seen.has(cur)) {
          if (held.has(cur)) return true;
          seen.add(cur);
          cur = sup.get(cur) ?? null;
        }
        return false;
      };
      const level = principal.company.settings.chart?.visibility?.kpi ?? "reporting_line";
      // KPI-VISIBILITY: a grant naming the caller for KPIs widens the level (never narrows it).
      const standing = kpiVisibility.standingOf(principal);
      const visible = (id: string) =>
        principal.manager ||
        level === "everyone" ||
        (level === "reporting_line" && (held.has(id) || above(id))) ||
        (level === "holder" && held.has(id)) ||
        standing.grants_reach("kpi", id);
      return json({
        kpi_visible_seat_ids: chart.seats.filter((s) => visible(s.id)).map((s) => s.id),
      } satisfies OrgChartKpiVisibility);
    },
    "operator.companies.list": ({ me, url }) => {
      if (!me.agency) return problem(403, "forbidden", "Forbidden");
      const week = url.searchParams.get("week") ?? todayWeek();
      if (!WEEK.test(week)) return validation("week must be a week key");
      const data = me.agency.grants.flatMap((g) => {
        const p = principalFor(me, g.company_id);
        if (!p) return [];
        const kpi = kpiOn(me, p.company.id);
        const card = structureStores?.companyRollup(p, week) ?? companyCard(p, week);
        const list = desOf(p.company.id);
        const latest = list
          .flatMap((de) => timeline(de, todayWeek()).slice(0, 1))
          .map((t) => t.occurred_at)
          .sort()
          .at(-1);
        const report = reports(p.company, todayWeek()).find((r) => r.week_key === week);
        return [
          {
            company: p.company,
            de_count: list.length,
            band: kpi && list.length ? card.band : null,
            kpi_score: kpi ? card.kpi_score : null,
            cost_minor: card.cost?.total_minor ?? 0,
            supervisor_minutes: card.hours?.supervision_minutes ?? 0,
            founder_minutes: card.hours?.founder_minutes ?? 0,
            report_state: kpi ? (report?.state ?? null) : null,
            last_ingest_at: latest ?? null,
          },
        ];
      });
      return json({ data });
    },
    "operator.consumers.list": ({ me }) => {
      if (!me.agency) return problem(403, "forbidden", "Forbidden");
      // Failed records of the granted companies only, as the API (retried or skipped ones leave).
      const granted = new Set(me.agency.grants.map((g) => g.company_id));
      return json({
        data: consumerRecords.filter((r) => r.state === "failed" && granted.has(r.company_id)),
      });
    },
    "operator.companies.create": ({ me, body }) => {
      if (!me.agency) return problem(403, "forbidden", "Forbidden");
      const b = body as {
        name?: string;
        slug?: string;
        reporting_currency?: string;
        calendar_config?: FakeCompany["calendar_config"];
        owner?: { email?: string; display_name?: string };
      };
      if (!b?.name?.trim() || !b.slug || !/^[a-z0-9][a-z0-9-]{1,62}$/.test(b.slug))
        return validation("name and a lowercase slug are required");
      // The owner is optional (an operator-managed company); when given, it must be complete.
      const owner = b.owner;
      if (owner && (!owner.email?.trim() || !owner.display_name?.trim()))
        return validation("owner needs an email and a display name");
      if (companies.some((c) => c.slug === b.slug))
        return problem(409, "conflict", "Conflict", `The slug "${b.slug}" is taken.`);
      const at = now().toISOString();
      const company: FakeCompany = {
        id: uid(0x0c00, companies.length + 1),
        slug: b.slug,
        name: b.name.trim(),
        kind: "operating",
        settings: {},
        calendar_config: b.calendar_config ?? {
          timezone: "America/New_York",
          weekStartDay: 1,
          fiscalYearStartMonth: 1,
        },
        reporting_currency: (b.reporting_currency ?? "USD") as FakeCompany["reporting_currency"],
        // MANAGED-POLICY: a client the agency creates starts managed (HTVA's own stays self-serve).
        agent_mode: b.slug === "htva" ? "self_serve" : "managed",
        created_at: at,
        updated_at: at,
      };
      companies.push(company);
      // The operator's grant and the default entitlements appear on the next `/v1/me`.
      for (const account of accounts.values()) {
        const a = account.me.agency;
        if (!a || a.agency_id !== me.agency.agency_id) continue;
        a.grants.push({
          company_id: company.id,
          company_slug: company.slug,
          company_name: company.name,
          company_calendar: company.calendar_config,
          level: "operator",
        });
        account.me.entitlements[company.id] = { core: "active", kpi: "active" };
      }
      // No owner: an operator-managed company, no invitation (as the real flow).
      if (!owner?.email || !owner.display_name)
        return json({ company, owner_invitation: null }, 201);
      // The client's owner gets an invitation, as the real flow (accepted with a magic link).
      access.invite(
        company,
        { email: owner.email, display_name: owner.display_name, roles: ["owner"] },
        "agency_user",
      );
      return json(
        {
          company,
          // The contract's shape: the owner is invited (a pending membership until accepted).
          owner_invitation: {
            company_id: company.id,
            id: uid(0x1c00, companies.length),
            email: owner.email.trim().toLowerCase(),
            display_name: owner.display_name,
            roles: ["owner"],
            status: "pending",
            version: 1,
            sent_at: at,
            expires_at: new Date(now().getTime() + 7 * 86_400_000).toISOString(),
            membership_id: uid(0x1d00, companies.length),
            accepted_at: null,
            actor_kind: "agency_user",
            actor_membership_id: null,
            created_at: at,
            updated_at: at,
          },
        },
        201,
      );
    },
    "companies.get": ({ principal }) =>
      principal
        ? json({
            ...principal.company,
            wallet_view: walletFake.walletViewOf(principal.company.id, principal.operator),
          })
        : notFound(),
    "digitalEmployees.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const v = viewerOf(principal);
      const shown = desShownTo(principal);
      return json(
        page(
          desOf(principal.company.id)
            // SHELL-DECISIONS: a member is listed only the DEs they work with.
            .filter((d) => shown === null || shown.has(d.membership_id))
            // AGENT-MENTION: removed DEs leave the roster unless asked for.
            .filter((d) => !d.removed_at || url.searchParams.get("include_removed") === "true")
            .map((d) => ({
              ...deSummary(d, todayWeek(), v),
              can_intervene: canIntervene(principal, d),
            })),
          url,
        ),
      );
    },
    "digitalEmployees.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      const week = todayWeek();
      const card = kpiOn(principal.me, principal.company.id) ? cardFor(principal, de, week) : null;
      return json({
        can_intervene: canIntervene(principal, de),
        ...deProfile(
          de,
          week,
          { ...viewerOf(principal), manager: principal.manager },
          card && {
            period_type: "week",
            period_key: week,
            kpi_score: card.kpi_score,
            completeness: card.completeness,
            band: card.band,
          },
        ),
        activity: activityOf(principal.company.id, de),
        how_i_work: howIWorkOf(principal, de),
      });
    },
    // CHART-HOWIWORK (the API's rule): an owner or admin, or a person supervising the DE's seat
    // at any depth, saves a new version; `base_version` no longer current is 409.
    "digitalEmployees.putHowIWork": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      if (!canEditHowIWork(principal, de)) return problem(403, "forbidden", "Forbidden");
      const b = body as { text?: unknown; base_version?: unknown };
      if (b.text !== null && typeof b.text !== "string")
        return validation("text: a string or null");
      const text = typeof b.text === "string" && b.text.trim() ? b.text.trim() : null;
      if (text && text.length > 20000) return validation("text: at most 20000 characters");
      const list = howIWorkStore.get(de.membership_id) ?? [];
      const current = list.at(-1);
      const at = current?.version ?? 0;
      if (b.base_version !== undefined && b.base_version !== at)
        return problem(409, "conflict", `"How I work" changed since you opened it`);
      if ((current?.text ?? null) !== text) {
        const me = principal.me.memberships.find((m) => m.company_id === principal.company.id);
        list.push({
          version: at + 1,
          text,
          created_at: new Date().toISOString(),
          author: {
            kind: me ? "person" : "agency_user",
            membership_id: me?.membership_id ?? null,
            name: me ? (principal.me.user.name ?? null) : null,
          },
        });
        howIWorkStore.set(de.membership_id, list);
      }
      return json(howIWorkOf(principal, de));
    },
    // TASK-BUDGETS (the API's rule): managers read and set a DE's override; the shipped DE
    // defaults (60 steps, $1, 30 minutes), enforcement off as shipped.
    "digitalEmployees.taskLimits": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de || !principal.manager) return notFound();
      return json(taskLimitsOf(de.membership_id));
    },
    "digitalEmployees.putTaskLimits": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de || !principal.manager) return notFound();
      const o = (body as { override?: Record<string, unknown> | null }).override ?? null;
      const pos = (v: unknown) =>
        typeof v === "number" && Number.isInteger(v) && v > 0 ? v : null;
      const next = {
        ...(pos(o?.steps) ? { steps: pos(o?.steps) as number } : {}),
        ...(pos(o?.cost_micros) ? { cost_micros: pos(o?.cost_micros) as number } : {}),
        ...(pos(o?.time_ms) ? { time_ms: pos(o?.time_ms) as number } : {}),
      };
      taskLimitStore.set(de.membership_id, Object.keys(next).length > 0 ? next : null);
      return json(taskLimitsOf(de.membership_id));
    },
    "digitalEmployees.howIWorkVersions": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      // The API's paging: newest first, `cursor` = versions older than this one.
      const limit = Math.min(Math.max(Number(url.searchParams.get("limit") ?? 50) || 50, 1), 100);
      const cursor = url.searchParams.get("cursor");
      const all = [...(howIWorkStore.get(de.membership_id) ?? [])]
        .reverse()
        .filter((v) => !cursor || v.version < Number(cursor));
      const data = all.slice(0, limit);
      return json({
        data,
        next_cursor: all.length > limit ? String(data.at(-1)?.version) : null,
      });
    },
    "digitalEmployees.scorecard": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      const week = weekParam(url, "period");
      return typeof week === "string" ? json(cardFor(principal, de, week)) : week;
    },
    // KPIS-MODE: a week, a month or a quarter (fake-scorecard-grain composes the longer grains).
    "seats.scorecard": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = desOf(principal.company.id).find((d) => d.seat_id === params.id);
      if (!de) return notFound();
      const period = periodParam(url, todayWeek);
      if ("error" in period) return validation(period.error);
      return json(cardForPeriod(period, (w) => cardFor(principal, de, w)));
    },
    // Overridden by the structure block (`companyRollup`: the person-seat fallback).
    "scorecards.company": ({ principal, url }) => {
      if (!principal) return notFound();
      const period = periodParam(url, todayWeek);
      if ("error" in period) return validation(period.error);
      return json(cardForPeriod(period, (w) => companyCard(principal, w)));
    },
    "digitalEmployees.timeline": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      const after = url.searchParams.get("after");
      const items = (de.fresh ? [] : timeline(de, todayWeek())).filter(
        (t) => !after || t.occurred_at > after,
      );
      const { data, next_cursor } = page(items, url);
      return json({ data, next_cursor });
    },
    "seats.cost": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = desOf(principal.company.id).find((d) => d.seat_id === params.id);
      // A seat nobody digital holds has a ledger too (the API serves every seat); here it is
      // empty, since the twin's cost events are its DEs' (Chart UX pass 2026-10-21).
      const seat = de
        ? null
        : structureStores?.seatsOf(principal.company).find((s) => s.id === params.id);
      if (!de && !seat) return notFound();
      const type = url.searchParams.get("period_type") ?? "month";
      const period = url.searchParams.get("period") ?? "";
      if (type === "month" ? !MONTH.test(period) : type !== "week" || !WEEK.test(period))
        return validation("period must match period_type (month or week)");
      const rows = (!de || de.fresh ? [] : costLedger(de, todayWeek())).filter(
        (e) =>
          (type === "month" ? e.period_key === period : weekOf(e.occurred_at) === period) &&
          (e.visibility === "client" || principal.operator),
      );
      const byKind: Record<string, number> = {};
      for (const e of rows) byKind[e.kind] = (byKind[e.kind] ?? 0) + e.amount_minor;
      // Client viewers read client-visibility totals only (13 §Principals: "(totals)").
      const clientViewerOnly = !principal.manager;
      return json({
        ledger: clientViewerOnly ? [] : rows,
        rollup: {
          by_kind: byKind,
          total_minor: rows.reduce((s, e) => s + e.amount_minor, 0),
          currency: principal.company.reporting_currency,
        },
      });
    },
    // T2-BOARD-WAKE: the twin's boards have no DE owner, so no DE has board wakes (honest empty).
    "seats.board_wakes": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = desOf(principal.company.id).find((d) => d.seat_id === params.id);
      return json({ membership_id: de?.membership_id ?? null, days: 7, data: [] });
    },
    "timeEntries.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const seat = url.searchParams.get("seat");
      const period = url.searchParams.get("period") ?? todayWeek();
      const kind = url.searchParams.get("kind");
      if (!WEEK.test(period)) return validation("period must be a week key in the fake");
      const rows = desOf(principal.company.id)
        .filter((d) => !seat || d.seat_id === seat)
        .flatMap((d) => entriesOf(d, period))
        .filter(
          (t) => (t.visibility === "client" || principal.operator) && (!kind || t.kind === kind),
        )
        .sort((a, b) => b.work_date.localeCompare(a.work_date));
      return json(page(rows, url));
    },
    "digitalEmployees.createKey": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = body as {
        scopes?: AgentKeyScope[];
        expires_at?: string | null;
        backfill?: boolean;
      };
      const scopes = (b?.scopes ?? []).filter((s) => ALL_SCOPES.includes(s));
      if (!scopes.length) return validation("scopes: at least one scope");
      if (b.backfill && !principal.operator) return problem(403, "forbidden", "Forbidden");
      const secret = Array.from(crypto.getRandomValues(new Uint8Array(24)), (x) =>
        x.toString(16).padStart(2, "0"),
      ).join("");
      const plaintext = `cosk_in_${secret}`;
      const at = now().toISOString();
      const key: AgentKey = {
        company_id: principal.company.id,
        id: uid(0x4100, ++keySeq),
        kind: "ingest",
        membership_id: de.membership_id,
        seat_id: de.seat_id,
        key_prefix: plaintext.slice(0, 12),
        scopes,
        backfill: b.backfill ?? false,
        meter_namespaces: [],
        source: null,
        status: "active",
        last_used_at: null,
        expires_at: b.expires_at ?? null,
        revoked_at: null,
        actor_kind: principal.operator ? "agency_user" : "person",
        actor_membership_id: null,
        created_at: at,
        updated_at: at,
      };
      de.keys.push(key);
      return json(
        {
          key: {
            id: key.id,
            prefix: key.key_prefix,
            scopes: key.scopes,
            backfill: key.backfill,
            meter_namespaces: key.meter_namespaces,
          },
          plaintext,
        },
        201,
      );
    },
    // CONNECTIONS-AREA: every DE's keys with its holder (Team › Connections › Agent keys).
    "agentKeys.list": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const rows = desOf(principal.company.id)
        .flatMap((d) =>
          d.keys.map((k) => ({
            ...k,
            holder: {
              membership_id: d.membership_id,
              kind: "digital_employee" as const,
              display_name: d.display_name,
            },
          })),
        )
        .sort(
          (a, b) =>
            Number(b.status === "active") - Number(a.status === "active") ||
            a.holder.display_name.localeCompare(b.holder.display_name) ||
            a.created_at.localeCompare(b.created_at),
        );
      return json({ data: rows });
    },
    "digitalEmployees.revokeKey": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const key = de.keys.find((k) => k.id === params.kid);
      if (!key) return notFound();
      if (key.status !== "revoked") {
        const at = now().toISOString();
        key.status = "revoked";
        key.revoked_at = at;
        key.updated_at = at;
      }
      return json(key);
    },
    // M7-05: the DE's Recall agent (routes/recall.ts). The twin plays a deployment with Recall
    // wired: a DE holding an active CompanyOS agent key holds its `rk_` (sealed, never shown),
    // one without waits; its grants are its seat's and the company's memory.
    "digitalEmployees.recallAgent": ({ principal, params }) => {
      if (!principal) return notFound();
      const memory = principal.me.entitlements[principal.company.id]?.memory ?? "disabled";
      if (memory !== "active" && memory !== "trial" && memory !== "grace")
        return featureDisabled("memory");
      const de = deIn(principal, params.id);
      if (!de) return notFound();
      const key = de.keys
        .filter((k) => k.status === "active")
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      const retired = de.status === "retired";
      return json({
        membership_id: de.membership_id,
        agent_kind: "digital_employee",
        state: retired ? "retired" : key ? "held" : "waiting",
        recall_agent_id:
          key || retired ? `ag_${de.membership_id.replaceAll("-", "").slice(0, 16)}` : null,
        personal_access: "none",
        grants: key ? [`seat:${de.seat_id}`, `company:${principal.company.id}`] : [],
        key_for_prefix: key?.key_prefix ?? null,
        updated_at: key?.created_at ?? null,
        copy: crewFake.copyOf(principal.company.id, de.membership_id),
        // D14 preview: the hot copy's status, only while the company's setting is on (a held
        // agent's copy was read when its key was made; a waiting one has none yet).
        ...(principal.company.settings.memory?.hot_copy?.enabled === true
          ? {
              hot_copy: key
                ? {
                    size_bytes: 153_600,
                    fetched_at: key.created_at,
                    source_spaces: ["Company", "Seat"],
                    trimmed: [],
                  }
                : null,
            }
          : {}),
      });
    },
    "reports.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const week = url.searchParams.get("week");
      const rows = reportsOf(principal.company)
        .filter((r) => (principal.manager || r.state === "sent") && (!week || r.week_key === week))
        .sort((a, b) => b.week_key.localeCompare(a.week_key) || b.version - a.version)
        .map((r) => ({ ...r, report_stale: staleOf(principal.company, r) }));
      return json(page(rows, url));
    },
    ...s117Handlers(),
    // Structure: seats, metrics, targets, observation tombstones and imports, publications. Roles
    // are access's (one store: Setup › Roles edits what the seat drawer offers).
    ...structureHandlers({
      kpiStanding: (p) => kpiVisibility.standingOf(p),
      roles: (companyId) => access.rolesOf(companyId),
      peopleOf: (p) => access.peopleOf(p),
      base: s117Handlers(),
      json,
      problem,
      notFound,
      validation,
      page,
      now,
      nextSeq: () => ++seq,
      desOf,
      observations,
      batches,
      touch,
      todayWeek,
      weekParam,
      seatCard: cardFor,
      companyCard,
      metricExtras: (p, seatId, def) => reporting.metricExtras(p, seatId, def),
      agentDuty: reporting.agentDuty,
      share: (stores) => {
        structureStores = stores;
      },
      // WORKFORCE: a DE leaving a seat (unassign, close) re-checks the reporting chains, and a
      // deleted team leaves its agents with no team.
      stranded: (p, leave) => strandedAfter(p, leave),
      onTeamDeleted: (p, teamId) => workforceGuards?.clearTeam(p, teamId),
      // CHART-AGENTS: the org chart's agents (AG) under their DE or person holder.
      chartAgentsOf: (p) => workforceGuards?.chartAgentsOf(p) ?? [],
      seedOf: (c) => portfolioFake.structureOf(c),
      serviceLinksOf: (c) => sharedDes.serviceLinks(c),
      desShownTo: (p) => desShownTo(p),
      // DE-WIZARD: a home channel is one of the company's chat channels (no DM, not archived).
      channelOk: (p, id) => {
        const res = chatFake.handlers["channels.list"]?.({
          url: new URL(`http://fake.local/v1/companies/${p.company.id}/channels`),
          params: { co: p.company.id },
          body: undefined,
          me: p.me,
          principal: p,
          headers: new Headers(),
        });
        const rows =
          res instanceof Response
            ? peekJson<{ data: { id: string; dm_key?: string | null }[] }>(res)?.data
            : undefined;
        return (rows ?? []).some((c) => c.id === id && !c.dm_key);
      },
    }),
    ...reporting.handlers,
    // Responsibilities, recurring work and templates (M1-05, M1-06) over the seat, metric and
    // role stores above.
    ...orgHandlers({
      json,
      problem,
      notFound,
      validation,
      page,
      now,
      nextSeq: () => ++seq,
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      metricsOf: (c) => structureStores?.metricsOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      rolesOf: (companyId) => access.rolesOf(companyId),
      kpiStanding: (p) => kpiVisibility.standingOf(p),
      templateHandlers: () => workTemplateHandlers,
      share: (stores) => {
        responsibilitiesOf = stores.responsibilitiesOf;
        goalsOf = stores.goalsOf;
        recurringOf = stores.recurringOf ?? null;
      },
      seedGoalsOf: (c, quarter) => portfolioFake.goalsSeedOf(c, quarter),
    }),
    // CALENDAR-C1: calendars, events, follows, shares, the tasks overlay and the ICS feed
    // (fake-calendar.ts) over the teams and seats above and the Work twin's tasks.
    ...createCalendarFake({
      now,
      teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
      teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      nameOf,
      memberKindOf: (c, id) =>
        desOf(c.id).some((x) => x.membership_id === id) ? "digital_employee" : "person",
      seedPeople: (companyId) => ({
        owner: ownerOf(companyId),
        member:
          accounts
            .get("member@acme.example")
            ?.me.memberships.find((m) => m.company_id === companyId)?.membership_id ?? null,
      }),
      work: (companyId) => {
        const state = workBridge?.stateOf(companyId);
        return state
          ? {
              tasks: state.tasks,
              boards: state.boards,
              views: state.views,
              spaces: state.spaces,
            }
          : null;
      },
      starredBoardsOf: (p, membershipId) => starredBoardsOf?.(p, membershipId) ?? [],
      // CAL-WORK-LAYERS: the Milestones layer reads the org twin's goals and the seats' titles.
      goalsOf: (c) => goalsOf?.(c) ?? [],
      seatTitleOf: (c, seatId) =>
        (structureStores?.seatsOf(c) ?? []).find((x) => x.id === seatId)?.title ?? null,
    }),
    // MEETINGS: issues, the weekly team meeting and the vision page (fake-cadence.ts) over the
    // seats, teams and goals above, the DEs' scorecards, the chat twin (summaries) and the Work
    // twin (to-dos are tasks).
    ...createCadenceFake({
      json,
      problem,
      notFound,
      validation,
      now,
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
      teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
      goalsOf: (c) => goalsOf?.(c) ?? [],
      nameOf,
      share: (bridge) => {
        cadenceBridge = bridge;
      },
      agentsOf: (p) =>
        desOf(p.company.id).map((de) => ({
          membership_id: de.membership_id,
          name: de.display_name,
          seat_id: de.seat_id,
          live: de.status === "active" && agentsOn(p),
        })),
      seatMetrics: (p, seatId, week) => {
        const de = desOf(p.company.id).find((x) => x.seat_id === seatId);
        return de && !de.fresh ? cardFor(p, de, week).metrics : [];
      },
      chat: () => chatFake.meetings,
      tasks: {
        create: (p, input) => {
          const bridge = workBridge;
          if (!bridge) return null;
          const co = p.company.id;
          const board =
            (input.board_id ? bridge.boardOf(co, input.board_id) : null) ??
            (bridge.spacesOf(co) ?? [])
              .flatMap((s) => bridge.sidebarOf(co, s.id)?.boards ?? [])
              .find((b) => !b.archived_at) ??
            null;
          if (!board) return null;
          const res = workHandlers["tasks.create"]?.({
            url: new URL(`http://fake.local/v1/companies/${co}/tasks`),
            params: { co },
            body: {
              board_id: board.id,
              title: input.title,
              due_at: input.due_at,
              ...(input.assignee_seat_id ? { assignee_seat_ids: [input.assignee_seat_id] } : {}),
            },
            me: p.me,
            principal: { ...p, roles: ["owner"], manager: true },
            headers: new Headers(),
          });
          const made = res ? peekJson<{ id: string }>(res) : undefined;
          const stored = made ? bridge.taskOf(co, made.id) : null;
          if (!stored) return null;
          stored.origin = input.origin;
          stored.origin_ref =
            input.origin === "meeting"
              ? { meeting_id: input.origin_ref }
              : { issue_id: input.origin_ref };
          return cadenceTask(co, stored.id);
        },
        get: (p, id) => cadenceTask(p.company.id, id),
      },
    }),
    // After structure, so access's roles.list (with Setup › Roles' edits) is the one served.
    ...(access.handlers as Partial<Record<RouteId, Handler>>),
    ...desHandlers(),
    ...opsHandlers(),
    // M1-07: outbound webhooks (fake-webhooks.ts).
    ...createWebhookHandlers({
      now,
      eventTypes: () => ALL_EVENT_TYPES,
      // WOS-BUNDLE: a hook scoped to a space or board names one of the company's.
      containerOf: (companyId, kind, id) => {
        const s = workBridge?.stateOf(companyId);
        const row = (kind === "space" ? s?.spaces : s?.boards)?.find((x) => x.id === id);
        return row ? row.name : null;
      },
    }),
    // WEBHOOKS-IN: incoming webhooks (fake-inbound-webhooks.ts); a delivery's task for the DE.
    ...createInboundWebhookHandlers({
      now,
      deOf: (co, m) => {
        const de = desOf(co).find((x) => x.membership_id === m && !x.removed_at);
        return de ? { seat_id: de.seat_id, name: de.display_name } : null;
      },
      agentOf: (p, id) => workforceGuards?.agentOf(p, id) ?? null,
      boardName: (co, b) => workBridge?.stateOf(co)?.boards.find((x) => x.id === b)?.name ?? null,
      // Production's boardFor: the hook's board if live (it and its space not archived), else
      // the first live board outside personal spaces, team spaces first, oldest first; and the
      // status a task wakes in (the first open one that is not the backlog).
      liveBoard: (co, boardId) => {
        const w = workBridge?.stateOf(co);
        if (!w) return null;
        const liveOf = (b: (typeof w.boards)[number]) => {
          const sp = w.spaces.find((x) => x.id === b.space_id);
          return sp && !b.archived_at && !sp.archived_at ? sp : null;
        };
        const pick = (b: (typeof w.boards)[number]) => ({
          id: b.id,
          waking_status: b.status_set.find((s) => !s.done && s.key !== "backlog")?.key,
        });
        const named = boardId ? w.boards.find((b) => b.id === boardId) : undefined;
        if (named && liveOf(named)) return pick(named);
        const rank = (b: (typeof w.boards)[number]) => (liveOf(b)?.kind === "team" ? 0 : 1);
        const first = w.boards
          .filter((b) => {
            const sp = liveOf(b);
            return sp && sp.kind !== "personal";
          })
          .sort((a, b) => rank(a) - rank(b) || a.created_at.localeCompare(b.created_at))[0];
        return first ? pick(first) : null;
      },
      createTask: (p, { origin_ref, ...body }) => {
        const res = workHandlers["tasks.create"]?.({
          url: new URL("http://fake/"),
          params: { co: p.company.id },
          body,
          me: p.me,
          principal: p,
          headers: new Headers(),
        });
        const made = res ? peekJson<{ id?: string; task?: { id: string } }>(res) : undefined;
        const taskId = made?.task?.id ?? made?.id ?? null;
        // A webhook's task says where it came from (the work twin's creator sets the rest).
        const row = taskId ? workBridge?.taskOf(p.company.id, taskId) : null;
        if (row) {
          row.origin = "webhook";
          row.origin_ref = origin_ref;
        }
        if (taskId && body.body_markdown !== undefined)
          pagesFake.setTaskBody(p, taskId, { markdown: body.body_markdown });
        return taskId;
      },
    }),
    // B2-GATES: per person × board × transition gates and the admin ceiling (fake-gates.ts).
    ...createGateHandlers({
      boardsOf: (co) => workBridge?.stateOf(co)?.boards ?? [],
      mySeatIds: (p) => {
        const m = p.me.memberships.find((x) => x.company_id === p.company.id)?.membership_id;
        return [
          ...new Set(
            desOf(p.company.id)
              .filter((x) => x.supervisor.membership_id === m)
              .map((x) => x.supervisor.seat_id),
          ),
        ];
      },
      seatOf: (co, seatId) => {
        const s = desOf(co).find((x) => x.supervisor.seat_id === seatId)?.supervisor;
        return s ? { title: s.title, holder: s.name } : null;
      },
    }),
    // BOARD-RESULTS: flag off in the demo (fake-post-results.ts).
    ...createPostResultsHandlers(),
    // T8-BOARD-NUMBERS: a board's Numbers tab (fake-board-numbers.ts).
    ...createBoardNumberHandlers({
      now,
      boardsOf: (co) => workBridge?.stateOf(co)?.boards ?? [],
      tasksOf: (co) => workBridge?.stateOf(co)?.tasks ?? [],
      fieldsOf: (co) => workBridge?.stateOf(co)?.fields ?? [],
    }),
    // Parity P5: automations and recurring work controls (fake-process.ts).
    ...createProcessHandlers({
      now,
      boardsOf: (co) => workBridge?.stateOf(co)?.boards ?? [],
      seatsOf: (co) => {
        const c = companies.find((x) => x.id === co);
        return c ? (structureStores?.seatsOf(c) ?? []) : [];
      },
      responsibilitiesOf: (co) => {
        const c = companies.find((x) => x.id === co);
        return c && responsibilitiesOf ? responsibilitiesOf(c) : [];
      },
      recurringOf: (co) => {
        const c = companies.find((x) => x.id === co);
        return c && recurringOf ? recurringOf(c) : [];
      },
      taskOf: (co, id) => workBridge?.taskOf(co, id) ?? null,
      nameOf: (co, m) =>
        [...accounts.values()].find((a) =>
          a.me.memberships.some((x) => x.company_id === co && x.membership_id === m),
        )?.me.user.name ?? null,
      timezoneOf: (co) => companies.find((x) => x.id === co)?.calendar_config.timezone ?? "UTC",
      createTask: (p, body) => {
        const res = workHandlers["tasks.create"]?.({
          url: new URL("http://fake/"),
          params: { co: p.company.id },
          body,
          me: p.me,
          principal: p,
          headers: new Headers(),
        });
        const made = res ? peekJson<{ id?: string; task?: { id: string } }>(res) : undefined;
        return made?.task?.id ?? made?.id ?? null;
      },
    }),
    // WOS-BUNDLE: duplicate, My Tasks sections, task time and the timer, the usage CSV.
    ...createWorkBundle2Fake({
      now,
      desOf,
      work: () => workBridge,
      workHandlers: () => workHandlers,
      copyFiles: (co, from, to, actor) => filesFake.copyTaskFiles(co, from, to, actor),
    }),
    // EXPORT-CORE: the Company Archive download (fake-company-export.ts), owners only.
    ...createCompanyExportHandlers(),
    // IMPORT (HARDEN): guided Zulip / ClickUp imports (fake-imports.ts), owners only.
    ...createImportHandlers({
      now,
      peopleOf: (p) => access.peopleOf(p).map((x) => ({ id: x.id, name: x.display_name })),
      featureOn: (p, feature) => {
        const s = p.me.entitlements[p.company.id]?.[feature as "core"] ?? "disabled";
        return s === "active" || s === "trial";
      },
    }),
    // M4-01: personal crews (fake-crews.ts), over the seats and roles above; M4-03's features
    // over the inbox, chat and Work twins.
    ...crewFake.handlers,
    // WORKFORCE: agents (AG; fake-workforce.ts), core, under the twin's DEs and people.
    ...createWorkforceHandlers({
      now,
      desOf,
      peopleOf: (p) =>
        access.peopleOf(p).map((x) => ({ id: x.id, name: x.display_name, status: x.status })),
      teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      // SYSTEM-AGENT: a system agent's (closed) assignments are history, never a holder here.
      assignmentsOf: (c) =>
        (structureStores?.assignmentsOf(c) ?? []).flatMap((a) =>
          a.membership_kind === "system_agent"
            ? []
            : [{ ...a, membership_kind: a.membership_kind }],
        ),
      revise: (p, id, before, after) => controlFake.revise(p, "workforce_agent", id, before, after),
      expose: (api) => {
        workforceGuards = api;
      },
    }),
    // M4-02: skill packs (fake-skill-packs.ts), behind `agents`, over the structure stores.
    ...createSkillPackHandlers({
      now,
      featureOn: (p, feature) => {
        const s = p.me.entitlements[p.company.id]?.[feature as "core"] ?? "disabled";
        return s === "active" || s === "trial" || s === "grace";
      },
      rolesOf: (companyId) => access.rolesOf(companyId),
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      desOf,
      reviseSkills: (p, id, from, to) => controlFake.reviseSkills(p, id, from, to),
      ...(options.skillAdditions ? { seedAdditions: options.skillAdditions } : {}),
    }),
    // D10 scope editor: a DE seat's agent policy, its preview and the explainer
    // (fake-agent-policy.ts), behind `agents`.
    ...createAgentPolicyHandlers({
      now,
      featureOn: (p, feature) => {
        const s = p.me.entitlements[p.company.id]?.[feature as "core"] ?? "disabled";
        return s === "active" || s === "trial" || s === "grace";
      },
      desOf,
      // the targets an explanation names, resolved by the work twin as its routes would
      boardExists: (p, id) => {
        const res = workHandlers["boards.get"]?.({
          url: new URL(`http://fake.local/v1/companies/${p.company.id}/boards/${id}`),
          params: { co: p.company.id, id },
          body: undefined,
          me: p.me,
          principal: { ...p, roles: ["owner"], manager: true },
          headers: new Headers(),
        });
        return Boolean(res?.ok);
      },
      taskBoardOf: (p, id) => {
        const res = workHandlers["tasks.get"]?.({
          url: new URL(`http://fake.local/v1/companies/${p.company.id}/tasks/${id}`),
          params: { co: p.company.id, id },
          body: undefined,
          me: p.me,
          principal: { ...p, roles: ["owner"], manager: true },
          headers: new Headers(),
        });
        return (res && peekJson<{ board_id: string }>(res)?.board_id) ?? null;
      },
    }),
    // BYO-01: My subscriptions (fake-subscriptions.ts): model credentials, tool connections.
    // M4-04: Acme's owner has two Google connections from before (fake-subscriptions.ts).
    // MODEL-POLICY: Setup › Model policy (fake-model-policy.ts), answering BYO through the
    // subscriptions twin and the overrides through the deploys twin's.
    ...(() => {
      const subs = createSubscriptionHandlers({
        now,
        demo: {
          companyId: COMPANY_IDS.acme,
          ownerUserId: USER_IDS.owner,
          email: "owner@acme.example",
        },
      });
      const byo = (p: Principal) => {
        const res = subs["subscriptionPolicy.get"]?.({
          url: new URL("http://fake.local/"),
          params: { co: p.company.id },
          body: undefined,
          me: p.me,
          principal: p,
          headers: new Headers(),
        });
        const v = res ? peekJson<OperationOutput<"subscriptionPolicy.get">>(res) : undefined;
        if (!v) throw new Error("fake: the subscription policy did not answer");
        return v;
      };
      // MODEL-KEYS-PLANS: the company's live keys, through the subscriptions twin's list.
      const keysOf = (p: Principal) => {
        const res = subs["modelCredentials.list"]?.({
          url: new URL("http://fake.local/?scope=company&limit=100"),
          params: { co: p.company.id },
          body: undefined,
          me: p.me,
          principal: p,
          headers: new Headers(),
        });
        const v = res ? peekJson<OperationOutput<"modelCredentials.list">>(res)?.data : undefined;
        return (v ?? []).filter((c) => c.kind === "api_key" && c.state === "active");
      };
      return {
        ...subs,
        ...createModelPolicyHandlers({ now, desOf, overrides: () => deOverrides, byo }),
        ...createModelAccessHandlers({ now, planOf: billingFake.modelAccessOf, keysOf }),
      };
    })(),
    // B5-DECIDE: Setup › Decisions, the classifier switches and the DEs' rubrics (fake-decide.ts).
    ...createDecideHandlers({ now, desOf, seedCompanyId: COMPANY_IDS.acme }),
    // DC-03: the agency console's template catalog and authoring (fake-templates.ts).
    ...createTemplateHandlers({
      now,
      share: (c) => {
        catalog = c;
      },
    }),
    // DC-04: deploys and template overrides over that catalog (fake-deploys.ts).
    ...createDeployHandlers({
      now,
      catalog: () => catalog,
      principalFor,
      desOf,
      seatHasBudget: (companyId, seatId) => seatHasBudget(companyId, seatId),
      shareOverrides: (o) => {
        deOverrides = o;
      },
      shareDeploys: (d, tick) => {
        deDeploys = d;
        deployTick = tick;
      },
    }),
    // COS-SLICE1: Chief of Staff settings and follow-up cards.
    ...cosFake.handlers,
    // COS-MODES: the Chief of Staff's mode and each person's agent-direction card.
    ...cosModesFake.handlers,
    // DE-BRIEFS: brief settings (company, person, DE).
    ...briefsFake.handlers,
    // Chat (M2-06) and Work (M2-07): the routes both twins serve go to Work first (twinRoutes).
    ...filesFake.handlers,
    ...chatFake.handlers,
    ...workHandlers,
    ...twinRoutes(workHandlers, chatFake.handlers, chatFake.prime),
    // B4-ITEM-TEMPLATES: a board's item templates (`tasks.create` below fills from one)
    ...itemTemplatesFake.handlers,
    // CLOSED-LOOPS: commitments asked of and by the caller (fake-commitments.ts)
    ...commitmentsFake.handlers,
    // BIZ-PLAYBOOKS / BIZ-CLIENT-HEALTH: playbook links, client health, surveys (fake-business.ts)
    ...businessFake.handlers,
    // DC-07: a DE's verified rate and the decision logs of the runs-with-verdicts twin
    // (fake-runs.ts), whose runs the Agents twin lists after its own and chat's.
    ...runsTwin.handlers,
    // Agents mode (M3-08): runs, approvals, budgets, the DE budget and composition, events. A run
    // or approval the Agents twin does not hold falls back to DC-07's runs twin, then to the chat
    // twin (its demo cards).
    ...agentsHandlers,
    ...fallbackRoutes(agentsHandlers, {
      ...chatFake.handlers,
      ...fallbackRoutes(runsTwin.handlers, chatFake.handlers),
    }),
    // DC-10: a deployed DE's drift audits, the run job played by time (fake-drift-audits.ts).
    ...createDriftAuditHandlers({ now, desOf, agentsOn }),
    // AGENT-RECORD: a DE's track record, the honest empty one (fake-agent-record.ts).
    // DE-KPIS-LIVE: the record's runs are the twins' runs; its helpers the workforce twin's.
    ...createAgentRecordHandlers({
      todayWeek,
      desOf,
      runsOf: (companyId) => runsOf(companyId) as readonly RecordRun[],
      helpersOf: (p, deId) =>
        (workforceGuards?.chartAgentsOf(p) ?? [])
          .filter(
            (a) => a.reports_to_membership_id === deId || a.used_by_membership_ids.includes(deId),
          )
          .map((a) => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            status: a.status,
            relation: a.reports_to_membership_id === deId ? ("lead" as const) : ("uses" as const),
            // The twin holds no ledger rows or helper cards: honest zeros.
            cost_minor: 0,
            calls: 0,
            jobs: 0,
            jobs_done: 0,
            jobs_blocked: 0,
            job_success_rate: null,
            last_activity: null,
            last_activity_at: null,
          }))
          .sort((a, b) =>
            a.relation === b.relation
              ? a.name.localeCompare(b.name)
              : a.relation === "lead"
                ? -1
                : 1,
          ),
    }),
    // PROVISION-IN-PRODUCT: a DE's set-up (fake-provisioning.ts).
    ...createProvisioningHandlers({ now, desOf }),
    // M2-04: lists both twins hold rows of are merged (fake-merge.ts) — the inbox (Work's task
    // items, Chat's mentions and replies), read-all counts, and search (tasks and messages).
    // SEARCH-NOTIF: the person's preferences applied to the merged inbox (fake-palette.ts).
    "inbox.list": primed(chatFake.prime, async (ctx) => {
      const res = await mergedPages(
        true,
        workHandlers["inbox.list"],
        chatFake.handlers["inbox.list"],
      )(ctx);
      if (!res.ok || !ctx.principal) return res;
      const page = (await res.json()) as {
        data: { kind: string; created_at: string; desktop: boolean }[];
      };
      return json({
        ...page,
        data: paletteFake.stampInbox(ctx.principal.company, paletteFake.meIn(ctx), page.data),
      });
    }),
    ...paletteFake.handlers,
    ...insightsFake.handlers,
    ...pushFake.handlers,
    ...agentHealthFake.handlers,
    ...skillProposalsFake.handlers,
    ...opportunitiesFake.handlers,
    ...screensFake.handlers,
    ...controlFake.handlers,
    ...serviceEndpointsFake.handlers,
    ...satellitesFake.handlers,
    ...operatorConsoleFake.handlers,
    ...monitoringFake.handlers,
    ...walletFake.handlers,
    ...outboxFake.handlers,
    ...loopsFake.handlers,
    ...oooFake.handlers,
    ...intakeFake.handlers,
    ...contactsFake.handlers,
    "inbox.readAll": primed(
      chatFake.prime,
      summedCounts(workHandlers["inbox.readAll"], chatFake.handlers["inbox.readAll"]),
    ),
    "search.query": primed(
      chatFake.prime,
      mergedPages(true, workHandlers["search.query"], chatFake.handlers["search.query"]),
    ),
    // M7-02: pages in Work's spaces (fake-pages.ts); a space's sidebar lists them.
    ...pagesFake.handlers,
    // A task made with a body becomes its page (the work twin makes the task, the pages twin
    // the page), as the real `tasks.create` does.
    "tasks.create": (ctx) => {
      const made = workHandlers["tasks.create"]?.(ctx);
      if (!made) return notFound();
      const b = (ctx.body ?? {}) as { body?: unknown[]; body_markdown?: string };
      if (made.status !== 201 || !ctx.principal) return made;
      if (b.body === undefined && typeof b.body_markdown !== "string") return made;
      const id = peekJson<{ id?: string }>(made)?.id;
      if (!id) return made;
      const set = pagesFake.setTaskBody(ctx.principal, id, {
        ...(b.body ? { body: b.body } : {}),
        ...(typeof b.body_markdown === "string" ? { markdown: b.body_markdown } : {}),
      });
      const fresh = set
        ? workHandlers["tasks.get"]?.({ ...ctx, params: { ...ctx.params, id } })
        : undefined;
      return fresh ? json(peekJson(fresh), 201) : made;
    },
    // DOCS-KB: agents' knowledge sources (fake-knowledge.ts) over those pages; after the pages
    // twin so its `pages.readers` is the one served.
    ...knowledgeFake.handlers,
    // WOS-BUNDLE: tags, task links, favorites and recents (fake-work-bundle.ts); its `tasks.bulk`
    // serves the `tag` action and hands every other kind to the Work twin.
    ...createWorkBundleFake({
      now,
      work: () => workBridge,
      workHandlers,
      pageGet: pagesFake.handlers["pages.get"],
      channelGet: chatFake.handlers["channels.get"],
      exposeStars: (read) => {
        starredBoardsOf = read;
      },
    }),
    // M7-04: memory review, the memory proxy, promotions (→ pages) and Setup › Recall.
    ...createMemoryFake({
      now,
      companies,
      work: () => workBridge,
      recall: options.recall ?? "connected",
      promote: pagesFake.promote,
    }),
    ...rulesFake.handlers,
    // RECALL-PEOPLE-SURFACES: Profile › Memory, Remember this, Off the record, "forget this".
    ...createMemoryPeopleFake({ now, recall: options.recall ?? "connected" }),
    // TEMPLATES-T2..T4: no role published in the fake catalog; the company's overlays are kept.
    ...createRoleTemplatesFake({
      now,
      isDe: (p, id) => desOf(p.company.id).some((d) => d.membership_id === id),
    }),
    // SHARED-DES: a shared seat's serves links (fake-shared-des.ts).
    ...sharedDes.handlers,
    // AWARENESS-MVP: the boards a DE seat uses (fake-seat-boards.ts).
    ...seatBoards.handlers,
    // D8: the create/move dialog's reach preview over the structure and work twins' stores.
    ...createReachFake({
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
      teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
      spacesOf: (companyId) => workBridge?.spacesOf(companyId) ?? null,
      nameOf: (companyId, membershipId) =>
        desOf(companyId).find((d) => d.membership_id === membershipId)?.display_name ?? "Agent",
      memberKind: (p, membershipId) =>
        desOf(p.company.id).some((d) => d.membership_id === membershipId)
          ? "digital_employee"
          : access.peopleOf(p).some((x) => x.id === membershipId) ||
              p.me.memberships.some(
                (m) => m.company_id === p.company.id && m.membership_id === membershipId,
              )
            ? "person"
            : null,
    }),
    // M5-04: the finance views, price lists and FX rates over the seats, teams, ledger and
    // budgets above (fake-finance.ts).
    ...createFinanceHandlers({
      now,
      seatsOf: (c) => structureStores?.seatsOf(c) ?? [],
      assignmentsOf: (c) => structureStores?.assignmentsOf(c) ?? [],
      teamsOf: (c) => structureStores?.teamsOf(c) ?? [],
      teamMembersOf: (c) => structureStores?.teamMembersOf(c) ?? [],
      roleName: (companyId, roleId) =>
        access.rolesOf(companyId).find((r) => r.id === roleId)?.name ?? "Role",
      personIn: (p, membershipId) =>
        access.peopleOf(p).some((x) => x.id === membershipId && x.kind === "person") ||
        [...accounts.values()].some((a) =>
          a.me.memberships.some(
            (m) => m.company_id === p.company.id && m.membership_id === membershipId,
          ),
        ),
      // The same ledger Setup › Costs lists (fake-ops.ts `ledgerOf`).
      ledgerOf: (p) =>
        [
          ...desOf(p.company.id).flatMap((de) => costLedger(de, todayWeek())),
          ...ops.costs.filter((c) => c.company_id === p.company.id),
        ].filter((e) => e.visibility === "client" || p.operator),
      budgetsOf: (companyId) => budgetsOf(companyId),
    }),
    // M5-05: the agency console and the company side of break-glass (fake-agency.ts).
    ...agency.handlers,
    // MANAGED-POLICY: managed mode and requests for agents (fake-agent-requests.ts).
    ...createAgentRequestHandlers({
      now,
      uid: () => uid(0xa9e0, ++seq),
      companies: () => companies,
      isDe: (companyId, id) => desOf(companyId).some((d) => d.membership_id === id),
      isAgent: (p, id) => workforceGuards?.agentOf(p, id) != null,
    }).handlers,
    // KPI-VISIBILITY: Setup › People & access › KPI visibility (fake-kpi-visibility.ts).
    ...kpiVisibility.handlers,
    // Parity P1: prefs, task layouts, watchers, field history, observations (fake-p1.ts); last,
    // since it wraps Work's `tasks.get` and `tasks.setStatus` (`previous` reads them lazily).
    ...p1Fake,
    // B4-ITEM-TEMPLATES: `tasks.create` over P1's (see `templatedCreate`).
    ...templatedCreate,
    // GUIDED-FLOW (fake-flows.ts); an accepted invitation hands its runs to the new member.
    ...flowsFake.handlers,
    "invitations.accept": flowsFake.onAccept(
      access.handlers["invitations.accept"] as Handler | undefined,
    ),
  };

  // S1-17 routes: gate, data entry, imports, the Friday report preview, Setup lists ----------

  function reportOf(p: Principal, id: string | undefined): FridayReportRecord | null {
    const r = reportsOf(p.company).find((x) => x.id === id);
    if (!r || (!p.manager && r.state !== "sent")) return null;
    return r;
  }

  function nextVersion(p: Principal, from: FridayReportRecord, patch: Partial<FridayReportRecord>) {
    const list = reportsOf(p.company);
    const version =
      Math.max(...list.filter((r) => r.week_key === from.week_key).map((r) => r.version)) + 1;
    const at = now().toISOString();
    const record: FridayReportRecord = {
      ...from,
      id: uid(0x8800, ++seq),
      version,
      state: "generated",
      generated_at: at,
      frozen_at: null,
      sent_at: null,
      send_error: null,
      html_object_key: `reports/${p.company.slug}/${from.week_key}.v${version}.html`,
      pdf_object_key: `reports/${p.company.slug}/${from.week_key}.v${version}.pdf`,
      created_at: at,
      updated_at: at,
      ...patch,
    };
    list.push(record);
    return record;
  }

  // UI des routes: DE create and lifecycle, LiteLLM provisioning, DE subscriptions, time-entry
  // corrections. Same access rules as the API: managers write, agency operators provision. -----

  function profileOf(p: Principal, de: FakeDe): DeProfile {
    return {
      ...deProfile(de, todayWeek(), { ...viewerOf(p), manager: p.manager }, null),
      can_intervene: canIntervene(p, de),
      activity: activityOf(p.company.id, de),
      how_i_work: howIWorkOf(p, de),
    };
  }

  /** CHART-HOWIWORK: an owner or admin, or a person supervising the DE's seat at any depth. */
  function canEditHowIWork(p: Principal, de: FakeDe): boolean {
    if (p.roles.includes("owner") || p.roles.includes("admin")) return true;
    return canIntervene({ ...p, manager: false }, de);
  }

  /** CHART-HOWIWORK: the DE's current version (none written: version 0, no text). */
  function howIWorkOf(p: Principal, de: FakeDe): NonNullable<DeProfile["how_i_work"]> {
    const current = howIWorkStore.get(de.membership_id)?.at(-1);
    return {
      text: current?.text ?? null,
      version: current?.version ?? 0,
      updated_at: current?.created_at ?? null,
      updated_by: current?.author ?? null,
      can_edit: canEditHowIWork(p, de),
    };
  }

  /**
   * WORKFORCE (the API's rule): a manager, or a person holding, supervising or standing above the
   * DE's seat at any depth, may intervene (pause / resume, rename).
   */
  function canIntervene(p: Principal, de: FakeDe): boolean {
    if (p.manager) return true;
    const me = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
    if (!me) return false;
    const stored = structureStores?.assignmentsOf(p.company) ?? [];
    const seatsList = structureStores?.seatsOf(p.company) ?? [];
    const des = desOf(p.company.id);
    const holds = (seatId: string) =>
      stored.some((a) => a.seat_id === seatId && a.active_to === null && a.membership_id === me) ||
      des.some((d) => d.supervisor.seat_id === seatId && d.supervisor.membership_id === me);
    const up = (seatId: string) =>
      seatsList.find((s) => s.id === seatId)?.supervisor_seat_id ??
      des.find((d) => d.seat_id === seatId)?.supervisor.seat_id ??
      null;
    const seen = new Set<string>();
    let at: string | null = de.seat_id;
    while (at !== null && !seen.has(at)) {
      seen.add(at);
      if (holds(at)) return true;
      at = up(at);
    }
    return false;
  }

  /** The DE as `LitellmKeyResult.digital_employee` plus the attempt as the operator sees it. */
  function litellmResult(p: Principal, de: FakeDe) {
    const i = issuances.get(de.membership_id);
    return {
      digital_employee: deSummary(de, todayWeek(), viewerOf(p)),
      provisioning: {
        attempt: i?.attempt ?? 0,
        state: i?.state ?? "failed",
        key_alias: i?.alias ?? "",
        expires_at: i?.state === "issued" ? i.expires_at : null,
        error: de.provisioning_error ?? null,
        pending_deletes: 0,
      },
    };
  }

  function subscriptionsOf(company: FakeCompany): DeSubscription[] {
    let list = subscriptionStore.get(company.id);
    if (!list) {
      list = subscriptions(
        company,
        desOf(company.id).filter((d) => !d.fresh),
      );
      subscriptionStore.set(company.id, list);
    }
    return list;
  }

  function desHandlers(): Partial<Record<RouteId, Handler>> {
    return {
      "digitalEmployees.create": ({ principal, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const b = (body ?? {}) as Partial<{
          display_name: string;
          template_key: string;
          seat_id: string;
          runtime_ref: DeProfile["runtime_ref"];
          started_on: string;
        }>;
        if (!b.display_name?.trim()) return validation("display_name must not be empty");
        if (!b.template_key?.trim()) return validation("template_key must not be empty");
        const list = desOf(principal.company.id);
        // Setup › Seats' store first (seats created there, DC-11's wizard), then the derived ones.
        const seat =
          structureStores?.seatsOf(principal.company).find((x) => x.id === b.seat_id) ??
          seats(principal.company, list).find((x) => x.id === b.seat_id);
        if (!seat) return validation("seat_id: no such seat in this company");
        if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
        if (list.some((d) => d.seat_id === seat.id))
          return problem(409, "seat_occupied", "Seat occupied", "the seat already has a DE");
        // The reporting rule (2026-10-26): the supervisor seat is a person's, or a DE's (whose
        // own supervisor, in this fake, is always a person). A paused DE above still holds its
        // seat (non-retired counts, as the API); a retired one does not.
        const deAbove = list.find(
          (d) => d.seat_id === seat.supervisor_seat_id && d.status !== "retired",
        );
        const sup =
          list.find((d) => d.supervisor.seat_id === seat.supervisor_seat_id)?.supervisor ??
          (deAbove
            ? {
                seat_id: deAbove.seat_id,
                title: deAbove.seat_title,
                membership_id: deAbove.membership_id,
                name: deAbove.display_name,
              }
            : undefined);
        if (!sup)
          return problem(
            422,
            "supervisor_required",
            "Supervisor required",
            "the seat's supervisor seat has no active person",
          );
        const first = list[0];
        const key = (Object.keys(COMPANY_IDS) as (keyof typeof COMPANY_IDS)[]).find(
          (k) => COMPANY_IDS[k] === principal.company.id,
        );
        if (!key || !first) return notFound();
        const de: FakeDe = {
          company: key,
          membership_id: uid(0x1000, 0xe00 + ++seq),
          display_name: b.display_name.trim(),
          seat_id: seat.id,
          seat_title: seat.title,
          supervisor: sup,
          founder_membership_id: first.founder_membership_id,
          template_key: b.template_key.trim(),
          instance: b.runtime_ref?.instance_id ?? "",
          started_weeks_ago: 0,
          status: "onboarding",
          skill: 1,
          keys: [],
          fresh: true,
          runtime: b.runtime_ref ?? null,
          started_on: b.started_on ?? null,
          litellm_key_alias: null,
          provisioning_error: null,
        };
        // WF-POLISH: the Custom add flow's role, goal and backstory (blank: none).
        const profile = deProfileText(body);
        if (profile instanceof Response) return profile;
        // CLOCKWORK B2: role, goal and backstory are required; one left out is the platform default.
        de.profile = {
          ...EMPTY_DE_PROFILE,
          ...defaultProfile(b.display_name.trim()),
          ...profile,
        };
        des.push(withLatestDeploy(de));
        // WF-CONTROL: a DE made with a role, goal, backstory or standing orders: revision 1.
        controlFake.revise(principal, "digital_employee", de.membership_id, null, {
          ...de.profile,
        });
        // DE-WIZARD: `seed_template_metrics: false` leaves the seat's KPIs to the job.
        structureStores?.seedDeSeat(
          principal.company,
          seat.id,
          de.template_key,
          (body as { seed_template_metrics?: boolean } | null)?.seed_template_metrics !== false,
        );
        return json(profileOf(principal, de), 201);
      },
      "digitalEmployees.update": ({ principal, params, body }) => {
        if (!principal) return notFound();
        const de = deIn(principal, params.id);
        if (!principal.manager && !de) return problem(403, "forbidden", "Forbidden");
        if (!de) return notFound();
        const b = (body ?? {}) as Partial<{
          display_name: string;
          template_key: string;
          runtime_ref: DeProfile["runtime_ref"];
          started_on: string | null;
          status: FakeDe["status"];
          person_name: string | null;
        }>;
        // WORKFORCE: a person above the DE (`can_intervene`) may only pause / resume and rename
        // (HELPERS: its person name too).
        if (!principal.manager) {
          const limited =
            Object.keys(b).every(
              (k) => k === "status" || k === "display_name" || k === "person_name",
            ) &&
            (b.status === undefined ||
              ((b.status === "active" || b.status === "paused") &&
                (de.status === "active" || de.status === "paused")));
          if (!limited || !canIntervene(principal, de))
            return problem(403, "forbidden", "Forbidden");
        }
        if (b.status === "onboarding") return validation("status: onboarding is not a move");
        // WF-POLISH: role, goal, backstory, standing orders (managers: the limited check above).
        const profile = deProfileText(body);
        if (profile instanceof Response) return profile;
        if (de.status === "retired" && b.status && b.status !== "retired")
          return problem(409, "conflict", "Conflict", "a retired digital employee stays retired");
        if (b.status === "retired" && de.status !== "retired") {
          const stranded = strandedAfter(principal, {
            membershipIds: [de.membership_id],
            retire: [de.membership_id],
          });
          if (stranded) return stranded;
        }
        if (b.display_name !== undefined) {
          if (!b.display_name.trim()) return validation("display_name must not be empty");
          de.display_name = b.display_name.trim();
        }
        // HELPERS-2 (Codex HELPERS): the person name is stored (production's column); a blank
        // one clears it; at most 80 characters.
        if (b.person_name !== undefined) {
          const pn = b.person_name?.trim() ?? "";
          if (pn.length > 80) return validation("person_name: at most 80 characters");
          de.person_name = pn || null;
        }
        if (b.template_key !== undefined) de.template_key = b.template_key;
        if (b.runtime_ref !== undefined) de.runtime = b.runtime_ref;
        if (b.started_on !== undefined) de.started_on = b.started_on;
        if (Object.keys(profile).length) {
          const before = { ...EMPTY_DE_PROFILE, ...de.profile };
          de.profile = { ...EMPTY_DE_PROFILE, ...de.profile, ...profile };
          // WF-CONTROL: a change to what the DE is becomes its next profile revision.
          controlFake.revise(principal, "digital_employee", de.membership_id, before, {
            ...de.profile,
          });
        }
        if (b.status) {
          if (b.status === "retired" && de.status !== "retired")
            for (const k of de.keys)
              if (k.status === "active") {
                k.status = "revoked";
                k.revoked_at = now().toISOString();
              }
          de.status = b.status;
        }
        return json(profileOf(principal, de));
      },
      // AGENT-MENTION: remove = retire + revoke every key + off the roster (name typed).
      "digitalEmployees.remove": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const de = deIn(principal, params.id);
        if (!de) return notFound();
        const confirm = String((body as { confirm?: unknown } | null)?.confirm ?? "");
        if (confirm.trim().toLowerCase() !== de.display_name.trim().toLowerCase())
          return validation("confirm: type the digital employee's name to remove it");
        if (!de.removed_at) {
          const stranded =
            de.status === "retired"
              ? null
              : strandedAfter(principal, {
                  membershipIds: [de.membership_id],
                  retire: [de.membership_id],
                });
          if (stranded) return stranded;
          const t = now().toISOString();
          for (const k of de.keys)
            if (k.status === "active") {
              k.status = "revoked";
              k.revoked_at = t;
            }
          de.status = "retired";
          de.removed_at = t;
          de.litellm_key_alias = null;
        }
        return json(profileOf(principal, de));
      },
      // PURGE-RETIRED-AGENTS: a retired DE removed for good (managers; the name typed).
      "digitalEmployees.purgePlan": ({ principal, params }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const de = deIn(principal, params.id);
        if (!de) return notFound();
        return json(purgePlanOfDe(de));
      },
      "digitalEmployees.purge": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const de = deIn(principal, params.id);
        if (!de) return notFound();
        const b = (body ?? {}) as { confirm?: unknown; delete_memory?: unknown };
        if (!sameName(String(b.confirm ?? ""), de.display_name))
          return validation("confirm: type the agent's name to remove it permanently");
        const plan = purgePlanOfDe(de);
        if (plan.blocked) return problem(409, "conflict", plan.blocked.detail);
        des.splice(des.indexOf(de), 1);
        return json(fakePurgeResult(plan, b.delete_memory === true));
      },
      "digitalEmployees.createLitellmKey": ({ principal, params, body }) => {
        if (!principal?.operator) return notFound();
        const de = deIn(principal, params.id);
        if (!de) return notFound();
        const b = (body ?? {}) as Partial<{ max_budget_minor: number; currency: string }>;
        if (!Number.isInteger(b.max_budget_minor) || (b.max_budget_minor ?? 0) <= 0)
          return validation("max_budget_minor must be a positive integer");
        if ((b.currency ?? "USD") !== "USD")
          return validation("currency: LiteLLM budgets are in USD");
        if (de.status === "retired")
          return problem(409, "conflict", "Conflict", "a retired digital employee gets no key");
        const prev = issuances.get(de.membership_id);
        if (prev?.state === "issued" || prev?.state === "delivered" || de.litellm_key_alias)
          return problem(409, "conflict", "Conflict", "the digital employee already has its key");
        const alias = `cos-${principal.company.slug}-${de.membership_id.slice(-6)}`;
        issuances.set(de.membership_id, {
          attempt: (prev?.attempt ?? 0) + 1,
          alias,
          key: `sk-fake-${crypto.randomUUID().replaceAll("-", "")}`,
          expires_at: new Date(now().getTime() + 24 * 3_600_000).toISOString(),
          state: "issued",
        });
        de.litellm_key_alias = alias;
        de.provisioning_error = null;
        return json(litellmResult(principal, de));
      },
      "digitalEmployees.getLitellmKey": ({ principal, params }) => {
        if (!principal?.operator) return notFound();
        const de = deIn(principal, params.id);
        const i = de ? issuances.get(de.membership_id) : undefined;
        if (i?.state !== "issued" || i.expires_at <= now().toISOString()) return notFound();
        return json({
          attempt: i.attempt,
          key_alias: i.alias,
          virtual_key: i.key,
          expires_at: i.expires_at,
        });
      },
      "digitalEmployees.ackLitellmKey": ({ principal, params, body }) => {
        if (!principal?.operator) return notFound();
        const de = deIn(principal, params.id);
        const i = de ? issuances.get(de.membership_id) : undefined;
        if (!de || !i) return notFound();
        const attempt = (body as { attempt?: number } | null)?.attempt;
        if (i.state !== "issued" || attempt !== i.attempt)
          return problem(409, "conflict", "Conflict", "no live issuance of that attempt");
        i.state = "delivered";
        if (de.status === "onboarding") de.status = "active";
        return json(litellmResult(principal, de));
      },
      "deSubscriptions.list": ({ principal, url }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const rows = subscriptionsOf(principal.company).filter(
          (x) => x.scope === "client" || principal.operator,
        );
        return json(page(rows, url));
      },
      "deSubscriptions.create": ({ principal, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const b = (body ?? {}) as Partial<DeSubscription>;
        const de = desOf(principal.company.id).find((d) => d.membership_id === b.membership_id);
        if (!de) return validation("membership_id: no digital employee with this membership id");
        if (b.seat_id !== de.seat_id) return validation("seat_id: no such seat");
        if (b.currency !== principal.company.reporting_currency)
          return validation(
            `currency: the company's ledger is in ${principal.company.reporting_currency}`,
          );
        if (!Number.isInteger(b.amount_minor) || (b.amount_minor ?? -1) < 0)
          return validation("amount_minor must be a non-negative integer");
        if (!b.start_month || !MONTH.test(b.start_month)) return validation("start_month");
        if (b.end_month && (!MONTH.test(b.end_month) || b.end_month < b.start_month))
          return validation("end_month is before start_month");
        const at = now().toISOString();
        const row: DeSubscription = {
          company_id: principal.company.id,
          id: uid(0x8f80, ++seq),
          seat_id: de.seat_id,
          membership_id: de.membership_id,
          amount_minor: b.amount_minor as number,
          currency: principal.company.reporting_currency,
          cadence: "monthly",
          start_month: b.start_month,
          end_month: b.end_month ?? null,
          scope: b.scope === "agency" ? "agency" : "client",
          maintenance_plan_id: null,
          tier: null,
          started_on: null,
          ended_on: null,
          actor_kind: principal.operator ? "agency_user" : "person",
          actor_membership_id: null,
          created_at: at,
          updated_at: at,
        };
        subscriptionsOf(principal.company).unshift(row);
        return json(row, 201);
      },
      "deSubscriptions.update": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const row = subscriptionsOf(principal.company).find((x) => x.id === params.id);
        if (!row || (row.scope === "agency" && !principal.operator)) return notFound();
        const b = (body ?? {}) as Partial<
          Pick<DeSubscription, "amount_minor" | "end_month" | "scope">
        >;
        const changes: Partial<DeSubscription> = {};
        if (b.amount_minor !== undefined && b.amount_minor !== row.amount_minor) {
          if (!Number.isInteger(b.amount_minor) || b.amount_minor < 0)
            return validation("amount_minor must be a non-negative integer");
          changes.amount_minor = b.amount_minor;
        }
        if (b.end_month !== undefined && b.end_month !== row.end_month) {
          if (b.end_month !== null && (!MONTH.test(b.end_month) || b.end_month < row.start_month))
            return validation("end_month is before start_month");
          changes.end_month = b.end_month;
        }
        if (b.scope !== undefined && b.scope !== row.scope) changes.scope = b.scope;
        if (!Object.keys(changes).length) return validation("the patch changes nothing");
        Object.assign(row, changes, { updated_at: now().toISOString() });
        return json(row);
      },
      "timeEntries.update": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const row = timeIndex.get(params.id ?? "");
        if (!row || row.company_id !== principal.company.id) return notFound();
        if (row.visibility === "agency" && !principal.operator) return notFound();
        if (timeEdits.get(row.id) === null || row.voided_at)
          return problem(409, "conflict", "Conflict", "no live revision");
        const b = (body ?? {}) as Partial<{
          duration_seconds: number;
          note: string | null;
          approval_status: TimeEntry["approval_status"];
          rejection_reason: string | null;
        }>;
        const at = now().toISOString();
        const next: TimeEntry = { ...row };
        if (b.duration_seconds !== undefined) {
          if (!Number.isInteger(b.duration_seconds) || b.duration_seconds <= 0)
            return validation("duration_seconds must be a positive integer");
          if (b.duration_seconds !== row.duration_seconds) {
            next.duration_seconds = b.duration_seconds;
            next.minutes = Math.round(b.duration_seconds / 60);
            next.revision = row.revision + 1;
            next.approval_status = "pending";
            next.decided_at = null;
          }
        }
        if (b.note !== undefined) next.note = b.note;
        if (b.approval_status) {
          next.approval_status = b.approval_status;
          next.decided_at = at;
          next.rejection_reason =
            b.approval_status === "rejected" ? (b.rejection_reason ?? null) : null;
        }
        timeEdits.set(row.id, next);
        timeIndex.set(row.id, next);
        touch(principal.company.id, row.period_key);
        return json(next);
      },
      "timeEntries.delete": ({ principal, params }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const row = timeIndex.get(params.id ?? "");
        if (!row || row.company_id !== principal.company.id || timeEdits.get(row.id) === null)
          return notFound();
        if (row.visibility === "agency" && !principal.operator) return notFound();
        timeEdits.set(row.id, null);
        touch(principal.company.id, row.period_key);
        return json({ ...row, voided_at: now().toISOString() });
      },
    };
  }

  function s117Handlers(): Partial<Record<RouteId, Handler>> {
    return {
      "operator.gate.get": ({ me, url }) => {
        // Agency operators only; anyone else is 404, as the real route.
        if (!me.agency) return notFound();
        const asOf = url.searchParams.get("as_of") ?? now().toISOString().slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(asOf) || Number.isNaN(Date.parse(asOf)))
          return validation("as_of must be YYYY-MM-DD");
        const week = isoWeekKey(new Date(`${asOf}T12:00:00Z`));
        // A future week is treated as having happened (the fake's data are week functions).
        const asToday = weeksBetween(todayWeek(), week) > 0 ? week : todayWeek();
        const perDe: GatePerDeRow[] = [];
        let htva: Principal | null = null;
        for (const g of me.agency.grants) {
          const p = principalFor(me, g.company_id);
          if (!p?.operator) continue;
          if (p.company.id === FAKE_OPERATOR_COMPANY_ID) {
            htva = p;
            continue;
          }
          for (const de of desOf(p.company.id).filter((d) => d.status === "active")) {
            const rows = [
              ...timeEntries(de, week, asToday),
              ...createdTime.filter((t) => t.seat_id === de.seat_id && t.period_key === week),
            ].filter((t) => t.approval_status === "approved" && t.voided_at === null);
            const minutes = (kind: string) =>
              rows.filter((t) => t.kind === kind).reduce((s, t) => s + t.minutes, 0);
            perDe.push({
              company: { id: p.company.id, name: p.company.name },
              de: { membership_id: de.membership_id, display_name: de.display_name },
              supervisor_minutes_week: minutes("supervision"),
              founder_minutes_week: minutes("founder"),
              day: deDay(deStartedOn(de, todayWeek()), asOf),
            });
          }
        }
        perDe.sort(
          (a, b) =>
            b.supervisor_minutes_week - a.supervisor_minutes_week ||
            a.company.name.localeCompare(b.company.name) ||
            a.de.display_name.localeCompare(b.de.display_name),
        );
        // The gate reads the company's own numbers (a seat's entries are the seat's).
        const gateRows = htva
          ? liveObservations(htva.company.id)
              .filter((o) => o.subject_kind === "company")
              .map((o) => ({
                key: metricDefs(htva.company).find((m) => m.id === o.metric_def_id)?.key ?? "",
                period_key: o.period_key,
                starts_on: o.period_starts_on,
                value: o.value === null ? null : Number(o.value),
                note: o.note,
              }))
          : [];
        // The operator's target rules in effect on as_of (review #9), as the real route reads them.
        const htvaCo = (htva as Principal | null)?.company;
        const htvaDefs = htvaCo ? metricDefs(htvaCo) : [];
        // PLATFORM-OPERATOR: the gates are the operator company's metrics with a gate spec.
        const gateDefs = htvaDefs.flatMap((m) =>
          m.gate &&
          !m.archived_at &&
          m.direction !== "gt" &&
          m.direction !== "lt" &&
          m.direction !== "eq"
            ? [
                {
                  key: m.key,
                  label: m.name,
                  unit: m.unit as GateDef["unit"],
                  direction: m.direction,
                  spec: m.gate,
                },
              ]
            : [],
        );
        const targets = new Map(
          (htvaCo ? targetRules(htvaCo) : [])
            .filter((t) => t.effective_from <= asOf && (!t.effective_to || t.effective_to >= asOf))
            .map((t) => [
              htvaDefs.find((m) => m.id === t.metric_def_id)?.key ?? "",
              {
                value: Number(t.value),
                value_max: t.value_max === null ? null : Number(t.value_max),
              },
            ]),
        );
        return json(
          evaluateGate({
            asOf,
            defs: gateDefs,
            company: htvaCo ? { id: htvaCo.id, slug: htvaCo.slug, name: htvaCo.name } : null,
            observations: gateRows,
            perDe,
            targets,
            // The fake reads every company in full; the operator's not granted is incomplete.
            incomplete: {
              observations: htva ? null : "the operator's company is not readable by this operator",
            },
          }),
        );
      },
      "targets.list": ({ principal, url }) => {
        if (!principal) return notFound();
        const metric = url.searchParams.get("metric");
        const rows = targetRules(principal.company).filter(
          (t) => !metric || t.metric_def_id === metric,
        );
        return json(page(rows, url));
      },
      "people.list": ({ principal, url }) => {
        if (!principal) return notFound();
        // MEMBER-ROLE: the directory is company#member's (managers and members).
        if (!principal.manager && !principal.roles.includes("member"))
          return problem(403, "forbidden", "Forbidden");
        return json(page(access.peopleOf(principal), url));
      },
      "seats.list": ({ principal, url }) => {
        if (!principal) return notFound();
        return json(page(seats(principal.company, desOf(principal.company.id)), url));
      },
      "metrics.list": ({ principal, url }) => {
        if (!principal) return notFound();
        const rows = metricDefs(principal.company).filter(
          (m) => m.visibility === "client" || principal.operator,
        );
        return json(page(rows, url));
      },
      "observations.list": ({ principal, url }) => {
        if (!principal) return notFound();
        const q = (k: string) => url.searchParams.get(k);
        // The structure block's store once it is up (metrics created through the API count).
        const visible = new Set(
          (structureStores?.metricsOf(principal.company) ?? metricDefs(principal.company))
            .filter((m) => m.visibility === "client" || principal.operator)
            .map((m) => m.id),
        );
        const rows = observations
          .filter(
            (o) =>
              o.company_id === principal.company.id &&
              visible.has(o.metric_def_id) &&
              (!q("metric") || o.metric_def_id === q("metric")) &&
              (!q("subject_kind") || o.subject_kind === q("subject_kind")) &&
              (!q("subject_id") || o.subject_id === q("subject_id")) &&
              (!q("period_type") || o.period_type === q("period_type")),
          )
          .sort(
            (a, b) =>
              b.period_starts_on.localeCompare(a.period_starts_on) ||
              b.created_at.localeCompare(a.created_at),
          );
        return json(page(rows, url));
      },
      // MEMBER-ENTRY (fake-numbers.ts): the caller's own numbers.
      "me.numbers": ({ principal }) => {
        if (!principal) return notFound();
        if (!principal.roles.includes("member") && !principal.manager)
          return problem(403, "forbidden", "Forbidden");
        return json(numbersOf(principal));
      },
      "observations.create": ({ principal, body }) => {
        if (!principal) return notFound();
        // MEMBER-ENTRY: a seat's person holder writes its own seats' manual numbers.
        const scope = principal.manager ? null : selfScopeOf(principal);
        if (!principal.manager && (!principal.roles.includes("member") || !scope))
          return problem(403, "forbidden", "Forbidden");
        const b = (body ?? {}) as Partial<{
          metric_def_id: string;
          subject_kind: string;
          subject_id: string;
          period_type: Observation["period_type"];
          period_key: string;
          value: string | number;
          note: string | null;
        }>;
        const metric = (
          structureStores?.metricsOf(principal.company) ?? metricDefs(principal.company)
        ).find((m) => m.id === b.metric_def_id);
        if (!metric) return notFound();
        if (metric.source !== "manual")
          return problem(422, "validation_failed", "Validation failed", "metric_is_derived");
        // The company, or one of its seats (the scorecard grid's inline entry, HOME-CAD-UX).
        const seatSubject =
          b.subject_kind === "seat" &&
          !!b.subject_id &&
          (structureStores?.seatsOf(principal.company) ?? []).some((s) => s.id === b.subject_id);
        // TEAM-KPI: a team's own value, entered by its lead or a manager.
        const team =
          b.subject_kind === "team"
            ? structureStores?.teamsOf(principal.company).find((t) => t.id === b.subject_id)
            : undefined;
        if (b.subject_kind === "team" && !team) return notFound();
        if (
          !seatSubject &&
          !team &&
          (b.subject_kind !== "company" || b.subject_id !== principal.company.id)
        )
          return validation("subject: the company, one of its seats or teams");
        if (!b.period_type || b.period_type !== periodTypeOf(metric.cadence) || !b.period_key)
          return validation("period_type must match the metric's cadence");
        const value = Number(b.value);
        if (b.value === undefined || b.value === "" || !Number.isFinite(value))
          return validation("value must be a number");
        const subject = {
          kind: b.subject_kind as Observation["subject_kind"],
          id: b.subject_id ?? principal.company.id,
        };
        if (
          scope &&
          !(team
            ? teamEntryAllowed(
                principal,
                team,
                structureStores?.assignmentsOf(principal.company) ?? [],
              )
            : subject.kind === "seat" &&
              (scope.get(subject.id) ?? []).some((m) => m.id === metric.id))
        )
          return problem(403, "forbidden", "Forbidden", "a member enters its own seats' numbers");
        const live = liveObservations(principal.company.id).find(
          (o) =>
            o.metric_def_id === metric.id &&
            o.period_key === b.period_key &&
            o.subject_kind === subject.kind &&
            o.subject_id === subject.id,
        );
        // A holder's correction carries its reason (the API's 422).
        if (scope && live && Number(live.value) !== value && !b.note?.trim())
          return validation("note: a correction needs its reason in note");
        const row = observationRow(principal.company.id, ++seq, {
          metric_def_id: metric.id,
          period_type: b.period_type,
          period_key: b.period_key,
          value,
          note: b.note ?? null,
          supersedes_id: live?.id ?? null,
          created_at: now().toISOString(),
          subject,
        });
        observations.push(row);
        touch(principal.company.id, isoWeekKey(new Date(`${row.period_starts_on}T12:00:00Z`)));
        return json(row, 201);
      },
      "timeEntries.create": ({ principal, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const b = (body ?? {}) as Partial<{
          seat_id: string;
          membership_id: string;
          kind: TimeEntry["kind"];
          grain: TimeEntry["grain"];
          work_date: string;
          duration_seconds: number;
          note: string | null;
        }>;
        const de = desOf(principal.company.id).find((d) => d.seat_id === b.seat_id);
        if (!de) return validation("seat_id: not a seat of this company");
        const who = people(principal.company, desOf(principal.company.id), principal.operator).find(
          (x) => x.id === b.membership_id,
        );
        if (!who) return validation("membership_id: not a person of this company");
        if (!b.kind || !["work", "supervision", "founder", "setup", "other"].includes(b.kind))
          return validation("kind");
        if (b.kind === "founder" && !principal.operator)
          return problem(403, "forbidden", "Forbidden", "founder time is entered by the agency");
        if (!b.work_date || !/^\d{4}-\d{2}-\d{2}$/.test(b.work_date))
          return validation("work_date");
        if (!Number.isInteger(b.duration_seconds) || (b.duration_seconds ?? 0) <= 0)
          return validation("duration_seconds must be a positive integer");
        const row = timeRow(principal, {
          seat_id: de.seat_id,
          membership_id: who.id,
          kind: b.kind,
          grain: b.grain ?? "day",
          work_date: b.work_date,
          seconds: b.duration_seconds as number,
          note: b.note ?? null,
          source: "manual",
        });
        createdTime.push(row);
        touch(principal.company.id, row.period_key);
        return json(row, 201);
      },
      "timeEntries.import": (ctx) => importFile(ctx, "time-entries"),
      "costs.import": (ctx) => importFile(ctx, "costs"),
      "imports.list": ({ principal, url }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const rows = batches
          .filter((b) => b.company_id === principal.company.id)
          .map(({ company_id: _c, ...b }) => b)
          .sort((a, b) => b.committed_at.localeCompare(a.committed_at));
        return json(page(rows, url));
      },
      "imports.reverse": ({ principal, params }) => {
        if (!principal) return notFound();
        if (!principal.operator) return problem(403, "forbidden", "Forbidden");
        const batch = batches.find(
          (b) => b.company_id === principal.company.id && b.batch_id === params.batch,
        );
        if (!batch) return notFound();
        if (batch.reversed_at) return problem(409, "conflict", "Conflict", "already reversed");
        const at = now().toISOString();
        let voided = 0;
        for (const t of createdTime)
          if (t.batch_id === batch.batch_id && t.voided_at === null) {
            t.voided_at = at;
            voided++;
            touch(principal.company.id, t.period_key);
          }
        batch.reversed_at = at;
        return json({
          batch_id: batch.batch_id,
          voided_time_entries: voided,
          tombstoned_observations: 0,
          reversed_cost_events: batch.resource === "costs" ? batch.rows : 0,
          closed_external_identities: 0,
        });
      },
      "reports.get": ({ principal, params }) => {
        if (!principal) return notFound();
        const r = reportOf(principal, params.id);
        if (!r) return notFound();
        const base = `/v1/companies/${principal.company.id}/reports/friday/${r.id}`;
        return json({
          ...r,
          report_stale: staleOf(principal.company, r),
          html_url: r.html_object_key ? `${base}/html` : null,
          pdf_url: r.pdf_object_key ? `${base}/pdf` : null,
        });
      },
      "reports.html": ({ principal, params }) => {
        if (!principal) return notFound();
        const r = reportOf(principal, params.id);
        if (!r?.html_object_key) return notFound();
        return new Response(reportHtml(principal.company, r, desOf(principal.company.id)), {
          status: 200,
          headers: { "content-type": "text/html; charset=utf-8" },
        });
      },
      "reports.pdf": ({ principal, params }) => {
        if (!principal) return notFound();
        const r = reportOf(principal, params.id);
        if (!r?.pdf_object_key) return notFound();
        return new Response(`%PDF-1.4\n% CompanyOS in-memory stand-in for ${r.week_key}\n%%EOF\n`, {
          status: 200,
          headers: { "content-type": "application/pdf" },
        });
      },
      "reports.generate": ({ principal, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const week = (body as { week_key?: string } | null)?.week_key ?? "";
        if (!WEEK.test(week)) return validation("week_key must be a week key");
        const list = reportsOf(principal.company);
        const latest = list
          .filter((r) => r.week_key === week)
          .sort((a, b) => b.version - a.version)[0];
        const at = now().toISOString();
        if (latest?.state === "draft") {
          Object.assign(latest, {
            state: "generated",
            generated_at: at,
            updated_at: at,
            html_object_key: `reports/${principal.company.slug}/${week}.html`,
            pdf_object_key: `reports/${principal.company.slug}/${week}.pdf`,
          });
          staleReports.delete(latest.id);
          return json(latest, 201);
        }
        if (latest) return json(nextVersion(principal, latest, {}), 201);
        const template = reports(principal.company, week)[0] as FridayReportRecord;
        const record: FridayReportRecord = {
          ...template,
          id: uid(0x8800, ++seq),
          state: "generated",
          generated_at: at,
          html_object_key: `reports/${principal.company.slug}/${week}.html`,
          pdf_object_key: `reports/${principal.company.slug}/${week}.pdf`,
          created_at: at,
          updated_at: at,
        };
        list.push(record);
        return json(record, 201);
      },
      "reports.update": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const r = reportOf(principal, params.id);
        if (!r) return notFound();
        const note = (body as { supervisor_note?: string | null } | null)?.supervisor_note ?? null;
        if (note !== null && note.length > 10_000) return validation("supervisor_note is too long");
        // A version whose send started is frozen: the note goes on version N+1.
        if (r.frozen_at) return json(nextVersion(principal, r, { supervisor_note: note }));
        r.supervisor_note = note;
        r.updated_at = now().toISOString();
        return json(r);
      },
      "reports.send": ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const r = reportOf(principal, params.id);
        if (!r) return notFound();
        const force = (body as { force?: boolean } | null)?.force === true;
        if (r.state === "draft")
          return problem(422, "report_stale", "Report is stale", "report is not generated yet");
        if (r.state === "sent") return json(r);
        if (staleReports.has(r.id) && !force)
          return problem(
            422,
            "report_stale",
            "Report is stale",
            "the ledger changed after this version was generated",
          );
        const at = now().toISOString();
        Object.assign(r, { state: "sent", frozen_at: at, sent_at: at, updated_at: at });
        return json(r);
      },
      // REPORTS-UI (reports/friday/subscription.ts): the caller's own entry of the opt-out list.
      "reports.subscription": ({ principal }) => {
        if (!principal) return notFound();
        const self = reportSelf(principal);
        return self ? json(subscriptionOf(principal, self)) : selfOnly();
      },
      "reports.setSubscription": ({ principal, body }) => {
        if (!principal) return notFound();
        const self = reportSelf(principal);
        if (!self) return selfOnly();
        const b = body as Record<string, unknown> | null;
        if (!b || typeof b.subscribed !== "boolean" || Object.keys(b).length !== 1)
          return validation("body must be exactly { subscribed: boolean }");
        const fr = principal.company.settings.friday_report ?? {};
        const list = fr.opted_out_membership_ids ?? [];
        const has = list.includes(self.membership_id);
        if (has === b.subscribed) {
          const next = b.subscribed
            ? list.filter((id) => id !== self.membership_id)
            : [...list, self.membership_id];
          if (next.length > 500) return validation("the opt-out list holds at most 500 members");
          principal.company.settings = {
            ...principal.company.settings,
            friday_report: { ...fr, opted_out_membership_ids: next },
          };
          principal.company.updated_at = now().toISOString();
        }
        return json(subscriptionOf(principal, self));
      },
      "companies.update": ({ principal, body, me }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        const b = (body ?? {}) as Partial<
          Pick<FakeCompany, "name" | "settings" | "calendar_config" | "reporting_currency">
        >;
        if (b.name !== undefined && !b.name.trim()) return validation("name must not be empty");
        const recipients = b.settings?.friday_report?.recipients_extra ?? [];
        if (recipients.some((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)))
          return validation("settings.friday_report.recipients_extra: not an email");
        // HARDEN-mfa (routes/org/company.ts): `settings.security` is the owners'; a patch whose
        // settings omit it keeps the stored value; turning the requirement on needs an owner who
        // uses two-step sign-in themselves.
        const stored = principal.company.settings.security;
        if (b.settings !== undefined && !("security" in b.settings) && stored)
          b.settings = { ...b.settings, security: stored };
        // FOUNDER-OS phase 1 (routes/org/company.ts): a patch that omits `founder_sharing` or
        // `head_seat_id` keeps the stored value (`head_seat_id: null` unpins); changing the
        // sharing is the owners'; the head seat must be an open or filled seat of the company.
        if (b.settings !== undefined) {
          const was = principal.company.settings;
          if (!("founder_sharing" in b.settings) && was.founder_sharing)
            b.settings = { ...b.settings, founder_sharing: was.founder_sharing };
          if (!("head_seat_id" in b.settings) && was.head_seat_id !== undefined)
            b.settings = { ...b.settings, head_seat_id: was.head_seat_id };
          if (
            JSON.stringify(b.settings.founder_sharing ?? null) !==
              JSON.stringify(was.founder_sharing ?? null) &&
            !principal.roles.includes("owner")
          )
            return problem(403, "forbidden", "Forbidden", "only an owner changes the sharing");
          const head = b.settings.head_seat_id;
          if (
            head &&
            head !== was.head_seat_id &&
            !(structureStores?.seatsOf(principal.company) ?? []).some(
              (s) => s.id === head && (s.status === "open" || s.status === "filled"),
            )
          )
            return validation("settings.head_seat_id: an open or filled seat of the company");
        }
        // REPORTS-UI (routes/org/company.ts `keepOptOuts`): the opt-out list changes only with
        // `opted_out_expected` equal to the stored list (409 otherwise); without it, kept.
        if (b.settings !== undefined) {
          const now = principal.company.settings.friday_report?.opted_out_membership_ids ?? [];
          const sent = b.settings.friday_report?.opted_out_membership_ids;
          const expected = (body as { opted_out_expected?: string[] } | null)?.opted_out_expected;
          const sameIds = (x: readonly string[], y: readonly string[]) =>
            x.length === y.length && [...x].sort().join() === [...y].sort().join();
          if (sent !== undefined && !sameIds(sent, now)) {
            if (expected !== undefined) {
              if (!sameIds(expected, now))
                return problem(409, "conflict", "Conflict", "the report opt-out list changed");
            } else
              b.settings = {
                ...b.settings,
                friday_report: { ...b.settings.friday_report, opted_out_membership_ids: [...now] },
              };
          } else if (sent === undefined && now.length)
            b.settings = {
              ...b.settings,
              friday_report: { ...b.settings.friday_report, opted_out_membership_ids: [...now] },
            };
        }
        if (
          b.settings !== undefined &&
          JSON.stringify(b.settings.security ?? {}) !== JSON.stringify(stored ?? {})
        ) {
          if (!principal.roles.includes("owner"))
            return problem(403, "forbidden", "Forbidden", "only an owner changes security");
          if (
            b.settings.security?.require_two_factor_for_admins === true &&
            stored?.require_two_factor_for_admins !== true &&
            !mfa.enrolled(me.user.email)
          )
            return problem(
              403,
              "mfa_enrollment_required",
              "Two-factor sign-in must be set up first",
              "Set up two-step sign-in for yourself before requiring it of others.",
            );
        }
        Object.assign(principal.company, {
          ...(b.name !== undefined ? { name: b.name.trim() } : {}),
          ...(b.settings !== undefined ? { settings: b.settings } : {}),
          ...(b.calendar_config !== undefined ? { calendar_config: b.calendar_config } : {}),
          ...(b.reporting_currency !== undefined
            ? { reporting_currency: b.reporting_currency }
            : {}),
          updated_at: now().toISOString(),
        });
        return json(principal.company);
      },
    };
  }

  /** Add-ons, failed events, the operator DE list, the cost ledger, resend and reconcile. */
  function opsHandlers(): Partial<Record<RouteId, Handler>> {
    return createOpsHandlers(ops, {
      now,
      todayWeek,
      accounts: () => [...accounts.values()].map((a) => a.me),
      companies: () => companies,
      desOf,
      principalFor,
      kpiOn,
      cardFor,
      reportsOf,
      sendBase: s117Handlers()["reports.send"] as Handler,
      deWeek: agency.facts.deWeek,
    });
  }

  /** The live rows of every cell (the newest row of a cell supersedes the older ones). */
  function liveObservations(companyId: string): Observation[] {
    const live = new Map<string, Observation>();
    for (const o of observations.filter((x) => x.company_id === companyId)) {
      const k = `${o.metric_def_id}|${o.subject_kind}|${o.subject_id}|${o.period_key}`;
      const prev = live.get(k);
      if (!prev || o.created_at >= prev.created_at) live.set(k, o);
    }
    return [...live.values()];
  }

  function timeRow(
    p: Principal,
    t: {
      seat_id: string;
      membership_id: string;
      kind: TimeEntry["kind"];
      grain: TimeEntry["grain"];
      work_date: string;
      seconds: number;
      note: string | null;
      source: TimeEntry["source"];
      batch_id?: string;
    },
  ): TimeEntry & { batch_id?: string } {
    const at = now().toISOString();
    const n = ++seq;
    return {
      task_id: null,
      company_id: p.company.id,
      id: uid(0x6800, n),
      seat_id: t.seat_id,
      membership_id: t.membership_id,
      kind: t.kind,
      grain: t.grain,
      work_date: t.work_date,
      period_key: isoWeekKey(new Date(`${t.work_date}T12:00:00Z`)),
      started_at: null,
      ended_at: null,
      duration_seconds: t.seconds,
      minutes: Math.round(t.seconds / 60),
      source: t.source,
      source_event_id: t.batch_id ? `import:${t.batch_id}:${n}` : `manual:fake:${n}`,
      revision: 1,
      supersedes_id: null,
      superseded_by: null,
      voided_at: null,
      approval_status: "approved",
      decided_by_membership_id: null,
      decided_at: at,
      rejection_reason: null,
      source_ref: null,
      source_actor_ref: null,
      dims: null,
      stats: null,
      visibility: t.kind === "founder" ? "agency" : "client",
      note: t.note,
      actor_kind: p.operator ? "agency_user" : "person",
      actor_membership_id: null,
      created_at: at,
      ...(t.batch_id ? { batch_id: t.batch_id } : {}),
    };
  }

  /** `POST …/{resource}/import` (multipart `file`, `dry_run`): validated fully before writing. */
  function importFile(ctx: Ctx, resource: "time-entries" | "costs"): Response {
    const p = ctx.principal;
    if (!p) return notFound();
    if (!p.manager) return problem(403, "forbidden", "Forbidden");
    const form = ctx.body as { file?: string; dry_run?: string } | null;
    if (typeof form?.file !== "string") return validation("file: a CSV part is required");
    const list = desOf(p.company.id);
    const checked = validateImport(resource, form.file, {
      seats: seats(p.company, list).map((s) => ({ id: s.id, title: s.title })),
      people: people(p.company, list, p.operator),
      operator: p.operator,
    });
    if ("error" in checked) return validation(checked.error);
    const dryRun = form.dry_run !== "false";
    const rows = checked.rows.map((r) => r.result);
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
    const batchId = uid(0x9a00, ++seq);
    if (!dryRun) {
      if (rows.some((r) => r.status === "error" || r.status === "unmapped"))
        return problem(
          422,
          "validation_failed",
          "Validation failed",
          "fix every row before committing",
        );
      for (const r of checked.rows)
        if (r.time) {
          const row = timeRow(p, {
            ...r.time,
            kind: r.time.kind as TimeEntry["kind"],
            grain: "day",
            source: "import",
            batch_id: batchId,
          });
          createdTime.push(row);
          touch(p.company.id, row.period_key);
        }
      batches.push({
        company_id: p.company.id,
        batch_id: batchId,
        resource,
        rows: rows.length,
        committed_at: now().toISOString(),
        reversed_at: null,
      });
    }
    return json({ batch_id: batchId, resource, dry_run: dryRun, rows, counts });
  }

  // A literal segment wins over a parameter (`/metrics/templates` before `/metrics/{id}`): the
  // routes with fewer parameters are tried first.
  // MANAGED-POLICY: in a managed company only its agency adds DEs and AGs (fake-agent-requests.ts).
  for (const id of MANAGED_ADD_ROUTES) {
    const h = handlers[id];
    if (h) handlers[id] = managedGate(h);
  }

  const paramCount = (id: RouteId) => operations[id].path.split("{").length;
  const table = (Object.keys(handlers) as RouteId[])
    .sort((a, b) => paramCount(a) - paramCount(b))
    .map((id) => ({
      id,
      method: operations[id].method,
      pattern: compile(operations[id].path),
    }));

  const mfaRequired = () =>
    problem(403, "mfa_enrollment_required", "Two-factor sign-in must be set up first");

  /** Better Auth's `sign-in/email`, with the two-step challenge (fake-mfa.ts). */
  function signInStep(email: string, password: string): "signed_in" | "two_factor" | "invalid" {
    const account = accounts.get(email.trim().toLowerCase());
    if (!account || account.password !== password) return "invalid";
    if (mfa.afterPassword(account.email) === "two_factor") {
      // `twoFactorRedirect`: only the challenge cookie, no session.
      current = null;
      access.endSession();
      return "two_factor";
    }
    current = account.email;
    access.startSession(account.email);
    return "signed_in";
  }

  const fakeFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = new URL(typeof input === "string" ? input : input.toString(), "http://fake.local");
    const method = (init?.method ?? "GET").toUpperCase();
    const match = table
      .map((r) => ({ r, m: r.method === method ? r.pattern.exec(url.pathname) : null }))
      .find((x) => x.m);
    // The palette's own reads of the twin (SEARCH-NOTIF) are not a screen's calls.
    if (!new Headers(init?.headers).has("x-fake-internal"))
      calls.push({
        id: method === "GET" && url.pathname === "/v1/me" ? "me.get" : (match?.r.id ?? null),
        method,
        path: `${url.pathname}${url.search}`,
      });
    const account = current ? accounts.get(current) : undefined;
    if (method === "GET" && url.pathname === "/v1/me") {
      if (!account) return problem(401, "unauthenticated", "Authentication required");
      return json({ ...account.me, mfa: mfaOf(account.me) });
    }
    // HARDEN-mfa (auth/principal.ts): an operator without a second factor reaches `/v1/me` only.
    const mustEnrol = account ? mfaOf(account.me) : null;
    if (mustEnrol?.operator_required && !mustEnrol.enrolled) return mfaRequired();
    if (!match?.m) {
      return problem(
        404,
        "not_found",
        "Not found",
        `${method} ${url.pathname} is not served by the in-memory API (wired at integration).`,
      );
    }
    // M8-ASSESS: a respondent's link is its own credential (access `assessment_token`): no
    // session, no principal; the handler finds the questionnaire by the token.
    // BIZ-CLIENT-HEALTH: likewise a client survey's link (`survey.*`).
    if (match.r.id.startsWith("respond.") || match.r.id.startsWith("survey.")) {
      const handler = handlers[match.r.id];
      if (!handler) return notFound();
      return handler({
        url,
        params: { ...(match.m.groups ?? {}) } as Record<string, string>,
        body: typeof init?.body === "string" ? JSON.parse(init.body) : undefined,
        me: account?.me ?? ANONYMOUS_ME,
        principal: null,
        headers: new Headers(init?.headers),
      });
    }
    // WF-CONTROL: the pairing code is its own credential (access `public`): no session needed.
    // CALENDAR-C1: likewise the ICS feed's token (access `feed_token`).
    if (match.r.id === "control.pair" || match.r.id === "calendarFeed.ics") {
      const handler = handlers[match.r.id];
      if (!handler) return notFound();
      return handler({
        url,
        params: { ...(match.m.groups ?? {}) } as Record<string, string>,
        body: typeof init?.body === "string" ? JSON.parse(init.body) : undefined,
        me: account?.me ?? ANONYMOUS_ME,
        principal: null,
        headers: new Headers(init?.headers),
      });
    }
    if (!account) return problem(401, "unauthenticated", "Authentication required");
    const params = { ...(match.m.groups ?? {}) } as Record<string, string>;
    const principal = principalFor(account.me, params.co);
    if (params.co && !principal) return notFound();
    // HARDEN-mfa: an owner or admin of a company that requires two-step sign-in, not enrolled,
    // is refused that company's routes (the company transaction's check).
    if (
      principal &&
      mustEnrol &&
      !mustEnrol.enrolled &&
      mustEnrol.required_company_ids.includes(principal.company.id)
    )
      return mfaRequired();
    if (principal && FAKE_KPI_ROUTES.has(match.r.id) && !kpiOn(account.me, principal.company.id))
      return featureDisabled("kpi");
    // Grace: kpi reads work, kpi writes are refused (ADR-0016, 13 §Entitlements).
    if (
      principal &&
      method !== "GET" &&
      FAKE_KPI_ROUTES.has(match.r.id) &&
      account.me.entitlements[principal.company.id]?.kpi === "grace"
    )
      return problem(
        403,
        "feature_read_only",
        "Read-only",
        "The kpi add-on is in its grace period.",
      );
    // After the feature gate, as the API checks `requireFeature` before `can()`.
    if (
      principal &&
      FAKE_READER_ROUTES.has(match.r.id) &&
      !principal.manager &&
      !principal.roles.includes("client_viewer")
    )
      return problem(403, "forbidden", "Forbidden", "company#reader only");
    // Chat (M2-06) and the agents routes chat reads: the same rules for their add-ons.
    const gated = principal ? addonOf(match.r.id) : null;
    if (principal && gated) {
      const state = addonState(account.me, principal.company.id, gated);
      if (state === "off") return featureDisabled(gated);
      if (state === "grace" && method !== "GET")
        return problem(
          403,
          "feature_read_only",
          "Read-only",
          `The ${gated} add-on is in its grace period.`,
        );
    }
    let body: unknown;
    if (typeof init?.body === "string") body = JSON.parse(init.body);
    else if (init?.body instanceof FormData) {
      // Multipart CSV imports: each part as text (the file part is read here, not streamed).
      // TASK-FILES: a file part's bytes, name and type ride along under `__files`.
      const parts: Record<string, unknown> = {};
      const files: Record<string, { bytes: Uint8Array; name: string; type: string }> = {};
      for (const [k, v] of init.body.entries()) {
        if (typeof v === "string") parts[k] = v;
        else {
          const blob = v as File;
          const bytes = new Uint8Array(await blob.arrayBuffer());
          parts[k] = new TextDecoder().decode(bytes);
          files[k] = { bytes, name: blob.name ?? k, type: blob.type ?? "" };
        }
      }
      if (Object.keys(files).length) parts.__files = files;
      body = parts;
    }
    const handler = handlers[match.r.id];
    const headers = new Headers(init?.headers);
    return handler
      ? handler({ url, params, body, me: account.me, principal, headers })
      : notFound();
  }) as typeof fetch;
  internal = (path, init) =>
    fakeFetch(path, {
      method: init?.method ?? "GET",
      headers: { "x-fake-internal": "1", "content-type": "application/json" },
      ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });

  return {
    fetch: fakeFetch,
    calls,
    resetCalls() {
      calls.length = 0;
    },
    addOutboxItem: (item) => outboxFake.add(item),
    addLoopIncident: (companyId, incident) => loopsFake.add(companyId, incident),
    answerStop: (runId, ok) => monitoringFake.answerStop(runId, ok),
    setScreensCapacity: (companyId, max) => screensFake.setCapacity(companyId, max),
    addInboundEvent: (e) => intakeFake.addEvent(e),
    addIntegrationJob: (j) => intakeFake.addJob(j),
    addContact: (c) => contactsFake.addContact(c),
    addProcedureRun: (companyId, run) => rulesFake.addProcedureRun(companyId, run),
    signIn: (email, password) => signInStep(email, password) === "signed_in",
    signInStep,
    verifyTwoFactor: (kind, code, trustDevice) => mfa.verify(kind, code, trustDevice),
    enableTwoFactor: (email, password) => mfa.enable(email, password),
    confirmTwoFactor: (email, code) => mfa.confirm(email, code),
    disableTwoFactor: (email, password) => mfa.disable(email, password),
    regenerateRecoveryCodes: (email, password) => mfa.regenerate(email, password),
    twoFactorSecret: (email) => mfa.secretOf(email),
    recoveryCodes: (email) => mfa.recoveryCodesOf(email),
    setTrustedDevice: (email, trusted) => mfa.setTrusted(email, trusted),
    signOut() {
      current = null;
      mfa.clearChallenge();
      access.endSession();
    },
    mailbox: access.mailbox,
    sendMagicLink: access.sendMagicLink,
    lastMagicLink: access.lastMagicLink,
    openMagicLink: access.openMagicLink,
    sessions: access.sessions,
    revokeSession: access.revokeSession,
    hasPassword: access.hasPassword,
    changePassword: access.changePassword,
    updateName: access.updateName,
    requestPasswordReset: access.requestPasswordReset,
    lastResetToken: access.lastResetToken,
    resetPassword: access.resetPassword,
    setPassword: access.setPassword,
    get signedInAs() {
      return current;
    },
    setMe(email, me) {
      const account = accounts.get(email);
      if (account) account.me = me;
    },
    setPortfolioSubset(userId, companyIds) {
      portfolioFake.setSubset(userId, companyIds);
    },
    setPushEnabled(on) {
      pushFake.setEnabled(on);
    },
    fileSkillsmithReport(companyId, report) {
      return agentHealthFake.fileReport(companyId, report);
    },
    fileSkillProposal(companyId, proposal) {
      return skillProposalsFake.fileProposal(companyId, proposal);
    },
    fileSkillsmithOpportunity(companyId, opportunity) {
      return opportunitiesFake.fileOpportunity(companyId, opportunity);
    },
    fileMemoryFinding(companyId, finding) {
      return agentHealthFake.fileMemoryFinding(companyId, finding);
    },
    seedMemoryInbox(companyId) {
      const company = companies.find((c) => c.id === companyId);
      const state = workBridge?.stateOf(companyId);
      let owner: string | null = null;
      for (const a of accounts.values())
        for (const m of a.me.memberships)
          if (m.company_id === companyId && m.roles.includes("owner")) owner = m.membership_id;
      if (!company || !state || !owner) return 0;
      const seatRows = (structureStores?.seatsOf(company) ?? []).filter((s) => s.title).slice(0, 3);
      const team = structureStores?.teamsOf(company)[0];
      const space = state.spaces[0];
      const scopes: NonNullable<InboxItem["memory_scope"]>[] = [
        ...seatRows.map((s) => ({
          kind: "seat" as const,
          id: s.id,
          name: s.title,
          // The demo's DE seats say so in their titles.
          de: /\(DE\)\s*$/.test(s.title),
        })),
        ...(team ? [{ kind: "team" as const, id: team.id, name: team.name }] : []),
        ...(space ? [{ kind: "space" as const, id: space.id, name: space.name }] : []),
      ];
      // Per scope: how many proposals each notice reported and how long ago (minutes), newest
      // first — repeats of a scope, as Recall sends a notice per extraction run.
      const runs = [
        [14, 25],
        [11, 600],
        [2, 360],
        [8, 60 * 26],
        [10, 60 * 30],
        [17, 60 * 50],
        [5, 60 * 54],
        [5, 60 * 75],
        [4, 60 * 80],
      ] as const;
      let n = 0;
      runs.forEach(([count, minutesAgo], i) => {
        const scope = scopes[i % scopes.length];
        if (!scope) return;
        const at = new Date(now().getTime() - minutesAgo * 60_000).toISOString();
        state.inbox.push({
          company_id: companyId,
          id: crypto.randomUUID(),
          membership_id: owner,
          kind: "memory",
          subject_kind: "recall_notice",
          subject_id: crypto.randomUUID(),
          message_id: null,
          source_event_id: crypto.randomUUID(),
          coalesce_key: null,
          count,
          summary: `Recall: ${count} memory proposal(s) waiting in ${scope.kind}; ${count} to review`,
          read_at: i === runs.length - 1 ? at : null,
          done_at: null,
          snoozed_until: null,
          desktop: false,
          category: null,
          from_membership_id: null,
          nudged_at: null,
          escalated_at: null,
          created_at: at,
          updated_at: at,
          memory_scope: scope,
        });
        n++;
      });
      return n;
    },
    seedWaitingInbox(companyId) {
      const state = workBridge?.stateOf(companyId);
      let owner: string | null = null;
      for (const a of accounts.values())
        for (const m of a.me.memberships)
          if (m.company_id === companyId && m.roles.includes("owner")) owner = m.membership_id;
      if (!state || !owner) return 0;
      const pending = approvalsOf(companyId).filter((a) => a.state === "pending");
      for (const a of pending)
        state.inbox.push({
          company_id: companyId,
          id: crypto.randomUUID(),
          membership_id: owner,
          kind: "approval",
          subject_kind: "approval",
          subject_id: a.id,
          message_id: null,
          source_event_id: crypto.randomUUID(),
          coalesce_key: null,
          count: 1,
          summary: `Approval: ${a.proposal.title}`,
          read_at: null,
          done_at: null,
          snoozed_until: null,
          desktop: false,
          category: null,
          from_membership_id: a.requested_by_membership_id,
          nudged_at: null,
          escalated_at: null,
          created_at: a.created_at,
          updated_at: a.created_at,
          memory_scope: null,
        });
      return pending.length;
    },
    seedCommitment(companyId, seed, opts) {
      const c = commitmentsFake.seed(companyId, seed);
      const state = workBridge?.stateOf(companyId);
      if (opts?.inboxFor && state)
        state.inbox.push({
          company_id: companyId,
          id: crypto.randomUUID(),
          membership_id: opts.inboxFor,
          kind: "agent_escalation",
          subject_kind: "commitment",
          subject_id: c.id,
          message_id: null,
          source_event_id: crypto.randomUUID(),
          coalesce_key: null,
          count: 1,
          summary: `Needs you: ${c.intent}`,
          read_at: null,
          done_at: null,
          snoozed_until: null,
          desktop: false,
          category: null,
          from_membership_id: c.requester.membership_id,
          nudged_at: null,
          escalated_at: null,
          created_at: c.created_at,
          updated_at: c.created_at,
          memory_scope: null,
        });
      return c;
    },
    suggest(companyId, seed) {
      return cosFake.suggest(companyId, seed);
    },
    selfHost(membershipId, runtime, inbox = 0) {
      const de = des.find((d) => d.membership_id === membershipId);
      if (!de) throw new Error(`no DE ${membershipId}`);
      const base = Date.parse(SELF_HOSTED_INBOX_BASE);
      de.self_hosted = {
        runtime,
        inbox: Array.isArray(inbox)
          ? [...inbox]
          : Array.from({ length: inbox }, (_, i) => new Date(base + i * 60_000).toISOString()),
      };
    },
    entitle(companyId, feature, status) {
      for (const a of accounts.values()) {
        const map = a.me.entitlements[companyId];
        if (map) a.me.entitlements[companyId] = { ...map, [feature]: status };
      }
    },
    async crewPropose(companyId, body) {
      const account = current ? accounts.get(current) : undefined;
      if (!account) return { status: 401, body: null };
      const res = crewFake.propose({
        url: new URL(`http://fake.local/v1/companies/${companyId}/crews/run/proposals`),
        params: { co: companyId },
        body,
        me: account.me,
        principal: principalFor(account.me, companyId),
        headers: new Headers(),
      });
      return { status: res.status, body: await res.json() };
    },
  };
}

/**
 * The routes the Work (M2-07) and Chat (M2-06) twins both serve. They share the company's
 * spaces (one array), so a list from Work is complete; for a route addressed by id, Work answers
 * the ids it holds (boards, tasks, task threads) and Chat the rest (its channels' threads, the
 * demo cards' tasks). Creating a space is Chat's (its default channel is a chat channel), and
 * the space sidebar is Chat's, which adds Work's boards and channels.
 */
function twinRoutes(
  work: Partial<Record<RouteId, Handler>>,
  chat: Partial<Record<RouteId, Handler>>,
  prime: (companyId: string) => void,
): Partial<Record<RouteId, Handler>> {
  const out: Partial<Record<RouteId, Handler>> = {};
  for (const id of Object.keys(work) as RouteId[]) {
    const w = work[id];
    const c = chat[id];
    if (!w || !c) continue;
    out[id] =
      id === "spaces.sidebar" || id === "spaces.create"
        ? c
        : (ctx) => {
            if (ctx.principal) prime(ctx.principal.company.id);
            const r = w(ctx);
            return r.status === 404 ? c(ctx) : r;
          };
  }
  return out;
}

/** Routes both twins serve: `primary` answers, and its 404 goes to `secondary`. */
function fallbackRoutes(
  primary: Partial<Record<RouteId, Handler>>,
  secondary: Partial<Record<RouteId, Handler>>,
): Partial<Record<RouteId, Handler>> {
  const out: Partial<Record<RouteId, Handler>> = {};
  for (const id of Object.keys(primary) as RouteId[]) {
    const p = primary[id];
    const s = secondary[id];
    if (!p || !s) continue;
    out[id] = (ctx) => {
      const r = p(ctx);
      return r.status === 404 ? s(ctx) : r;
    };
  }
  return out;
}

/** Seeds the caller's company in the chat twin before a merged list reads both twins. */
function primed(prime: (companyId: string) => void, h: ComposedHandler): ComposedHandler {
  return (ctx) => {
    if (ctx.principal) prime(ctx.principal.company.id);
    return h(ctx);
  };
}
